import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import api from "../api";
import { notifyWorkspaceChanged, subscribeToWorkspaceChanged } from "../workspaceSync";

type Status = "green" | "green_strong" | "yellow" | "red" | "neutral";
type Campus = string;
type Frequency = "weekly" | "monthly" | "bimonthly" | "quarterly" | "yearly" | "periodic";
type WeekStartsOn = "saturday" | "sunday" | "monday";
type Scope = "campus" | "global";
type PerformanceDirection = "higher_is_better" | "lower_is_better";
type WorkStatus = "not-started" | "in-progress" | "blocked" | "done";
type WorkPriority = "low" | "normal" | "high" | "critical";
type WorkEffort = "light" | "medium" | "heavy";
type ValueType = "count" | "currency" | "percentage" | "rating_1_10";
type MetricPersistence = "period_only" | "carry_forward";
type AggregationMode = "current" | "mtd" | "qtd" | "ytd";
type ComparisonMode = "target" | "previous_period" | "same_period_last_year";
type KpiCalculationMode =
  | "direct"
  | "calculated_values"
  | "influenced_by_children"
  | "formula_percentage";

type EnterpriseSummary = {
  id: number;
  name: string;
  slug: string;
  week_starts_on?: WeekStartsOn | null;
  updated_at?: string;
};

type AppUser = {
  id: number;
  name: string;
  email: string;
  role: string;
  enterprise_id?: number | null;
  is_active?: boolean;
};

type TaskItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  notes: string;
};

type InitiativeItem = {
  id: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  summary: string;
  tasks: TaskItem[];
};

const canRequestTaskUpdate = (task: Pick<TaskItem, "id" | "owner" | "status">) =>
  Boolean(task.id) && Boolean(task.owner) && task.status !== "done";

type PollQuestionType = "number" | "rating" | "yes_no" | "text";
type PollResponseValue = number | boolean | string;

type PollResponse = {
  id: string;
  submittedAt: string;
  respondentLabel?: string;
  respondentGroup?: string;
  value: PollResponseValue;
  comment?: string;
};

type PollInvite = {
  id: string;
  token: string;
  recipientId: string;
  recipientName: string;
  recipientEmail: string;
  sentAt?: string;
  completedAt?: string;
};

type PollRecipient = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  groups: string[];
  name?: string;
};

type PollConfig = {
  id: string;
  campaignRootKpiId?: string;
  linkedKpiIds: string[];
  title: string;
  description: string;
  targetGroup?: string;
  targetGroups: string[];
  questionPrompt: string;
  questionType: PollQuestionType;
  isAnonymous: boolean;
  status: "draft" | "published";
  publishedAt?: string;
  responses: PollResponse[];
  invites: PollInvite[];
};
type PollEditorMode = "view" | "edit";

type LensConfig = {
  name: string;
  scopeType: string;
  scopeId: string;
  description: string;
};

type LensConfigs = Record<string, LensConfig>;
type MetricHistory = Record<string, number | undefined>;
type RestrictedFields = Record<string, string[]>;

type PersistedNodeData = {
  label: string;
  targetHistory: MetricHistory;
  actualHistory: MetricHistory;
  unit?: string;
  frequency: Frequency;
  scope: Scope;
  performanceDirection: PerformanceDirection;
  supportsCumulative?: boolean;
  metricPersistence?: MetricPersistence;
  calculationMode?: KpiCalculationMode;
  formulaNumeratorKpiId?: string;
  formulaDenominatorKpiId?: string;
  valueType?: ValueType;
  eventDate?: string;
  definition?: string;
  measurement?: string;
  owner?: string;
  dataSource?: string;
  updateNotes?: string;
  pollConfig?: PollConfig | null;
  restrictedFields?: RestrictedFields;
  metricsRestricted?: boolean;
  status?: Status;
  colorActual?: number;
  initiatives?: InitiativeItem[];
  tasks?: TaskItem[];
  lenses?: string[];
  collapsedInLenses?: string[];
  isRollup?: boolean;
};

type PersistedNode = {
  id: string;
  data: PersistedNodeData;
};

type PersistedEdge = {
  id: string;
  source: string;
  target: string;
  data?: {
    influence?: number;
  };
};

type EnterpriseIdentity = {
  id: number | null;
  slug: string;
  name: string;
};

type Workspace = {
  enterpriseIdentity?: EnterpriseIdentity;
  availableLenses?: string[];
  lensConfigs?: LensConfigs;
  activeLens?: string;
  referenceDate?: string;
  isCumulativeView?: boolean;
  aggregationMode?: AggregationMode;
  comparisonMode?: ComparisonMode;
  campus?: string;
  campusList?: string[];
  ownerList?: string[];
  pollAudienceGroups?: string[];
  pollRecipients?: PollRecipient[];
  allNodes?: PersistedNode[];
  allEdges?: PersistedEdge[];
};

type ReportNode = {
  id: string;
  label: string;
  target?: number;
  actual?: number;
  unit: string;
  owner: string;
  scope: Scope;
  frequency: Frequency;
  valueType: ValueType;
  performanceDirection: PerformanceDirection;
  eventDate: string;
  status: Status;
  statusNote?: string;
  colorActual?: number;
  metricsRestricted?: boolean;
  isRollup: boolean;
  calculationMode: KpiCalculationMode;
  lenses: string[];
  initiatives: InitiativeItem[];
  tasks: TaskItem[];
};

type ReportWorkItem = {
  kind: "kpi-task" | "initiative" | "initiative-task";
  kpiId: string;
  initiativeId?: string;
  taskId?: string;
  title: string;
  owner: string;
  dueDate: string;
  status: WorkStatus;
  priority?: WorkPriority;
  effort?: WorkEffort;
  kpiLabel: string;
  kpiLineageLabel: string;
  initiativeTitle?: string;
  indentLevel: number;
};

type TaskEmailResult = {
  sent_count: number;
  skipped_count: number;
  failed_count: number;
  errors?: string[];
};

type SummaryDisplayMode = "kpis" | "initiatives";
type WorkDueFilter = "all" | "past-due" | "this-week" | "next-week" | "future" | "unassigned";
type WorkPriorityFilter = "all" | WorkPriority;
type WorkStatusFilter = "all" | WorkStatus;
type WorkEffortFilter = "all" | WorkEffort;
type WorkSortMode = "due-date" | "priority" | "assigned-to";

type ReportTreeNode = {
  depth: number;
  node: ReportNode;
};

type KpiStatusFilter = "all" | "green" | "yellow" | "red" | "neutral";
type WorkEditorFocus =
  | { kind: "initiative"; initiativeId: string }
  | { kind: "kpi-task"; taskId: string }
  | { kind: "initiative-task"; initiativeId: string; taskId: string };

const activeEnterpriseStorageKey = "lenses.activeEnterpriseId";
const workspaceCacheVersion = "postgres-v2";
const getEnterpriseCacheIdentity = (enterprise: EnterpriseSummary | null | undefined) => {
  const rawIdentity = enterprise?.slug || enterprise?.name || "unknown";
  return rawIdentity
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "") || "unknown";
};
const getWorkspaceEnterpriseIdentity = (
  enterpriseId: number | null,
  enterprise: EnterpriseSummary | null | undefined
): EnterpriseIdentity => ({
  id: enterpriseId,
  slug: enterprise?.slug ?? "",
  name: enterprise?.name ?? "",
});
const getWorkspaceCacheKey = (enterpriseId: number, enterprise: EnterpriseSummary | null | undefined) =>
  `lenses.workspaceCache.${workspaceCacheVersion}.${enterpriseId}.${getEnterpriseCacheIdentity(enterprise)}`;
const defaultCampus: Campus = "global";
const defaultWeekStartsOn: WeekStartsOn = "monday";
const normalizeWeekStartsOn = (value: unknown): WeekStartsOn =>
  value === "saturday" || value === "sunday" || value === "monday" ? value : defaultWeekStartsOn;
const getReportingWeekStart = (date: Date, weekStartsOn: WeekStartsOn) => {
  const nextDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const day = nextDate.getDay();
  const targetDay = weekStartsOn === "saturday" ? 6 : weekStartsOn === "sunday" ? 0 : 1;
  const offset = (day - targetDay + 7) % 7;
  nextDate.setDate(nextDate.getDate() - offset);
  return nextDate;
};
const getInitialReferenceDate = (weekStartsOn: WeekStartsOn = defaultWeekStartsOn) =>
  getReportingWeekStart(new Date(), weekStartsOn);
const initialReferenceDate = getInitialReferenceDate();
const workStatusOptions: WorkStatus[] = ["not-started", "in-progress", "blocked", "done"];
const workPriorityOptions: WorkPriority[] = ["low", "normal", "high", "critical"];
const workEffortOptions: WorkEffort[] = ["light", "medium", "heavy"];
const dataSourceOptions = ["Manual", "API", "Poll"] as const;
const valueTypeOptions: ValueType[] = ["count", "currency", "percentage", "rating_1_10"];

const getApiErrorDetail = (error: unknown, fallback: string) => {
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === "string" && detail.trim()) {
      return detail;
    }
  }
  return fallback;
};

const getNormalizedValueType = (dataSource: string | undefined, valueType: unknown): ValueType => {
  if (dataSource === "Poll") {
    return "rating_1_10";
  }

  return typeof valueType === "string" && valueTypeOptions.includes(valueType as ValueType)
    ? (valueType as ValueType)
    : "count";
};
const frequencyOptions: Frequency[] = ["weekly", "monthly", "bimonthly", "quarterly", "yearly", "periodic"];
const aggregationModeOptions: AggregationMode[] = ["current", "mtd", "qtd", "ytd"];
const comparisonModeOptions: ComparisonMode[] = ["target", "previous_period", "same_period_last_year"];
const initialPollAudienceGroups: string[] = [
  "Staff",
  "Senior Staff",
  "Executive Team",
  "D-Group Leaders",
  "Volunteers",
];
const brandLogoUrl = `${process.env.PUBLIC_URL}/favicon.png?v=2`;

const toTitleCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

const formatEnumLabel = (value: string) =>
  value
    .split("_")
    .map((part) => toTitleCase(part))
    .join(" ");

const formatValueTypeLabel = (value: ValueType) =>
  value === "rating_1_10" ? "Rating (1-10)" : formatEnumLabel(value);

const formatWorkStatusLabel = (status: WorkStatus) => formatEnumLabel(status.replace("-", "_"));
const formatWorkPriorityLabel = (priority: WorkPriority | undefined) =>
  formatEnumLabel(priority ?? "normal");
const formatWorkEffortLabel = (effort: WorkEffort | undefined) => formatEnumLabel(effort ?? "medium");
const formatWorkDueDate = (value: string) => {
  if (!value) return "No due date";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return "No due date";
  return `Due ${parsed.toLocaleDateString(undefined, { month: "short", day: "numeric" })}`;
};
const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());
const matchesWorkDueFilter = (value: string, filter: WorkDueFilter) => {
  if (filter === "all") return true;
  if (!value) return filter === "unassigned";

  const dueDate = startOfLocalDay(new Date(`${value}T00:00:00`));
  if (Number.isNaN(dueDate.getTime())) return filter === "unassigned";
  if (filter === "unassigned") return false;

  const today = startOfLocalDay(new Date());
  const endOfThisWeek = new Date(today);
  endOfThisWeek.setDate(today.getDate() + (today.getDay() === 0 ? 0 : 7 - today.getDay()));
  const endOfNextWeek = new Date(endOfThisWeek);
  endOfNextWeek.setDate(endOfThisWeek.getDate() + 7);

  if (filter === "past-due") return dueDate < today;
  if (filter === "this-week") return dueDate <= endOfThisWeek;
  if (filter === "next-week") return dueDate > endOfThisWeek && dueDate <= endOfNextWeek;
  return dueDate > endOfNextWeek;
};
const workStatusChipStyle = (status: WorkStatus): React.CSSProperties => {
  const palette: Record<WorkStatus, { background: string; color: string; border: string }> = {
    "not-started": { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    "in-progress": { background: "#eff6ff", color: "#1d4ed8", border: "#bfdbfe" },
    blocked: { background: "#fff1f2", color: "#be123c", border: "#fecdd3" },
    done: { background: "#ecfdf5", color: "#047857", border: "#a7f3d0" },
  };
  const colors = palette[status];
  return {
    display: "inline-flex",
    alignItems: "center",
    minHeight: 22,
    border: `1px solid ${colors.border}`,
    borderRadius: 999,
    padding: "2px 8px",
    background: colors.background,
    color: colors.color,
    fontSize: 12,
    fontWeight: 800,
    lineHeight: 1.2,
  };
};
const workPriorityChipStyle = (priority: WorkPriority | undefined): React.CSSProperties => {
  const palette: Record<WorkPriority, { background: string; color: string; border: string }> = {
    low: { background: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
    normal: { background: "#f3f4f6", color: "#374151", border: "#e5e7eb" },
    high: { background: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
    critical: { background: "#fef2f2", color: "#b91c1c", border: "#fecaca" },
  };
  const colors = palette[priority ?? "normal"];
  return {
    ...workStatusChipStyle("not-started"),
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.color,
  };
};
const workEffortChipStyle = (effort: WorkEffort | undefined): React.CSSProperties => {
  const palette: Record<WorkEffort, { background: string; color: string; border: string }> = {
    light: { background: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
    medium: { background: "#f8fafc", color: "#475569", border: "#e2e8f0" },
    heavy: { background: "#f5f3ff", color: "#6d28d9", border: "#ddd6fe" },
  };
  const colors = palette[effort ?? "medium"];
  return {
    ...workStatusChipStyle("not-started"),
    border: `1px solid ${colors.border}`,
    background: colors.background,
    color: colors.color,
  };
};

const formatMetricValue = (value: number | undefined, valueType: ValueType) => {
  if (typeof value !== "number" || Number.isNaN(value)) return "—";
  if (valueType === "percentage") return `${Number(value.toFixed(2))}%`;
  if (valueType === "currency") {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(value);
  }
  if (valueType === "rating_1_10") return String(Number(value.toFixed(2)));
  return new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value);
};

const buildPollQuestionPrompt = (kpi: {
  label?: string;
  definition?: string;
  measurement?: string;
}) => {
  const label = kpi.label?.trim() || "this KPI";
  const definition = kpi.definition?.trim();
  const definitionLine = definition
    ? `${label} is defined as: ${definition}.`
    : `${label} is the KPI being measured.`;

  return `${definitionLine}\n\nPlease rate from 1-10 how well we are doing in this area, and leave a comment with any evidence or examples that shaped your rating.`;
};

const getPollPublishContext = (_scope?: Scope, _campus?: Campus) => {
  return {
    canPublish: true,
    tone: "info",
    summary: "This poll will run enterprise-wide.",
    detail: "Responses will update this KPI's enterprise-wide result.",
  };
};

const formatReferenceDate = (date: Date) =>
  date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });

const shiftReferenceDate = (date: Date, direction: "previous" | "next") => {
  const nextDate = new Date(date);
  nextDate.setDate(nextDate.getDate() + (direction === "next" ? 7 : -7));
  return nextDate;
};

const normalizeInfluence = (edge: PersistedEdge): number | null => {
  const raw = edge.data?.influence;
  if (typeof raw !== "number" || Number.isNaN(raw) || raw <= 0) return null;
  return raw / 100;
};

const getIsoWeekParts = (date: Date) => {
  const utc = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const dayNum = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  const week = Math.ceil((((utc.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return { year: utc.getUTCFullYear(), week };
};

const getReportingWeekParts = (date: Date, weekStartsOn: WeekStartsOn) => {
  const weekStart = getReportingWeekStart(date, weekStartsOn);
  const weekNumberDate = new Date(weekStart);
  if (weekStartsOn === "saturday") {
    weekNumberDate.setDate(weekNumberDate.getDate() + 2);
  } else if (weekStartsOn === "sunday") {
    weekNumberDate.setDate(weekNumberDate.getDate() + 1);
  }
  return getIsoWeekParts(weekNumberDate);
};

const getDateFromIsoWeek = (year: number, week: number) => {
  const simple = new Date(Date.UTC(year, 0, 1 + (week - 1) * 7));
  const dayOfWeek = simple.getUTCDay() || 7;
  if (dayOfWeek <= 4) {
    simple.setUTCDate(simple.getUTCDate() - dayOfWeek + 1);
  } else {
    simple.setUTCDate(simple.getUTCDate() + 8 - dayOfWeek);
  }
  return new Date(simple.getUTCFullYear(), simple.getUTCMonth(), simple.getUTCDate());
};

const getMetricKey = (
  referenceDate: Date,
  frequency: Frequency,
  _scope: Scope,
  _campus: Campus
) => {
  if (frequency === "weekly") {
    const iso = getIsoWeekParts(referenceDate);
    return `${iso.year}-W${String(iso.week).padStart(2, "0")}::global`;
  }

  if (frequency === "monthly") {
    const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
    return `${referenceDate.getFullYear()}-${month}::global`;
  }

  if (frequency === "bimonthly") {
    return `${referenceDate.getFullYear()}-B${Math.floor(referenceDate.getMonth() / 2) + 1}::global`;
  }

  if (frequency === "quarterly") {
    return `${referenceDate.getFullYear()}-Q${Math.floor(referenceDate.getMonth() / 3) + 1}::global`;
  }

  if (frequency === "periodic") {
    const month = String(referenceDate.getMonth() + 1).padStart(2, "0");
    const day = String(referenceDate.getDate()).padStart(2, "0");
    return `${referenceDate.getFullYear()}-${month}-${day}::global`;
  }

  return `${referenceDate.getFullYear()}::global`;
};

const setValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  value: number | undefined
): MetricHistory => {
  const key = getMetricKey(referenceDate, frequency, scope, campus);
  return {
    ...history,
    [key]: value,
  };
};

const derivePollActualHistory = (
  pollConfig: PollConfig | null | undefined,
  frequency: Frequency,
  scope: Scope,
  campus: Campus
): MetricHistory => {
  if (!pollConfig) return {};

  const valuesByKey = new Map<string, number[]>();

  pollConfig.responses.forEach((response) => {
    const numericValue =
      typeof response.value === "number" ? response.value : Number(response.value);
    if (Number.isNaN(numericValue)) return;

    const submittedAt = new Date(response.submittedAt);
    if (Number.isNaN(submittedAt.getTime())) return;

    const key = getMetricKey(submittedAt, frequency, scope, campus);
    valuesByKey.set(key, [...(valuesByKey.get(key) ?? []), numericValue]);
  });

  return Object.fromEntries(
    Array.from(valuesByKey.entries()).map(([key, values]) => [
      key,
      values.reduce((sum, value) => sum + value, 0) / values.length,
    ])
  );
};

const getStartOfDay = (date: Date) => new Date(date.getFullYear(), date.getMonth(), date.getDate());

const getStartOfMonth = (date: Date) => new Date(date.getFullYear(), date.getMonth(), 1);

const getStartOfQuarter = (date: Date) =>
  new Date(date.getFullYear(), Math.floor(date.getMonth() / 3) * 3, 1);

const getStartOfYear = (date: Date) => new Date(date.getFullYear(), 0, 1);

const getAggregationStart = (date: Date, aggregationMode: AggregationMode) => {
  switch (aggregationMode) {
    case "mtd":
      return getStartOfMonth(date);
    case "qtd":
      return getStartOfQuarter(date);
    case "ytd":
      return getStartOfYear(date);
    default:
      return null;
  }
};

const getPeriodRange = (frequency: Frequency, periodKey: string) => {
  if (frequency === "weekly") {
    const match = /^(\d{4})-W(\d{2})$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, weekString] = match;
    const year = Number(yearString);
    const week = Number(weekString);
    const start = getDateFromIsoWeek(year, week);
    const end = new Date(start);
    end.setDate(end.getDate() + 6);
    return { start, end };
  }

  if (frequency === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, monthString] = match;
    const year = Number(yearString);
    const month = Number(monthString) - 1;
    return { start: new Date(year, month, 1), end: new Date(year, month + 1, 0) };
  }

  if (frequency === "bimonthly") {
    const match = /^(\d{4})-B([1-6])$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, periodString] = match;
    const year = Number(yearString);
    const startMonth = (Number(periodString) - 1) * 2;
    return { start: new Date(year, startMonth, 1), end: new Date(year, startMonth + 2, 0) };
  }

  if (frequency === "quarterly") {
    const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    if (!match) return null;
    const [, yearString, quarterString] = match;
    const year = Number(yearString);
    const quarter = Number(quarterString) - 1;
    const startMonth = quarter * 3;
    return {
      start: new Date(year, startMonth, 1),
      end: new Date(year, startMonth + 3, 0),
    };
  }

  if (frequency === "yearly") {
    const year = Number(periodKey);
    if (Number.isNaN(year)) return null;
    return { start: new Date(year, 0, 1), end: new Date(year, 11, 31) };
  }

  const parsed = new Date(`${periodKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;
  return { start: parsed, end: parsed };
};

const aggregateMetricValues = (values: number[], valueType: ValueType) => {
  if (values.length === 0) return undefined;
  if (valueType === "count" || valueType === "currency") {
    return values.reduce((sum, value) => sum + value, 0);
  }
  return values.reduce((sum, value) => sum + value, 0) / values.length;
};

const calculateChildValue = (values: Array<number | undefined>, valueType: ValueType) =>
  aggregateMetricValues(
    values.filter((value): value is number => typeof value === "number"),
    valueType
  );

const calculateFormulaPercentage = (
  numerator?: number,
  denominator?: number
) => {
  if (
    typeof numerator !== "number" ||
    Number.isNaN(numerator) ||
    typeof denominator !== "number" ||
    Number.isNaN(denominator) ||
    denominator <= 0
  ) {
    return undefined;
  }
  return (numerator / denominator) * 100;
};

const normalizeCalculationMode = (
  value: unknown,
  fallbackIsRollup = false
): KpiCalculationMode => {
  if (
    value === "calculated_values" ||
    value === "influenced_by_children" ||
    value === "formula_percentage" ||
    value === "direct"
  ) {
    return value;
  }
  return fallbackIsRollup ? "influenced_by_children" : "direct";
};

const formatCalculationModeLabel = (mode: KpiCalculationMode) => {
  switch (mode) {
    case "calculated_values":
      return "Calculated from child values";
    case "influenced_by_children":
      return "Influenced by child performance";
    case "formula_percentage":
      return "Calculated percentage from KPIs";
    default:
      return "Direct KPI";
  }
};

const formatAggregationLabel = (aggregationMode: AggregationMode) => {
  switch (aggregationMode) {
    case "mtd":
      return "MTD";
    case "qtd":
      return "QTD";
    case "ytd":
      return "YTD";
    default:
      return "Current Period";
  }
};

const formatComparisonLabel = (comparisonMode: ComparisonMode) => {
  switch (comparisonMode) {
    case "previous_period":
      return "Previous Period";
    case "same_period_last_year":
      return "Same Period Last Year";
    default:
      return "Target";
  }
};

const formatComparisonMetricLabel = (comparisonMode: ComparisonMode) => {
  switch (comparisonMode) {
    case "previous_period":
      return "Previous";
    case "same_period_last_year":
      return "Previous Year";
    default:
      return "Target";
  }
};

const shiftComparisonDate = (
  referenceDate: Date,
  frequency: Frequency,
  comparisonMode: ComparisonMode
) => {
  const nextDate = new Date(referenceDate);
  if (comparisonMode === "previous_period") {
    switch (frequency) {
      case "weekly":
        nextDate.setDate(nextDate.getDate() - 7);
        return nextDate;
      case "monthly":
        nextDate.setMonth(nextDate.getMonth() - 1);
        return nextDate;
      case "bimonthly":
        nextDate.setMonth(nextDate.getMonth() - 2);
        return nextDate;
      case "quarterly":
        nextDate.setMonth(nextDate.getMonth() - 3);
        return nextDate;
      case "yearly":
        nextDate.setFullYear(nextDate.getFullYear() - 1);
        return nextDate;
      case "periodic":
        nextDate.setDate(nextDate.getDate() - 1);
        return nextDate;
      default:
        return nextDate;
    }
  }

  if (comparisonMode === "same_period_last_year") {
    nextDate.setFullYear(nextDate.getFullYear() - 1);
  }

  return nextDate;
};

const getPeriodKeyForDate = (date: Date, frequency: Frequency) =>
  getMetricKey(date, frequency, "global", defaultCampus).split("::")[0] ?? "";

const shiftPeriodKeyLastYear = (periodKey: string, frequency: Frequency) => {
  if (frequency === "weekly") {
    const match = /^(\d{4})-W(\d{2})$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-W${match[2]}` : periodKey;
  }
  if (frequency === "monthly") {
    const match = /^(\d{4})-(\d{2})$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-${match[2]}` : periodKey;
  }
  if (frequency === "bimonthly") {
    const match = /^(\d{4})-B([1-6])$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-B${match[2]}` : periodKey;
  }
  if (frequency === "quarterly") {
    const match = /^(\d{4})-Q([1-4])$/.exec(periodKey);
    return match ? `${Number(match[1]) - 1}-Q${match[2]}` : periodKey;
  }
  if (frequency === "yearly") {
    return String(Number(periodKey) - 1);
  }
  const parsed = new Date(`${periodKey}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return periodKey;
  parsed.setFullYear(parsed.getFullYear() - 1);
  return parsed.toISOString().slice(0, 10);
};

const shiftPeriodAnchorDate = (anchorDate: Date, frequency: Frequency, offset: number) => {
  const nextDate = new Date(anchorDate);
  switch (frequency) {
    case "weekly":
      nextDate.setDate(nextDate.getDate() + offset * 7);
      break;
    case "monthly":
      nextDate.setMonth(nextDate.getMonth() + offset);
      break;
    case "bimonthly":
      nextDate.setMonth(nextDate.getMonth() + offset * 2);
      break;
    case "quarterly":
      nextDate.setMonth(nextDate.getMonth() + offset * 3);
      break;
    case "yearly":
      nextDate.setFullYear(nextDate.getFullYear() + offset);
      break;
    case "periodic":
      nextDate.setDate(nextDate.getDate() + offset);
      break;
    default:
      break;
  }
  return nextDate;
};

const getPeriodAnchorDate = (date: Date, frequency: Frequency) => {
  const periodKey = getPeriodKeyForDate(date, frequency);
  const range = getPeriodRange(frequency, periodKey);
  return range?.start ?? getStartOfDay(date);
};

const getAggregationWindowPeriodKeys = (
  referenceDate: Date,
  frequency: Frequency,
  aggregationMode: AggregationMode
) => {
  if (aggregationMode === "current") {
    return [getPeriodKeyForDate(referenceDate, frequency)];
  }

  const aggregationStart = getAggregationStart(referenceDate, aggregationMode);
  if (!aggregationStart) return [getPeriodKeyForDate(referenceDate, frequency)];

  const keys: string[] = [];
  let cursor = getPeriodAnchorDate(referenceDate, frequency);

  while (cursor >= aggregationStart) {
    keys.unshift(getPeriodKeyForDate(cursor, frequency));
    cursor = shiftPeriodAnchorDate(cursor, frequency, -1);
  }

  return Array.from(new Set(keys.filter(Boolean)));
};

const resolveMetricPersistence = (
  frequency: Frequency,
  _metricPersistence: MetricPersistence
): MetricPersistence => {
  if (frequency === "periodic") return "carry_forward";
  return "period_only";
};

const getValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  _scope: Scope,
  _campus: Campus,
  valueType: ValueType,
  aggregationMode: AggregationMode = "current",
  persistenceMode: MetricPersistence = "period_only"
): number | undefined => {
  const effectivePersistence = resolveMetricPersistence(frequency, persistenceMode);
  const currentKey = getMetricKey(referenceDate, frequency, "global", defaultCampus);
  const [currentPeriodKey] = currentKey.split("::");
  const filteredEntries = Object.entries(history).filter(([key, value]) => {
    const [entryPeriodKey, entryScopeKey] = key.split("::");
    return Boolean(
      entryPeriodKey && entryScopeKey && entryPeriodKey <= currentPeriodKey && typeof value === "number"
    );
  });

  const getPeriodValue = (targetPeriodKey: string) => {
    const samePeriodEntries = Object.entries(history).filter(([key, value]) => {
      const [entryPeriodKey, entryScopeKey] = key.split("::");
      return entryPeriodKey === targetPeriodKey && Boolean(entryScopeKey) && typeof value === "number";
    });
    const globalEntry = samePeriodEntries.find(([key]) => key.endsWith("::global"));
    if (typeof globalEntry?.[1] === "number") return globalEntry[1];
    return aggregateMetricValues(
      samePeriodEntries
        .map(([, value]) => value)
        .filter((value): value is number => typeof value === "number"),
      valueType
    );
  };

  const getLatestValue = () => {
    const latestPeriodKey = filteredEntries
      .map(([key]) => key.split("::")[0])
      .filter(Boolean)
      .sort((left, right) => left.localeCompare(right))
      .at(-1);
    return latestPeriodKey ? getPeriodValue(latestPeriodKey) : undefined;
  };

  if (aggregationMode !== "current") {
    const windowValues = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode)
      .map((entryPeriodKey) => getPeriodValue(entryPeriodKey))
      .filter((value): value is number => typeof value === "number");

    const aggregatedValue = aggregateMetricValues(windowValues, valueType);
    if (typeof aggregatedValue === "number") return aggregatedValue;
    if (effectivePersistence === "carry_forward") return getLatestValue();
    return undefined;
  }

  if (effectivePersistence === "carry_forward") {
    return getLatestValue();
  }

  return getPeriodValue(currentPeriodKey);
};

const getComparisonValueForContext = (
  history: MetricHistory,
  referenceDate: Date,
  frequency: Frequency,
  scope: Scope,
  campus: Campus,
  valueType: ValueType,
  aggregationMode: AggregationMode,
  comparisonMode: ComparisonMode
) => {
  if (comparisonMode === "previous_period" && aggregationMode !== "current") {
    const currentKeys = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode);
    if (currentKeys.length > 0) {
      const firstRange = getPeriodRange(frequency, currentKeys[0]);
      if (firstRange) {
        const previousValues = currentKeys
          .map((_, index) =>
            getValueForContext(
              history,
              shiftPeriodAnchorDate(firstRange.start, frequency, index - currentKeys.length),
              frequency,
              scope,
              campus,
              valueType,
              "current",
              "period_only"
            )
          )
          .filter((value): value is number => typeof value === "number");
        const aggregatedValue = aggregateMetricValues(previousValues, valueType);
        if (typeof aggregatedValue === "number") {
          return aggregatedValue;
        }
      }
    }
  }

  if (comparisonMode === "same_period_last_year") {
    const currentKeys = getAggregationWindowPeriodKeys(referenceDate, frequency, aggregationMode);
    const comparisonKeys = currentKeys.map((periodKey) =>
      shiftPeriodKeyLastYear(periodKey, frequency)
    );
    const comparisonValues = comparisonKeys
      .map((periodKey) =>
        getValueForContext(
          history,
          getPeriodRange(frequency, periodKey)?.start ?? comparisonDate,
          frequency,
          scope,
          campus,
          valueType,
          "current",
          "period_only"
        )
      )
      .filter((value): value is number => typeof value === "number");
    const aggregatedValue = aggregateMetricValues(comparisonValues, valueType);
    if (typeof aggregatedValue === "number") {
      return aggregatedValue;
    }
  }

  const comparisonDate = shiftComparisonDate(referenceDate, frequency, comparisonMode);
  return getValueForContext(
    history,
    comparisonDate,
    frequency,
    scope,
    campus,
    valueType,
    aggregationMode,
    "period_only"
  );
};

const getDirectScore = (
  target?: number,
  actual?: number,
  performanceDirection: PerformanceDirection = "higher_is_better"
): number | null => {
  if (
    typeof target !== "number" ||
    typeof actual !== "number" ||
    Number.isNaN(target) ||
    Number.isNaN(actual) ||
    target < 0
  ) {
    return null;
  }

  if (target === 0) {
    if (actual === 0) return 1;
    return performanceDirection === "higher_is_better" ? Number.POSITIVE_INFINITY : 0;
  }

  if (performanceDirection === "lower_is_better") {
    if (actual === 0) return Number.POSITIVE_INFINITY;
    return target / actual;
  }

  return actual / target;
};

const getStatusFromScore = (score: number | null): Status => {
  if (score === null) return "neutral";
  if (score >= 1.2) return "green_strong";
  if (score >= 1) return "green";
  if (score >= 0.8) return "yellow";
  return "red";
};
const getScoreFromRatingActual = (value: number | undefined): number | null => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  if (value >= 9) return 1.2;
  if (value >= 7.5) return 1;
  if (value >= 6.5) return 0.8;
  return 0;
};

const isDateInFuture = (referenceDate: Date, eventDate?: string) => {
  if (!eventDate) return false;
  const parsedDate = new Date(eventDate);
  if (Number.isNaN(parsedDate.getTime())) return false;
  return parsedDate.getTime() > referenceDate.getTime();
};

const statusColor = (status: Status) => {
  switch (status) {
    case "green_strong":
      return "#166534";
    case "green":
      return "#16a34a";
    case "yellow":
      return "#ca8a04";
    case "red":
      return "#dc2626";
    default:
      return "#6b7280";
  }
};
const interpolateHexColor = (startHex: string, endHex: string, amount: number) => {
  const clampedAmount = Math.min(1, Math.max(0, amount));
  const start = Number.parseInt(startHex.slice(1), 16);
  const end = Number.parseInt(endHex.slice(1), 16);
  const startRgb = [(start >> 16) & 255, (start >> 8) & 255, start & 255];
  const endRgb = [(end >> 16) & 255, (end >> 8) & 255, end & 255];
  const nextRgb = startRgb.map((channel, index) =>
    Math.round(channel + (endRgb[index] - channel) * clampedAmount)
  );
  return `#${nextRgb.map((channel) => channel.toString(16).padStart(2, "0")).join("")}`;
};
const ratingColor = (value: number | undefined) => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  const rating = Math.min(10, Math.max(1, value));
  const stops = [
    "#450a0a",
    "#7f1d1d",
    "#dc2626",
    "#ea580c",
    "#f97316",
    "#fb923c",
    "#facc15",
    "#86efac",
    "#22c55e",
    "#166534",
  ];
  const lowerRating = Math.floor(rating);
  const upperRating = Math.ceil(rating);
  const lowerColor = stops[lowerRating - 1];
  const upperColor = stops[upperRating - 1];
  return lowerRating === upperRating
    ? lowerColor
    : interpolateHexColor(lowerColor, upperColor, rating - lowerRating);
};
const kpiDisplayColor = (node: Pick<ReportNode, "valueType" | "actual" | "colorActual" | "status">) =>
  typeof node.colorActual === "number"
    ? ratingColor(node.colorActual) ?? statusColor(node.status)
    : node.valueType === "rating_1_10"
      ? ratingColor(node.actual) ?? statusColor(node.status)
      : statusColor(node.status);
const hexToRgba = (hex: string, alpha: number) => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => `${char}${char}`)
          .join("")
      : normalized;
  const value = Number.parseInt(expanded, 16);
  if (Number.isNaN(value)) return `rgba(148,163,184,${alpha})`;
  const red = (value >> 16) & 255;
  const green = (value >> 8) & 255;
  const blue = value & 255;
  return `rgba(${red},${green},${blue},${alpha})`;
};
const getRatingDisplayStatus = (value: number | undefined): Status | null => {
  if (typeof value !== "number" || Number.isNaN(value)) return null;
  if (value >= 7.5) return value >= 9 ? "green_strong" : "green";
  if (value >= 6.5) return "yellow";
  return "red";
};
const getKpiDisplayStatus = (node: Pick<ReportNode, "valueType" | "actual" | "colorActual" | "status">): Status =>
  typeof node.colorActual === "number"
    ? getRatingDisplayStatus(node.colorActual) ?? node.status
    : node.valueType === "rating_1_10"
      ? getRatingDisplayStatus(node.actual) ?? node.status
      : node.status;

const workItemSurface = (kind: ReportWorkItem["kind"]) => {
  if (kind === "initiative") {
    return {
      background: "linear-gradient(180deg, rgba(17,24,39,0.96), rgba(8,13,20,0.92))",
      border: "rgba(251,146,60,0.38)",
      title: "#fed7aa",
      meta: "#cbd5e1",
    };
  }

  if (kind === "kpi-task") {
    return {
      background: "rgba(37,99,235,0.16)",
      border: "rgba(96,165,250,0.38)",
      title: "#bfdbfe",
      meta: "#93c5fd",
    };
  }

  return {
    background: "rgba(15,23,42,0.72)",
    border: "rgba(148,163,184,0.22)",
    title: "#f8fafc",
    meta: "#94a3b8",
  };
};
const formatWorkItemKindLabel = (kind: ReportWorkItem["kind"]) => {
  if (kind === "kpi-task") return "KPI Task";
  if (kind === "initiative-task") return "Initiative Task";
  return "Initiative";
};
const playTaskDoneSound = () => {
  const AudioContextConstructor =
    window.AudioContext ||
    (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextConstructor) return;

  const audioContext = new AudioContextConstructor();
  const now = audioContext.currentTime;
  const gain = audioContext.createGain();
  gain.gain.setValueAtTime(0.0001, now);
  gain.gain.exponentialRampToValueAtTime(0.13, now + 0.012);
  gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.34);
  gain.connect(audioContext.destination);

  [660, 990].forEach((frequency, index) => {
    const oscillator = audioContext.createOscillator();
    oscillator.type = "sine";
    oscillator.frequency.setValueAtTime(frequency, now + index * 0.08);
    oscillator.connect(gain);
    oscillator.start(now + index * 0.08);
    oscillator.stop(now + 0.28 + index * 0.08);
  });

  window.setTimeout(() => {
    void audioContext.close();
  }, 500);
};

const createId = () =>
  `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;

const getWorkEditorFocusKey = (focus: WorkEditorFocus) => {
  if (focus.kind === "initiative") return `initiative:${focus.initiativeId}`;
  if (focus.kind === "kpi-task") return `kpi-task:${focus.taskId}`;
  return `initiative-task:${focus.initiativeId}:${focus.taskId}`;
};

const createTask = (): TaskItem => ({
  id: createId(),
  title: "New task",
  owner: "",
  dueDate: "",
  status: "not-started",
  priority: "normal",
  effort: "medium",
  notes: "",
});

const createInitiative = (): InitiativeItem => ({
  id: createId(),
  title: "New initiative",
  owner: "",
  dueDate: "",
  status: "not-started",
  priority: "normal",
  effort: "medium",
  summary: "",
  tasks: [],
});

const createPollConfig = (kpi?: {
  label?: string;
  definition?: string;
  measurement?: string;
}): PollConfig => ({
  id: createId(),
  campaignRootKpiId: undefined,
  linkedKpiIds: [],
  title: "New KPI Poll",
  description: "",
  targetGroups: [],
  questionPrompt: buildPollQuestionPrompt(kpi ?? {}),
  questionType: "rating",
  isAnonymous: true,
  status: "draft",
  responses: [],
  invites: [],
});

const normalizePollConfig = (
  pollConfig?: (Partial<PollConfig> & { status?: string }) | null
): PollConfig | null => {
  if (!pollConfig) return null;
  const rawStatus = (pollConfig as { status?: string }).status;

  return {
    ...pollConfig,
    id: pollConfig.id ?? createId(),
    campaignRootKpiId:
      typeof pollConfig.campaignRootKpiId === "string" && pollConfig.campaignRootKpiId.trim().length > 0
        ? pollConfig.campaignRootKpiId
        : undefined,
    linkedKpiIds: Array.isArray(pollConfig.linkedKpiIds)
      ? Array.from(
          new Set(
            pollConfig.linkedKpiIds.filter(
              (linkedKpiId): linkedKpiId is string =>
                typeof linkedKpiId === "string" && linkedKpiId.trim().length > 0
            )
          )
        )
      : [],
    title: pollConfig.title ?? "New KPI Poll",
    description: pollConfig.description ?? "",
    questionPrompt: pollConfig.questionPrompt ?? "How would you rate this KPI on a 1-10 scale?",
    questionType: "rating",
    isAnonymous: pollConfig.isAnonymous ?? true,
    responses: Array.isArray(pollConfig.responses)
      ? pollConfig.responses.map((response) => ({
          ...response,
          comment: typeof response.comment === "string" ? response.comment : "",
        }))
      : [],
    invites: Array.isArray(pollConfig.invites) ? pollConfig.invites : [],
    targetGroups:
      Array.isArray(pollConfig.targetGroups) && pollConfig.targetGroups.length > 0
        ? pollConfig.targetGroups
        : pollConfig.targetGroup
          ? [pollConfig.targetGroup]
          : [],
    status: rawStatus === "published" || rawStatus === "active" ? "published" : "draft",
  };
};

const normalizeWorkspace = (workspace?: Workspace | null): Workspace => {
  const nextWorkspace = workspace ?? {};
  const nextCampus = defaultCampus;
  return {
    ...nextWorkspace,
    pollAudienceGroups:
      Array.isArray(nextWorkspace.pollAudienceGroups) && nextWorkspace.pollAudienceGroups.length > 0
        ? nextWorkspace.pollAudienceGroups
        : initialPollAudienceGroups,
    pollRecipients: Array.isArray(nextWorkspace.pollRecipients)
      ? nextWorkspace.pollRecipients.map((recipient) => ({
          id: recipient.id ?? createId(),
          firstName:
            recipient.firstName ??
            recipient.name?.trim().split(/\s+/).filter(Boolean)[0] ??
            "",
          lastName:
            recipient.lastName ??
            recipient.name?.trim().split(/\s+/).filter(Boolean).slice(1).join(" ") ??
            "",
          email: recipient.email ?? "",
          groups: Array.isArray(recipient.groups) ? recipient.groups : [],
          name: recipient.name,
        }))
      : [],
    allNodes: (nextWorkspace.allNodes ?? []).map((node) => ({
      ...node,
      data: {
        ...node.data,
        unit: node.data.dataSource === "Poll" ? "" : node.data.unit ?? "",
        valueType: getNormalizedValueType(node.data.dataSource, node.data.valueType),
        calculationMode: normalizeCalculationMode(node.data.calculationMode, node.data.isRollup),
        isRollup: normalizeCalculationMode(node.data.calculationMode, node.data.isRollup) !== "direct",
        pollConfig: normalizePollConfig(node.data.pollConfig),
        actualHistory:
          node.data.dataSource === "Poll"
            ? derivePollActualHistory(
                normalizePollConfig(node.data.pollConfig),
                node.data.frequency ?? "weekly",
                "global",
                nextCampus
              )
            : node.data.actualHistory ?? {},
      },
    })),
  };
};

const formatPollResponseValue = (value: PollResponseValue) => String(value);

const getPollCampaignKpiIds = (masterKpiId: string, pollConfig?: PollConfig | null) =>
  Array.from(new Set([masterKpiId, ...((pollConfig?.linkedKpiIds ?? []).filter(Boolean))]));

const getPollCampaignSetup = (
  nodes: PersistedNode[],
  masterKpiId: string,
  pollConfig: PollConfig | null | undefined,
  referenceDate: Date,
  campus: Campus
) => {
  const campaignKpis = getPollCampaignKpiIds(masterKpiId, pollConfig)
    .map((kpiId) => nodes.find((node) => node.id === kpiId))
    .filter((node): node is PersistedNode => Boolean(node));

  const autoConvertKpis = campaignKpis.filter((node) => node.data.dataSource !== "Poll");
  const missingTargetKpis = campaignKpis.filter((node) => {
    const targetValue = getValueForContext(
      node.data.targetHistory ?? {},
      referenceDate,
      node.data.frequency ?? "weekly",
      "global",
      campus,
      node.data.valueType ?? "count",
      "current",
      node.data.metricPersistence ?? "period_only"
    );
    return typeof targetValue !== "number";
  });

  const frequencyGroups = campaignKpis.reduce<Record<Frequency, string[]>>((groups, node) => {
    const frequency = node.data.frequency ?? "weekly";
    const label = node.data.label || "Untitled KPI";
    if (!groups[frequency]) {
      groups[frequency] = [];
    }
    groups[frequency].push(label);
    return groups;
  }, {} as Record<Frequency, string[]>);

  const mismatchedFrequencies = Object.keys(frequencyGroups).length > 1;

  return {
    campaignKpis,
    autoConvertKpis,
    missingTargetKpis,
    frequencyGroups,
    mismatchedFrequencies,
  };
};

const summarizePollComments = (comments: string[]) => {
  const cleanedComments = comments
    .map((comment) => comment.trim())
    .filter((comment) => comment.length > 0);

  if (cleanedComments.length === 0) {
    return null;
  }

  const splitIntoClauses = (comment: string) =>
    comment
      .replace(/\s+(but|however|although|though|while)\s+/gi, ". ")
      .split(/[.!?;]+/)
      .map((part) => part.trim())
      .filter((part) => part.length >= 12);

  const positiveSignals = [
    "clear",
    "helpful",
    "strong",
    "good",
    "great",
    "love",
    "encouraging",
    "effective",
    "better",
    "improved",
  ];
  const concernSignals = [
    "confusing",
    "unclear",
    "hard",
    "difficult",
    "frustrating",
    "slow",
    "missing",
    "need",
    "lack",
    "weak",
    "poor",
    "performance",
    "performative",
    "staged",
    "stagey",
    "spotlight",
  ];
  const themePatterns = [
    { label: "clarity", words: ["clear", "clarity", "understand", "understanding", "confusing", "unclear"] },
    { label: "communication", words: ["communication", "communicate", "updates", "follow-up", "follow up"] },
    { label: "support", words: ["support", "care", "help", "encouragement", "pastor"] },
    { label: "consistency", words: ["consistent", "consistency", "regular", "follow through", "routine"] },
    { label: "engagement", words: ["engaged", "engagement", "participation", "connected", "involved"] },
    { label: "timeliness", words: ["timely", "late", "slow", "responsive", "response"] },
    { label: "measurement", words: ["measure", "measured", "metrics", "score", "rating"] },
  ];

  const positiveClauses: string[] = [];
  const concernClauses: string[] = [];
  const themeCounts = new Map<string, number>();

  cleanedComments.forEach((comment) => {
    splitIntoClauses(comment).forEach((clause) => {
      const normalized = clause.toLowerCase();
      const hasPositive = positiveSignals.some((word) => normalized.includes(word));
      const hasConcern = concernSignals.some((word) => normalized.includes(word));

      if (hasPositive && positiveClauses.length < 3) {
        positiveClauses.push(clause);
      }
      if (hasConcern && concernClauses.length < 3) {
        concernClauses.push(clause);
      }

      themePatterns.forEach((theme) => {
        if (theme.words.some((word) => normalized.includes(word))) {
          themeCounts.set(theme.label, (themeCounts.get(theme.label) ?? 0) + 1);
        }
      });
    });
  });

  const topThemes = Array.from(themeCounts.entries())
    .sort((left, right) => right[1] - left[1])
    .slice(0, 3)
    .map(([label]) => label);

  const themeProfiles: Record<string, { title: string; detail: string }> = {
    clarity: {
      title: "Clarity is coming through",
      detail: "People are reacting to whether expectations, direction, and communication feel easy to understand.",
    },
    communication: {
      title: "Communication is shaping trust",
      detail: "Updates, follow-through, and how information is shared are clearly affecting how people experience this KPI.",
    },
    support: {
      title: "Support is being felt",
      detail: "Comments suggest people notice encouragement, care, and whether help is meaningfully present.",
    },
    consistency: {
      title: "Consistency is becoming part of the story",
      detail: "People are paying attention to whether the experience feels steady, repeatable, and dependable over time.",
    },
    engagement: {
      title: "Engagement is strong",
      detail: "People are noticing participation, involvement, and responsiveness rather than passivity.",
    },
    timeliness: {
      title: "Responsiveness matters",
      detail: "The pace of follow-up and whether people experience things as timely is part of the feedback pattern.",
    },
    measurement: {
      title: "People are reacting to how this is measured",
      detail: "The comments are not only about the outcome, but also about what is being noticed, scored, or counted.",
    },
  };

  const formatSentence = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) return "";
    const sentence = trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    return /[.!?]$/.test(sentence) ? sentence : `${sentence}.`;
  };

  const shortSummaryLead =
    positiveClauses.length > concernClauses.length
      ? concernClauses.length > 0
        ? "Comments are mostly positive, with a few concerns to watch."
        : "Comments are mostly positive."
      : concernClauses.length > positiveClauses.length
        ? positiveClauses.length > 0
          ? "Comments are mixed, but concerns are coming through clearly."
          : "Comments are mostly concerned."
        : positiveClauses.length > 0 || concernClauses.length > 0
          ? "Comments are mixed."
          : "Comments are varied.";

  const shortSummary =
    topThemes.length > 0
      ? `${shortSummaryLead} The main theme is ${topThemes[0]}.`
      : shortSummaryLead;

  const coreThemeSections =
    topThemes.length > 0
      ? topThemes.map((theme, index) => {
          const profile = themeProfiles[theme] ?? {
            title: `${theme.charAt(0).toUpperCase() + theme.slice(1)} is showing up in the feedback`,
            detail: "This theme is appearing often enough to shape the overall read of the comments.",
          };
          const example = positiveClauses[index] ?? concernClauses[index] ?? null;
          return [
            `${index + 1}. ${profile.title}`,
            `- ${profile.detail}`,
            example ? `- Example signal: ${formatSentence(example)}` : null,
          ]
            .filter(Boolean)
            .join("\n");
        })
      : [
          [
            "1. The feedback is varied",
            "- There is not one dominant theme yet, so the comments are better read as directional than definitive.",
            `- Example signal: ${formatSentence(cleanedComments[0])}`,
          ].join("\n"),
        ];

  const keyTensionSection =
    positiveClauses.length > 0 && concernClauses.length > 0
      ? [
          "Key Tension",
          `People are seeing real value here, but there is also a caution around how ${topThemes[0] ?? "the experience"} is landing.`,
          `- Positive side: ${formatSentence(positiveClauses[0])}`,
          `- Risk side: ${formatSentence(concernClauses[0])}`,
        ].join("\n")
      : positiveClauses.length > 1
        ? [
            "Key Tension",
            "There is not a major negative thread yet, but there is still a need to protect what people are responding well to.",
            `- Reinforce: ${formatSentence(positiveClauses[0])}`,
            `- Also preserve: ${formatSentence(positiveClauses[1])}`,
          ].join("\n")
        : [
            "Key Tension",
            "No single tension dominates the feedback yet.",
          ].join("\n");

  const strategicInsightSection =
    concernClauses.length > 0
      ? [
          "Strategic Insight",
          "This looks more like refinement than overhaul.",
          "- Keep reinforcing what people already value.",
          `- Address this concern early: ${formatSentence(concernClauses[0])}`,
        ].join("\n")
      : [
          "Strategic Insight",
          "This reads more like momentum to build on than a problem to solve.",
          positiveClauses.length > 0
            ? `- Keep leaning into: ${formatSentence(positiveClauses[0])}`
            : "- Keep reinforcing the signals that are already landing well.",
        ].join("\n");

  const bottomLineSection =
    positiveClauses.length > concernClauses.length
      ? concernClauses.length > 0
        ? "Bottom Line\nThe feedback is largely encouraging, with one or two concerns worth shepherding before they grow."
        : "Bottom Line\nThe feedback is clearly encouraging and points to something healthy worth strengthening."
      : concernClauses.length > positiveClauses.length
        ? "Bottom Line\nThe feedback is mixed and caution is rising enough that it should be addressed directly."
        : "Bottom Line\nThe feedback is mixed, but it gives a useful read on where to tune rather than where to rebuild.";

  const longSummary = [
    "Core Themes",
    ...coreThemeSections.flatMap((section, index) => (index > 0 ? ["", section] : [section])),
    "",
    keyTensionSection,
    "",
    strategicInsightSection,
    "",
    bottomLineSection,
  ].join("\n");

  return {
    shortSummary,
    longSummary,
  };
};

const getPollRecipientLabel = (recipient: PollRecipient) => {
  const fullName = [recipient.firstName?.trim(), recipient.lastName?.trim()]
    .filter(Boolean)
    .join(" ");
  return fullName || recipient.name?.trim() || "Unnamed recipient";
};

const createToken = () =>
  `${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}${Math.random()
    .toString(36)
    .slice(2, 8)}`;

export default function Reports({
  signedInUser = null,
  onSignOut,
}: {
  signedInUser?: AppUser | null;
  onSignOut?: () => void;
}) {
  const ownerInputRef = React.useRef<HTMLSelectElement | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [enterprises, setEnterprises] = React.useState<EnterpriseSummary[]>([]);
  const [selectedEnterpriseId, setSelectedEnterpriseId] = React.useState<number | null>(null);
  const [workspace, setWorkspace] = React.useState<Workspace | null>(null);
  const [workspaceUsers, setWorkspaceUsers] = React.useState<AppUser[]>([]);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [selectedLens, setSelectedLens] = React.useState<string>("all");
  const [selectedStatusFilter, setSelectedStatusFilter] =
    React.useState<KpiStatusFilter>("red");
  const [summaryDisplayMode, setSummaryDisplayMode] = React.useState<SummaryDisplayMode>("kpis");
  const [workOwnerFilter, setWorkOwnerFilter] = React.useState<string>("all");
  const [workPriorityFilter, setWorkPriorityFilter] = React.useState<WorkPriorityFilter>("all");
  const [workStatusFilter, setWorkStatusFilter] = React.useState<WorkStatusFilter>("all");
  const [workDueFilter, setWorkDueFilter] = React.useState<WorkDueFilter>("all");
  const [workEffortFilter, setWorkEffortFilter] = React.useState<WorkEffortFilter>("all");
  const [workSortMode, setWorkSortMode] = React.useState<WorkSortMode>("due-date");
  const [selectedOwner, setSelectedOwner] = React.useState<string>("all");
  const [selectedKpiId, setSelectedKpiId] = React.useState<string | null>(null);
  const [editingKpiId, setEditingKpiId] = React.useState<string | null>(null);
  const [pollEditorKpiId, setPollEditorKpiId] = React.useState<string | null>(null);
  const [pollEditorMode, setPollEditorMode] = React.useState<PollEditorMode>("view");
  const [isSettingsLibrariesOpen, setIsSettingsLibrariesOpen] = React.useState(false);
  const [isSettingsLensesCollapsed, setIsSettingsLensesCollapsed] = React.useState(true);
  const [isSettingsKpisCollapsed, setIsSettingsKpisCollapsed] = React.useState(true);
  const [isSettingsReferenceListsCollapsed, setIsSettingsReferenceListsCollapsed] = React.useState(true);
  const [isPollSummaryExpanded, setIsPollSummaryExpanded] = React.useState(false);
  const [pollCommentsCopyMessage, setPollCommentsCopyMessage] = React.useState<string | null>(null);
  const [isPollEmailComposerOpen, setIsPollEmailComposerOpen] = React.useState(false);
  const [pollEmailSubject, setPollEmailSubject] = React.useState("");
  const [pollEmailGreeting, setPollEmailGreeting] = React.useState("");
  const [pollEmailBody, setPollEmailBody] = React.useState("");
  const [pollEmailClosing, setPollEmailClosing] = React.useState("");
  const [pollEmailStatusMessage, setPollEmailStatusMessage] = React.useState<string | null>(null);
  const [isSendingPollEmails, setIsSendingPollEmails] = React.useState(false);
  const [taskUpdateEmailStatusMessage, setTaskUpdateEmailStatusMessage] = React.useState<string | null>(null);
  const [isSendingTaskUpdateEmails, setIsSendingTaskUpdateEmails] = React.useState(false);
  const [selectedUpdateInitiativeKey, setSelectedUpdateInitiativeKey] = React.useState("");
  const [isKpiInfoOpen, setIsKpiInfoOpen] = React.useState(true);
  const [isKpiWorkOpen, setIsKpiWorkOpen] = React.useState(false);
  const [workEditorFocus, setWorkEditorFocus] = React.useState<WorkEditorFocus | null>(null);
  const [workEditorFocusNonce, setWorkEditorFocusNonce] = React.useState(0);
  const [isSaving, setIsSaving] = React.useState(false);
  const [saveMessage, setSaveMessage] = React.useState<string | null>(null);
  const [changeVersion, setChangeVersion] = React.useState(0);
  const [lastSavedVersion, setLastSavedVersion] = React.useState(0);
  const [reportReferenceDate, setReportReferenceDate] = React.useState<Date>(initialReferenceDate);
  const [reportAggregationMode, setReportAggregationMode] =
    React.useState<AggregationMode>("current");
  const [reportComparisonMode, setReportComparisonMode] =
    React.useState<ComparisonMode>("target");
  const workEditorItemRefs = React.useRef<Record<string, HTMLDivElement | null>>({});

  const loadWorkspace = React.useCallback(
    async (options?: { preserveSelection?: boolean; silent?: boolean }) => {
      if (selectedEnterpriseId === null) return;

      if (!options?.silent) {
        setIsLoading(true);
        setError(null);
      }

      try {
        const response = await api.get(`/enterprises/${selectedEnterpriseId}/workspace`);
        const nextWorkspace = normalizeWorkspace((response.data.workspace ?? {}) as Workspace);
        const cacheEnterprise = enterprises.find((enterprise) => enterprise.id === selectedEnterpriseId) ?? null;
        setWorkspace(nextWorkspace);
        window.localStorage.setItem(
          getWorkspaceCacheKey(selectedEnterpriseId, cacheEnterprise),
          JSON.stringify(nextWorkspace)
        );

        if (!options?.preserveSelection) {
          setSelectedKpiId(null);
          setEditingKpiId(null);
          setPollEditorKpiId(null);
          setSelectedLens("all");
          setSelectedOwner("all");
        }

        setChangeVersion(0);
        setLastSavedVersion(0);
        window.localStorage.setItem(activeEnterpriseStorageKey, String(selectedEnterpriseId));
      } catch (loadError) {
        console.error("Failed to load workspace for reports", loadError);
        setError("Could not load the saved workspace for this enterprise.");
      } finally {
        if (!options?.silent) {
          setIsLoading(false);
        }
      }
    },
    [enterprises, selectedEnterpriseId]
  );

  React.useEffect(() => {
    const loadEnterprises = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await api.get("/enterprises/");
        const nextEnterprises = (response.data as EnterpriseSummary[]).slice().sort((left, right) => {
          const leftUpdatedAt = left.updated_at ? new Date(left.updated_at).getTime() : 0;
          const rightUpdatedAt = right.updated_at ? new Date(right.updated_at).getTime() : 0;
          return rightUpdatedAt - leftUpdatedAt;
        });

        setEnterprises(nextEnterprises);

        if (nextEnterprises.length === 0) {
          setSelectedEnterpriseId(null);
          return;
        }

        const queryEnterpriseId = Number(searchParams.get("enterpriseId"));
        const storedEnterpriseId = Number(window.localStorage.getItem(activeEnterpriseStorageKey));
        const preferredEnterprise =
          nextEnterprises.find((enterprise) => enterprise.id === queryEnterpriseId) ??
          nextEnterprises.find((enterprise) => enterprise.id === storedEnterpriseId) ??
          nextEnterprises[0];

        setSelectedEnterpriseId(preferredEnterprise.id);
      } catch (loadError) {
        console.error("Failed to load enterprises for reports", loadError);
        setError("Could not load enterprises from the backend.");
      } finally {
        setIsLoading(false);
      }
    };

    void loadEnterprises();
  }, [searchParams]);

  React.useEffect(() => {
    if (selectedEnterpriseId === null) return;
    void loadWorkspace();
  }, [loadWorkspace, selectedEnterpriseId]);

  const selectedEnterprise =
    selectedEnterpriseId === null
      ? null
      : enterprises.find((enterprise) => enterprise.id === selectedEnterpriseId) ?? null;
  const weekStartsOn = normalizeWeekStartsOn(selectedEnterprise?.week_starts_on);

  React.useEffect(() => {
    if (selectedEnterpriseId === null) {
      setWorkspaceUsers([]);
      return;
    }

    let isActive = true;
    const loadUsers = async () => {
      try {
        const response = await api.get("/users/");
        if (isActive) {
          setWorkspaceUsers(response.data as AppUser[]);
        }
      } catch (loadUsersError) {
        console.error("Failed to load users for report owners", loadUsersError);
        if (isActive) {
          setWorkspaceUsers([]);
        }
      }
    };

    void loadUsers();

    return () => {
      isActive = false;
    };
  }, [selectedEnterpriseId]);

  React.useEffect(() => {
    const requestedEditKpiId = searchParams.get("editKpiId");
    if (!requestedEditKpiId || !workspace?.allNodes?.some((node) => node.id === requestedEditKpiId)) {
      return;
    }

    setEditingKpiId(requestedEditKpiId);
    setSelectedKpiId(requestedEditKpiId);

    const nextSearchParams = new URLSearchParams(searchParams);
    ["editKpiId", "panel", "taskId", "initiativeId", "lens"].forEach((paramName) => {
      nextSearchParams.delete(paramName);
    });
    setSearchParams(nextSearchParams, { replace: true });
  }, [searchParams, setSearchParams, workspace?.allNodes]);

  React.useEffect(() => {
    if (searchParams.get("view") === "initiatives") {
      setSummaryDisplayMode("initiatives");
    }

    const requestedWorkOwner = searchParams.get("workOwner");
    if (requestedWorkOwner) {
      setWorkOwnerFilter(requestedWorkOwner);
    }

    const requestedWorkDue = searchParams.get("workDue");
    if (
      requestedWorkDue === "all" ||
      requestedWorkDue === "past-due" ||
      requestedWorkDue === "this-week" ||
      requestedWorkDue === "next-week" ||
      requestedWorkDue === "future" ||
      requestedWorkDue === "unassigned"
    ) {
      setWorkDueFilter(requestedWorkDue);
    }

    const requestedWorkSort = searchParams.get("workSort");
    if (
      requestedWorkSort === "due-date" ||
      requestedWorkSort === "priority" ||
      requestedWorkSort === "assigned-to"
    ) {
      setWorkSortMode(requestedWorkSort);
    }
  }, [searchParams]);

  React.useEffect(() => {
    setReportReferenceDate(getInitialReferenceDate(weekStartsOn));
  }, [weekStartsOn, workspace]);

  React.useEffect(() => {
    setReportAggregationMode(
      workspace?.aggregationMode === "mtd" ||
        workspace?.aggregationMode === "qtd" ||
        workspace?.aggregationMode === "ytd" ||
        workspace?.aggregationMode === "current"
        ? workspace.aggregationMode
        : Boolean(workspace?.isCumulativeView)
          ? "ytd"
          : "current"
    );
    setReportComparisonMode(
      workspace?.comparisonMode === "previous_period" ||
        workspace?.comparisonMode === "same_period_last_year" ||
        workspace?.comparisonMode === "target"
        ? workspace.comparisonMode
        : "target"
    );
  }, [workspace?.aggregationMode, workspace?.comparisonMode, workspace?.isCumulativeView]);

  const workspaceLensConfigs = React.useMemo(
    () => workspace?.lensConfigs ?? {},
    [workspace?.lensConfigs]
  );
  const workspaceLenses = React.useMemo(() => {
    const configuredLenses = workspace?.availableLenses ?? [];
    const fallbackLenses = Object.keys(workspaceLensConfigs);
    return (configuredLenses.length > 0 ? configuredLenses : fallbackLenses).filter(Boolean);
  }, [workspace?.availableLenses, workspaceLensConfigs]);

  const userOwnerOptions = React.useMemo(
    () => {
      const seen = new Set<string>();
      return workspaceUsers
        .filter(
          (user) =>
            user.is_active &&
            (selectedEnterpriseId === null ||
              user.enterprise_id == null ||
              user.enterprise_id === selectedEnterpriseId)
        )
        .map((user) => user.name.trim() || user.email.trim())
        .filter((owner) => {
          if (!owner || seen.has(owner.toLowerCase())) {
            return false;
          }
          seen.add(owner.toLowerCase());
          return true;
        })
        .sort((left, right) => left.localeCompare(right));
    },
    [selectedEnterpriseId, workspaceUsers]
  );
  const userOwnerSet = React.useMemo(
    () => new Set(userOwnerOptions.map((owner) => owner.toLowerCase())),
    [userOwnerOptions]
  );
  const cleanOwner = React.useCallback(
    (owner?: string | null) => {
      const nextOwner = owner?.trim() ?? "";
      return userOwnerSet.has(nextOwner.toLowerCase()) ? nextOwner : "";
    },
    [userOwnerSet]
  );
  const ownerOptions = userOwnerOptions;

  const reportNodes = React.useMemo<ReportNode[]>(() => {
    const rawNodes = workspace?.allNodes ?? [];
    const rawEdges = workspace?.allEdges ?? [];
    const campus = defaultCampus;
    const aggregationMode = reportAggregationMode;
    const comparisonMode = reportComparisonMode;

    const baseNodes = rawNodes.map((node) => {
      const data = node.data ?? ({} as PersistedNodeData);
      const metricPersistence =
        data.metricPersistence === "carry_forward" || data.metricPersistence === "period_only"
          ? data.metricPersistence
          : data.frequency === "periodic" || data.dataSource === "Poll" || Boolean(data.eventDate)
            ? "carry_forward"
            : "period_only";
      const frequency = data.frequency ?? "weekly";
      const scope: Scope = "global";
      const valueType = data.valueType ?? "count";
      const calculationMode = normalizeCalculationMode(data.calculationMode, data.isRollup);
      const target =
        comparisonMode === "target"
          ? getValueForContext(
              data.targetHistory ?? {},
              reportReferenceDate,
              frequency,
              scope,
              campus,
              valueType,
              aggregationMode,
              metricPersistence
            )
          : getComparisonValueForContext(
              data.actualHistory ?? {},
              reportReferenceDate,
              frequency,
              scope,
              campus,
              valueType,
              aggregationMode,
              comparisonMode
            );
      const actual = getValueForContext(
        data.actualHistory ?? {},
        reportReferenceDate,
        frequency,
        scope,
        campus,
        valueType,
        aggregationMode,
        metricPersistence
      );

      return {
        id: node.id,
        label: data.label ?? "New KPI",
        target,
        actual,
        unit: data.unit ?? "",
        owner: cleanOwner(data.owner),
        scope,
        frequency,
        valueType,
        performanceDirection: data.performanceDirection ?? "higher_is_better",
        eventDate: data.eventDate ?? "",
        status: data.status ?? "neutral" as Status,
        statusNote: isDateInFuture(reportReferenceDate, data.eventDate)
          ? "Upcoming"
          : [
              aggregationMode !== "current" ? `${formatAggregationLabel(aggregationMode)} view` : null,
              comparisonMode !== "target"
                ? `Compared to ${formatComparisonLabel(comparisonMode).toLowerCase()}`
                : null,
            ]
              .filter(Boolean)
              .join(" | ") || undefined,
        colorActual: data.colorActual,
        metricsRestricted: data.metricsRestricted ?? false,
        isRollup: calculationMode !== "direct",
        calculationMode,
        lenses: data.lenses ?? [],
        initiatives: data.initiatives ?? [],
        tasks: data.tasks ?? [],
      };
    });

    const nodeMap = new Map(baseNodes.map((node) => [node.id, node]));
    const incomingMap = new Map<string, PersistedEdge[]>();

    rawEdges.forEach((edge) => {
      if (!incomingMap.has(edge.target)) {
        incomingMap.set(edge.target, []);
      }
      incomingMap.get(edge.target)!.push(edge);
    });

    const computeScore = (
      nodeId: string,
      visiting = new Set<string>()
    ): { score: number | null; statusNote?: string; target?: number; actual?: number; colorActual?: number } => {
      if (visiting.has(nodeId)) {
        return { score: null, statusNote: "Circular dependency detected" };
      }

      const node = nodeMap.get(nodeId);
      if (!node) return { score: null, statusNote: "Missing KPI" };
      if (node.metricsRestricted) {
        return {
          score: null,
          target: node.target,
          actual: node.actual,
          colorActual: node.colorActual,
        };
      }

      if (node.calculationMode === "direct") {
        if (node.statusNote === "Upcoming") {
          return { score: null, statusNote: node.statusNote };
        }

        return {
          score: getDirectScore(node.target, node.actual, node.performanceDirection),
          statusNote: node.statusNote,
          target: node.target,
          actual: node.actual,
          colorActual: node.valueType === "rating_1_10" ? node.actual : undefined,
        };
      }

      if (node.calculationMode === "formula_percentage") {
        const persistedNode = rawNodes.find((candidate) => candidate.id === nodeId);
        const numeratorId = persistedNode?.data.formulaNumeratorKpiId;
        const denominatorId = persistedNode?.data.formulaDenominatorKpiId;

        if (!numeratorId || !denominatorId) {
          return {
            score: null,
            statusNote: "Select numerator and denominator KPIs for this percentage formula",
          };
        }

        const nextVisiting = new Set(visiting);
        nextVisiting.add(nodeId);
        const numeratorResult = computeScore(numeratorId, nextVisiting);
        const denominatorResult = computeScore(denominatorId, nextVisiting);

        if (numeratorResult.statusNote || denominatorResult.statusNote) {
          return {
            score: null,
            statusNote: "Numerator or denominator KPI needs attention before this percentage can calculate",
          };
        }

        const actual = calculateFormulaPercentage(
          numeratorResult.actual,
          denominatorResult.actual
        );

        if (
          typeof denominatorResult.actual === "number" &&
          denominatorResult.actual <= 0
        ) {
          return { score: null, statusNote: "Actual denominator must be greater than 0" };
        }

        if (typeof node.target !== "number") {
          return {
            score: null,
            actual,
            statusNote: "Set a target for this percentage KPI",
          };
        }

        if (typeof actual !== "number") {
          return {
            score: null,
            target: node.target,
            actual,
            statusNote: "Waiting on numerator and denominator actual values",
          };
        }

        return {
          score: getDirectScore(node.target, actual, node.performanceDirection),
          target: node.target,
          actual,
          colorActual: node.valueType === "rating_1_10" ? actual : undefined,
        };
      }

      const incomingEdges = incomingMap.get(nodeId) ?? [];
      if (incomingEdges.length === 0) {
        return { score: null, statusNote: "Calculated KPI has no child KPIs" };
      }

      if (node.calculationMode === "calculated_values") {
        const childResults = incomingEdges.map((edge) => {
          const nextVisiting = new Set(visiting);
          nextVisiting.add(nodeId);
          return computeScore(edge.source, nextVisiting);
        });
        const target = calculateChildValue(
          childResults.map((result) => result.target),
          node.valueType
        );
        const actual = calculateChildValue(
          childResults.map((result) => result.actual),
          node.valueType
        );
        const hasMissingTarget = childResults.some((result) => typeof result.target !== "number");
        const hasMissingActual = childResults.some((result) => typeof result.actual !== "number");

        if (typeof target !== "number" || typeof actual !== "number") {
          return {
            score: null,
            target,
            actual,
            statusNote:
              hasMissingTarget && hasMissingActual
                ? "Waiting on child KPI targets and actuals"
                : hasMissingTarget
                  ? "Waiting on child KPI targets"
                  : "Waiting on child KPI actuals",
          };
        }

        return {
          score: getDirectScore(target, actual, node.performanceDirection),
          target,
          actual,
          colorActual: node.valueType === "rating_1_10" ? actual : undefined,
        };
      }

      let configuredWeightTotal = 0;
      let weightedSum = 0;
      let validWeightTotal = 0;
      let weightedColorActualSum = 0;
      let validColorActualWeightTotal = 0;
      let missingWeight = false;
      let missingChildScore = false;

      for (const edge of incomingEdges) {
        const influence = normalizeInfluence(edge);
        if (influence === null) {
          missingWeight = true;
          continue;
        }

        configuredWeightTotal += influence;

        const nextVisiting = new Set(visiting);
        nextVisiting.add(nodeId);
        const childResult = computeScore(edge.source, nextVisiting);
        const childScore = childResult.score ?? getScoreFromRatingActual(childResult.colorActual);
        if (childScore === null) {
          missingChildScore = true;
          continue;
        }

        weightedSum += childScore * influence;
        validWeightTotal += influence;
        if (typeof childResult.colorActual === "number") {
          weightedColorActualSum += childResult.colorActual * influence;
          validColorActualWeightTotal += influence;
        }
      }

      if (missingWeight) {
        return { score: null, statusNote: "Child influence is incomplete" };
      }

      if (Math.abs(configuredWeightTotal - 1) > 0.001) {
        return {
          score: null,
          statusNote: `Influence totals ${Math.round(configuredWeightTotal * 100)}%`,
        };
      }

      if (missingChildScore || validWeightTotal === 0) {
        return { score: null, statusNote: "Waiting on child KPI performance" };
      }

      const score = weightedSum / validWeightTotal;
      return {
        score,
        target: node.target,
        actual: node.actual,
        colorActual:
          validColorActualWeightTotal > 0
            ? weightedColorActualSum / validColorActualWeightTotal
            : undefined,
      };
    };

    return baseNodes.map((node) => {
      const result = computeScore(node.id);
      const preserveRestrictedPerformance = Boolean(node.metricsRestricted);
      return {
        ...node,
        target: node.calculationMode === "calculated_values" ? result.target : node.target,
        actual:
          node.calculationMode === "calculated_values" ||
          node.calculationMode === "formula_percentage"
            ? result.actual
            : node.actual,
        status: preserveRestrictedPerformance ? node.status : getStatusFromScore(result.score),
        statusNote: preserveRestrictedPerformance ? undefined : result.statusNote ?? node.statusNote,
        colorActual: preserveRestrictedPerformance ? node.colorActual : result.colorActual,
      };
    });
  }, [cleanOwner, reportAggregationMode, reportComparisonMode, reportReferenceDate, workspace]);

  React.useEffect(() => {
    if (selectedLens === "all") return;
    if (workspaceLenses.includes(selectedLens)) return;
    setSelectedLens("all");
  }, [selectedLens, workspaceLenses]);

  React.useEffect(() => {
    if (selectedOwner === "all") return;
    if (ownerOptions.includes(selectedOwner)) return;
    setSelectedOwner("all");
  }, [ownerOptions, selectedOwner]);

  const audienceGroupOptions = React.useMemo(() => {
    const listedGroups = (workspace?.pollAudienceGroups ?? [])
      .map((group) => group.trim())
      .filter((group) => group.length > 0);

    if (listedGroups.length > 0) {
      return Array.from(new Set(listedGroups)).sort((left, right) => left.localeCompare(right));
    }

    return initialPollAudienceGroups;
  }, [workspace?.pollAudienceGroups]);

  const lensFilteredNodes = React.useMemo(
    () =>
      selectedLens === "all"
        ? reportNodes
        : reportNodes.filter((node) => node.lenses.includes(selectedLens)),
    [reportNodes, selectedLens]
  );

  const filteredNodes = React.useMemo(
    () =>
      selectedOwner === "all"
        ? lensFilteredNodes
        : lensFilteredNodes.filter((node) => node.owner === selectedOwner),
    [lensFilteredNodes, selectedOwner]
  );

  const statusCounts = React.useMemo(
    () =>
      filteredNodes.reduce<Record<Status, number>>(
        (counts, node) => {
          counts[getKpiDisplayStatus(node)] += 1;
          return counts;
        },
        { green: 0, green_strong: 0, yellow: 0, red: 0, neutral: 0 }
      ),
    [filteredNodes]
  );

  const statusFilteredNodes = React.useMemo(() => {
    if (selectedStatusFilter === "all") return filteredNodes;
    if (selectedStatusFilter === "green") {
      return filteredNodes.filter(
        (node) => {
          const displayStatus = getKpiDisplayStatus(node);
          return displayStatus === "green" || displayStatus === "green_strong";
        }
      );
    }
    return filteredNodes.filter((node) => getKpiDisplayStatus(node) === selectedStatusFilter);
  }, [filteredNodes, selectedStatusFilter]);

  const kpiRelationships = React.useMemo(() => {
    const parentByChild = new Map<string, string>();
    const childrenByParent = new Map<string, string[]>();
    const labelsById = new Map(
      (workspace?.allNodes ?? []).map((node) => [node.id, node.data.label ?? "Untitled KPI"])
    );

    (workspace?.allEdges ?? []).forEach((edge) => {
      if (!childrenByParent.has(edge.target)) {
        childrenByParent.set(edge.target, []);
      }

      childrenByParent.get(edge.target)!.push(edge.source);
      if (!parentByChild.has(edge.source)) {
        parentByChild.set(edge.source, edge.target);
      }
    });

    const getLineageLabels = (nodeId: string) => {
      const lineage: string[] = [];
      const visited = new Set<string>();
      let currentId: string | undefined = nodeId;

      while (currentId && !visited.has(currentId)) {
        visited.add(currentId);
        lineage.unshift(labelsById.get(currentId) ?? "Untitled KPI");
        currentId = parentByChild.get(currentId);
      }

      return lineage;
    };

    const lineageLabelsById = new Map(
      (workspace?.allNodes ?? []).map((node) => [node.id, getLineageLabels(node.id)])
    );

    const lineageLabelById = new Map(
      Array.from(lineageLabelsById.entries()).map(([nodeId, labels]) => [
        nodeId,
        labels.join(" > "),
      ])
    );

    return { parentByChild, childrenByParent, labelsById, lineageLabelsById, lineageLabelById };
  }, [workspace?.allEdges, workspace?.allNodes]);

  const treeFilteredNodes = React.useMemo<ReportTreeNode[]>(() => {
    const visibleNodeMap = new Map(statusFilteredNodes.map((node) => [node.id, node]));
    const rawEdges = workspace?.allEdges ?? [];
    const visibleChildrenByParent = new Map<string, string[]>();
    const visibleParentByChild = new Map<string, string>();

    rawEdges.forEach((edge) => {
      if (!visibleNodeMap.has(edge.source) || !visibleNodeMap.has(edge.target)) return;

      if (!visibleChildrenByParent.has(edge.target)) {
        visibleChildrenByParent.set(edge.target, []);
      }

      visibleChildrenByParent.get(edge.target)!.push(edge.source);
      if (!visibleParentByChild.has(edge.source)) {
        visibleParentByChild.set(edge.source, edge.target);
      }
    });

    const sortIdsByLabel = (ids: string[]) =>
      ids
        .slice()
        .sort((left, right) =>
          (visibleNodeMap.get(left)?.label ?? "").localeCompare(
            visibleNodeMap.get(right)?.label ?? ""
          )
        );

    const rootIds = sortIdsByLabel(
      statusFilteredNodes
        .map((node) => node.id)
        .filter((nodeId) => !visibleParentByChild.has(nodeId))
    );

    const orderedNodes: ReportTreeNode[] = [];
    const visited = new Set<string>();

    const visitNode = (nodeId: string, depth: number) => {
      if (visited.has(nodeId)) return;
      const node = visibleNodeMap.get(nodeId);
      if (!node) return;

      visited.add(nodeId);
      orderedNodes.push({ depth, node });

      const childIds = sortIdsByLabel(visibleChildrenByParent.get(nodeId) ?? []);
      childIds.forEach((childId) => visitNode(childId, depth + 1));
    };

    rootIds.forEach((nodeId) => visitNode(nodeId, 0));

    const unvisitedIds = sortIdsByLabel(
      statusFilteredNodes
        .map((node) => node.id)
        .filter((nodeId) => !visited.has(nodeId))
    );
    unvisitedIds.forEach((nodeId) => visitNode(nodeId, 0));

    return orderedNodes;
  }, [statusFilteredNodes, workspace?.allEdges]);

  const workSourceNodes = React.useMemo(() => {
    const nodeMap = new Map(lensFilteredNodes.map((node) => [node.id, node]));
    const includedIds = new Set<string>();

    const includeWithDescendants = (nodeId: string) => {
      if (includedIds.has(nodeId)) return;
      const node = nodeMap.get(nodeId);
      if (!node) return;

      includedIds.add(nodeId);
      (kpiRelationships.childrenByParent.get(nodeId) ?? []).forEach(includeWithDescendants);
    };

    statusFilteredNodes.forEach((node) => includeWithDescendants(node.id));

    return Array.from(includedIds)
      .map((nodeId) => nodeMap.get(nodeId))
      .filter((node): node is ReportNode => Boolean(node))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [kpiRelationships.childrenByParent, lensFilteredNodes, statusFilteredNodes]);

  const workItems = React.useMemo<ReportWorkItem[]>(() => {
    return workSourceNodes.flatMap((node) => {
      const directTasks = node.tasks.map((task) => ({
        kind: "kpi-task" as const,
        kpiId: node.id,
        taskId: task.id,
        title: task.title,
        owner: cleanOwner(task.owner),
        dueDate: task.dueDate,
        status: task.status,
        priority: task.priority,
        effort: task.effort,
        kpiLabel: node.label,
        kpiLineageLabel: kpiRelationships.lineageLabelById.get(node.id) ?? node.label,
        indentLevel: 0,
      }));

      const initiativeItems = node.initiatives.flatMap((initiative) => [
        {
          kind: "initiative" as const,
          kpiId: node.id,
          initiativeId: initiative.id,
          title: initiative.title,
          owner: cleanOwner(initiative.owner),
          dueDate: initiative.dueDate,
          status: initiative.status,
          priority: initiative.priority,
          effort: initiative.effort,
          kpiLabel: node.label,
          kpiLineageLabel: kpiRelationships.lineageLabelById.get(node.id) ?? node.label,
          initiativeTitle: initiative.title,
          indentLevel: 0,
        },
        ...initiative.tasks.map((task) => ({
          kind: "initiative-task" as const,
          kpiId: node.id,
          initiativeId: initiative.id,
          taskId: task.id,
          title: task.title,
          owner: cleanOwner(task.owner),
          dueDate: task.dueDate,
          status: task.status,
          priority: task.priority,
          effort: task.effort,
          kpiLabel: node.label,
          kpiLineageLabel: kpiRelationships.lineageLabelById.get(node.id) ?? node.label,
          initiativeTitle: initiative.title,
          indentLevel: 1,
        })),
      ]);

      return [...directTasks, ...initiativeItems];
    });
  }, [cleanOwner, kpiRelationships.lineageLabelById, workSourceNodes]);

  React.useEffect(() => {
    if (selectedKpiId === null) return;
    if (statusFilteredNodes.some((node) => node.id === selectedKpiId)) return;
    setSelectedKpiId(null);
  }, [selectedKpiId, statusFilteredNodes]);

  const displayedWorkItems = React.useMemo(
    () =>
      selectedKpiId === null
        ? []
        : workItems.filter((item) => item.kpiId === selectedKpiId),
    [selectedKpiId, workItems]
  );

  const sortedWorkItems = React.useMemo(() => {
    const priorityRank: Record<WorkPriority, number> = {
      critical: 0,
      high: 1,
      normal: 2,
      low: 3,
    };
    const statusRank: Record<WorkStatus, number> = {
      blocked: 0,
      "in-progress": 1,
      "not-started": 2,
      done: 3,
    };
    const itemKindRank: Record<ReportWorkItem["kind"], number> = {
      "kpi-task": 0,
      initiative: 1,
      "initiative-task": 2,
    };
    const sortValue = (item: ReportWorkItem) => {
      if (workSortMode === "assigned-to") {
        return item.owner || "zzzz-unassigned";
      }
      if (workSortMode === "priority") {
        return priorityRank[item.priority ?? "normal"];
      }
      return item.dueDate ? new Date(`${item.dueDate}T00:00:00`).getTime() : Number.POSITIVE_INFINITY;
    };
    const compareBySortMode = (left: ReportWorkItem, right: ReportWorkItem) => {
      const leftValue = sortValue(left);
      const rightValue = sortValue(right);
      if (typeof leftValue === "string" && typeof rightValue === "string") {
        return leftValue.localeCompare(rightValue);
      }
      return Number(leftValue) - Number(rightValue);
    };
    return workItems
      .filter((item) => item.kind === "initiative" || item.status !== "done")
      .slice()
      .sort((left, right) => {
        const sameInitiative =
          left.kpiId === right.kpiId &&
          left.initiativeId &&
          left.initiativeId === right.initiativeId;
        if (sameInitiative && left.kind === "initiative" && right.kind === "initiative-task") return -1;
        if (sameInitiative && left.kind === "initiative-task" && right.kind === "initiative") return 1;
        return (
          compareBySortMode(left, right) ||
          left.kpiLineageLabel.localeCompare(right.kpiLineageLabel) ||
          itemKindRank[left.kind] - itemKindRank[right.kind] ||
          priorityRank[left.priority ?? "normal"] - priorityRank[right.priority ?? "normal"] ||
          statusRank[left.status] - statusRank[right.status] ||
          left.title.localeCompare(right.title)
        );
      });
  }, [workItems, workSortMode]);

  const workOwnerOptions = React.useMemo(
    () =>
      Array.from(
        new Set(workItems.map((item) => item.owner).filter((owner): owner is string => Boolean(owner)))
      ).sort((left, right) => left.localeCompare(right)),
    [workItems]
  );

  const filteredWorkItems = React.useMemo(() => {
    const getItemKey = (item: ReportWorkItem) =>
      `${item.kind}:${item.kpiId}:${item.initiativeId ?? ""}:${item.taskId ?? ""}`;
    const getInitiativeParentKey = (item: ReportWorkItem) =>
      `initiative:${item.kpiId}:${item.initiativeId ?? ""}:`;
    const visibleKeys = new Set<string>();

    sortedWorkItems.forEach((item) => {
      const itemMatchesFilters = (() => {
        if (item.status === "done") return false;
        if (workOwnerFilter !== "all" && item.owner !== workOwnerFilter) return false;
        if (workPriorityFilter !== "all" && (item.priority ?? "normal") !== workPriorityFilter) return false;
        if (workStatusFilter !== "all" && item.status !== workStatusFilter) return false;
        if (workDueFilter !== "all" && !matchesWorkDueFilter(item.dueDate, workDueFilter)) return false;
        if (workEffortFilter !== "all" && (item.effort ?? "medium") !== workEffortFilter) return false;
        return true;
      })();

      if (!itemMatchesFilters) return;

      visibleKeys.add(getItemKey(item));
      if (item.kind === "initiative-task" && item.initiativeId) {
        visibleKeys.add(getInitiativeParentKey(item));
      }
    });

    return sortedWorkItems.filter((item) => visibleKeys.has(getItemKey(item)));
  },
    [
      sortedWorkItems,
      workDueFilter,
      workEffortFilter,
      workOwnerFilter,
      workPriorityFilter,
      workStatusFilter,
    ]
  );

  const updateWorkspaceNode = React.useCallback(
    (nodeId: string, updater: (node: PersistedNode) => PersistedNode) => {
      setWorkspace((previous) => {
        if (!previous?.allNodes) return previous;

        return {
          ...previous,
          allNodes: previous.allNodes.map((node) => (node.id === nodeId ? updater(node) : node)),
        };
      });
      setChangeVersion((current) => current + 1);
      setSaveMessage(null);
    },
    []
  );

  const onKpiFieldChange = React.useCallback(
    (nodeId: string, field: "label" | "owner", value: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onKpiMetricChange = React.useCallback(
    (nodeId: string, field: "target" | "actual", value: string) => {
      updateWorkspaceNode(nodeId, (node) => {
        const nextValue = value === "" ? undefined : Number(value);
        const historyField = field === "target" ? "targetHistory" : "actualHistory";

        return {
          ...node,
          data: {
            ...node.data,
            [historyField]: setValueForContext(
              node.data[historyField] ?? {},
              reportReferenceDate,
              node.data.frequency ?? "weekly",
              "global",
              defaultCampus,
              nextValue
            ),
          },
        };
      });
    },
    [reportReferenceDate, updateWorkspaceNode]
  );

  const onKpiDetailChange = React.useCallback(
    (
      nodeId: string,
      field:
        | "frequency"
        | "definition"
        | "measurement"
        | "updateNotes"
        | "eventDate"
        | "dataSource"
        | "valueType"
        | "formulaNumeratorKpiId"
        | "formulaDenominatorKpiId",
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
          ...(field === "dataSource" && value === "Poll" ? { valueType: "rating_1_10" } : {}),
          ...(field === "valueType" &&
          normalizeCalculationMode(node.data.calculationMode, node.data.isRollup) ===
            "formula_percentage"
            ? { valueType: "percentage" }
            : {}),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onKpiConfigChange = React.useCallback(
    (
      nodeId: string,
      field:
        | "performanceDirection"
        | "supportsCumulative"
        | "metricPersistence"
        | "calculationMode",
      value: string | boolean | MetricPersistence | KpiCalculationMode
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          [field]: value,
          ...(field === "calculationMode"
            ? {
                isRollup: (value as KpiCalculationMode) !== "direct",
                ...((value as KpiCalculationMode) === "formula_percentage"
                  ? { valueType: "percentage" }
                  : {}),
              }
            : {}),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onCreatePoll = React.useCallback(
    (nodeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          pollConfig:
            node.data.pollConfig ??
            createPollConfig({
              label: node.data.label,
              definition: node.data.definition,
              measurement: node.data.measurement,
            }),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onPollConfigChange = React.useCallback(
    (
      nodeId: string,
      field: keyof Omit<PollConfig, "id">,
      value: string | boolean
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          pollConfig: {
            ...(node.data.pollConfig ?? createPollConfig()),
            [field]: value,
          },
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onTogglePollAudienceGroup = React.useCallback(
    (nodeId: string, groupName: string) => {
      updateWorkspaceNode(nodeId, (node) => {
        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const currentGroups =
          currentPoll.targetGroups.length > 0
            ? currentPoll.targetGroups
            : currentPoll.targetGroup
              ? [currentPoll.targetGroup]
              : [];
        const nextGroups = currentGroups.includes(groupName)
          ? currentGroups.filter((group) => group !== groupName)
          : [...currentGroups, groupName];

        return {
          ...node,
          data: {
            ...node.data,
            pollConfig: {
              ...currentPoll,
              targetGroup: undefined,
              targetGroups: nextGroups,
            },
          },
        };
      });
    },
    [updateWorkspaceNode]
  );

  const onTogglePollLinkedKpi = React.useCallback(
    (nodeId: string, linkedKpiId: string) => {
      updateWorkspaceNode(nodeId, (node) => {
        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const currentLinkedKpiIds = currentPoll.linkedKpiIds ?? [];
        const nextLinkedKpiIds = currentLinkedKpiIds.includes(linkedKpiId)
          ? currentLinkedKpiIds.filter((currentId) => currentId !== linkedKpiId)
          : [...currentLinkedKpiIds, linkedKpiId];

        return {
          ...node,
          data: {
            ...node.data,
            pollConfig: {
              ...currentPoll,
              linkedKpiIds: nextLinkedKpiIds,
            },
          },
        };
      });
    },
    [updateWorkspaceNode]
  );

  const onPublishPoll = React.useCallback(
    (nodeId: string) => {
      setWorkspace((previous) => {
        if (!previous?.allNodes) return previous;
        const node = previous.allNodes.find((currentNode) => currentNode.id === nodeId);
        if (!node) return previous;

        const publishContext = getPollPublishContext(node.data.scope, defaultCampus);
        if (!publishContext.canPublish) {
          return previous;
        }

        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const campaignKpiIds = getPollCampaignKpiIds(nodeId, currentPoll);
        const selectedGroups =
          currentPoll.targetGroups.length > 0
            ? currentPoll.targetGroups
            : currentPoll.targetGroup
              ? [currentPoll.targetGroup]
              : [];
        const matchedRecipients = (workspace?.pollRecipients ?? []).filter((recipient) =>
          recipient.groups.some((group) => selectedGroups.includes(group))
        );
        const existingInvites = currentPoll.invites ?? [];
        const recipientInvites = matchedRecipients.map((recipient) => {
          const existingInvite = existingInvites.find(
            (invite) => invite.recipientId === recipient.id || invite.recipientEmail === recipient.email
          );

          return {
            id: existingInvite?.id ?? createId(),
            token: existingInvite?.token ?? createToken(),
            recipientId: recipient.id,
            recipientName: getPollRecipientLabel(recipient),
            recipientEmail: recipient.email,
            sentAt: existingInvite?.sentAt ?? new Date().toISOString(),
            completedAt: existingInvite?.completedAt,
          };
        });
        const generalInvite =
          matchedRecipients.length === 0 && selectedGroups.length > 0
            ? (() => {
                const existingInvite = existingInvites.find(
                  (invite) => invite.recipientId === "__general__"
                );

                return {
                  id: existingInvite?.id ?? createId(),
                  token: existingInvite?.token ?? createToken(),
                  recipientId: "__general__",
                  recipientName: `General share link for ${selectedGroups.join(", ")}`,
                  recipientEmail: "",
                  sentAt: existingInvite?.sentAt ?? new Date().toISOString(),
                  completedAt: existingInvite?.completedAt,
                };
              })()
            : null;
        const nextInvites = generalInvite ? [generalInvite] : recipientInvites;
        const publishedAt = new Date().toISOString();

        return {
          ...previous,
          allNodes: previous.allNodes.map((currentNode) => {
            if (!campaignKpiIds.includes(currentNode.id)) {
              return currentNode;
            }

            const existingPoll =
              currentNode.data.pollConfig ??
              createPollConfig({
                label: currentNode.data.label,
                definition: currentNode.data.definition,
                measurement: currentNode.data.measurement,
              });

            return {
              ...currentNode,
              data: {
                ...currentNode.data,
                unit: "",
                dataSource: "Poll",
                valueType: "rating_1_10",
                pollConfig: {
                  ...existingPoll,
                  campaignRootKpiId: nodeId,
                  linkedKpiIds: campaignKpiIds.filter((campaignKpiId) => campaignKpiId !== currentNode.id),
                  title: currentPoll.title,
                  description: currentPoll.description,
                  targetGroup: undefined,
                  targetGroups: selectedGroups,
                  isAnonymous: currentPoll.isAnonymous,
                  status: "published",
                  publishedAt,
                  invites: nextInvites,
                  questionPrompt:
                    currentNode.id === nodeId
                      ? currentPoll.questionPrompt
                      : buildPollQuestionPrompt({
                          label: currentNode.data.label,
                          definition: currentNode.data.definition,
                          measurement: currentNode.data.measurement,
                        }),
                },
              },
            };
          }),
        };
      });
      setChangeVersion((current) => current + 1);
      setSaveMessage(null);
    },
    [workspace?.pollRecipients]
  );

  const onRerunPoll = React.useCallback(
    (nodeId: string) => {
      const confirmed = window.confirm(
        "Re-run this poll? This will clear prior responses and generate fresh invite links for the next round."
      );
      if (!confirmed) {
        return;
      }

      setWorkspace((previous) => {
        if (!previous?.allNodes) return previous;
        const node = previous.allNodes.find((currentNode) => currentNode.id === nodeId);
        if (!node) return previous;

        const publishContext = getPollPublishContext(node.data.scope, defaultCampus);
        if (!publishContext.canPublish) {
          return previous;
        }

        const currentPoll = node.data.pollConfig ?? createPollConfig();
        const campaignKpiIds = getPollCampaignKpiIds(nodeId, currentPoll);
        const selectedGroups =
          currentPoll.targetGroups.length > 0
            ? currentPoll.targetGroups
            : currentPoll.targetGroup
              ? [currentPoll.targetGroup]
              : [];
        const matchedRecipients = (previous.pollRecipients ?? []).filter((recipient) =>
          recipient.groups.some((group) => selectedGroups.includes(group))
        );
        const nextInvites =
          matchedRecipients.length === 0 && selectedGroups.length > 0
            ? [
                {
                  id: createId(),
                  token: createToken(),
                  recipientId: "__general__",
                  recipientName: `General share link for ${selectedGroups.join(", ")}`,
                  recipientEmail: "",
                  sentAt: new Date().toISOString(),
                  completedAt: undefined,
                },
              ]
            : matchedRecipients.map((recipient) => ({
                id: createId(),
                token: createToken(),
                recipientId: recipient.id,
                recipientName: getPollRecipientLabel(recipient),
                recipientEmail: recipient.email,
                sentAt: new Date().toISOString(),
                completedAt: undefined,
              }));
        const publishedAt = new Date().toISOString();

        return {
          ...previous,
          allNodes: previous.allNodes.map((currentNode) => {
            if (!campaignKpiIds.includes(currentNode.id)) {
              return currentNode;
            }

            const existingPoll =
              currentNode.data.pollConfig ??
              createPollConfig({
                label: currentNode.data.label,
                definition: currentNode.data.definition,
                measurement: currentNode.data.measurement,
              });

            return {
              ...currentNode,
              data: {
                ...currentNode.data,
                unit: "",
                dataSource: "Poll",
                valueType: "rating_1_10",
                actualHistory: {},
                pollConfig: {
                  ...existingPoll,
                  campaignRootKpiId: nodeId,
                  linkedKpiIds: campaignKpiIds.filter((campaignKpiId) => campaignKpiId !== currentNode.id),
                  title: currentPoll.title,
                  description: currentPoll.description,
                  targetGroup: undefined,
                  targetGroups: selectedGroups,
                  isAnonymous: currentPoll.isAnonymous,
                  status: "published",
                  publishedAt,
                  responses: [],
                  invites: nextInvites,
                  questionPrompt:
                    currentNode.id === nodeId
                      ? currentPoll.questionPrompt
                      : buildPollQuestionPrompt({
                          label: currentNode.data.label,
                          definition: currentNode.data.definition,
                          measurement: currentNode.data.measurement,
                        }),
                },
              },
            };
          }),
        };
      });
      setChangeVersion((current) => current + 1);
      setSaveMessage(null);
    },
    []
  );

  const onTaskChange = React.useCallback(
    (nodeId: string, taskId: string, field: keyof Omit<TaskItem, "id">, value: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: (node.data.tasks ?? []).map((task) =>
            task.id === taskId ? { ...task, [field]: value } : task
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onAddTask = React.useCallback(
    (nodeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: [...(node.data.tasks ?? []), createTask()],
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onDeleteTask = React.useCallback(
    (nodeId: string, taskId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          tasks: (node.data.tasks ?? []).filter((task) => task.id !== taskId),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onInitiativeChange = React.useCallback(
    (
      nodeId: string,
      initiativeId: string,
      field: keyof Omit<InitiativeItem, "id" | "tasks">,
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id === initiativeId ? { ...initiative, [field]: value } : initiative
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onAddInitiative = React.useCallback(
    (nodeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: [...(node.data.initiatives ?? []), createInitiative()],
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onDeleteInitiative = React.useCallback(
    (nodeId: string, initiativeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).filter(
            (initiative) => initiative.id !== initiativeId
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onInitiativeTaskChange = React.useCallback(
    (
      nodeId: string,
      initiativeId: string,
      taskId: string,
      field: keyof Omit<TaskItem, "id">,
      value: string
    ) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id !== initiativeId
              ? initiative
              : {
                  ...initiative,
                  tasks: initiative.tasks.map((task) =>
                    task.id === taskId ? { ...task, [field]: value } : task
                  ),
                }
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onAddInitiativeTask = React.useCallback(
    (nodeId: string, initiativeId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id !== initiativeId
              ? initiative
              : {
                  ...initiative,
                  tasks: [...initiative.tasks, createTask()],
                }
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onDeleteInitiativeTask = React.useCallback(
    (nodeId: string, initiativeId: string, taskId: string) => {
      updateWorkspaceNode(nodeId, (node) => ({
        ...node,
        data: {
          ...node.data,
          initiatives: (node.data.initiatives ?? []).map((initiative) =>
            initiative.id !== initiativeId
              ? initiative
              : {
                  ...initiative,
                  tasks: initiative.tasks.filter((task) => task.id !== taskId),
                }
          ),
        },
      }));
    },
    [updateWorkspaceNode]
  );

  const onWorkItemStatusChange = React.useCallback(
    (item: ReportWorkItem, nextStatus: WorkStatus) => {
      if (nextStatus === "done" && item.status !== "done") {
        playTaskDoneSound();
      }

      if (item.kind === "kpi-task" && item.taskId) {
        onTaskChange(item.kpiId, item.taskId, "status", nextStatus);
        return;
      }

      if (item.kind === "initiative" && item.initiativeId) {
        onInitiativeChange(item.kpiId, item.initiativeId, "status", nextStatus);
        return;
      }

      if (item.kind === "initiative-task" && item.initiativeId && item.taskId) {
        onInitiativeTaskChange(item.kpiId, item.initiativeId, item.taskId, "status", nextStatus);
      }
    },
    [onInitiativeChange, onInitiativeTaskChange, onTaskChange]
  );

  React.useEffect(
    () =>
      subscribeToWorkspaceChanged(selectedEnterpriseId, () =>
        void loadWorkspace({ silent: true, preserveSelection: true })
      ),
    [loadWorkspace, selectedEnterpriseId]
  );

  React.useEffect(() => {
    if (selectedEnterpriseId === null) return;

    const refreshWhenVisible = () => {
      if (document.visibilityState !== "visible") return;
      void loadWorkspace({ silent: true, preserveSelection: true });
    };

    window.addEventListener("focus", refreshWhenVisible);
    document.addEventListener("visibilitychange", refreshWhenVisible);

    return () => {
      window.removeEventListener("focus", refreshWhenVisible);
      document.removeEventListener("visibilitychange", refreshWhenVisible);
    };
  }, [loadWorkspace, selectedEnterpriseId]);

  const saveWorkspace = React.useCallback(async (options?: { silent?: boolean }) => {
    if (selectedEnterpriseId === null || !workspace) return;

    setIsSaving(true);
    const workspaceToPersist = {
      ...workspace,
      enterpriseIdentity: getWorkspaceEnterpriseIdentity(selectedEnterpriseId, selectedEnterprise),
    };
    if (!options?.silent) {
      setSaveMessage(null);
    }

    try {
      await api.put(`/enterprises/${selectedEnterpriseId}/workspace`, {
        workspace: workspaceToPersist,
      });
      window.localStorage.setItem(
        getWorkspaceCacheKey(selectedEnterpriseId, selectedEnterprise),
        JSON.stringify(workspaceToPersist)
      );
      notifyWorkspaceChanged(selectedEnterpriseId, "workspace-save");
      setLastSavedVersion(changeVersion);
      setSaveMessage("Changes saved.");
    } catch (saveError) {
      console.error("Failed to save report edits", saveError);
      setSaveMessage("Could not save changes.");
    } finally {
      setIsSaving(false);
    }
  }, [changeVersion, selectedEnterprise, selectedEnterpriseId, workspace]);

  React.useEffect(() => {
    if (!workspace || selectedEnterpriseId === null) return;
    if (changeVersion === 0 || changeVersion === lastSavedVersion) return;

    const timeoutId = window.setTimeout(() => {
      void saveWorkspace({ silent: true });
    }, 800);

    return () => window.clearTimeout(timeoutId);
  }, [changeVersion, lastSavedVersion, saveWorkspace, selectedEnterpriseId, workspace]);

  React.useEffect(() => {
    const refreshFromBackend = () => {
      if (document.visibilityState === "hidden") return;
      if (changeVersion !== lastSavedVersion) return;
      if (selectedEnterpriseId === null) return;
      void loadWorkspace({ preserveSelection: true, silent: true });
    };

    window.addEventListener("focus", refreshFromBackend);
    document.addEventListener("visibilitychange", refreshFromBackend);

    return () => {
      window.removeEventListener("focus", refreshFromBackend);
      document.removeEventListener("visibilitychange", refreshFromBackend);
    };
  }, [changeVersion, lastSavedVersion, loadWorkspace, selectedEnterpriseId]);

  const selectedFilteredKpi = React.useMemo(() => {
    if (selectedKpiId === null) return null;
    return statusFilteredNodes.find((node) => node.id === selectedKpiId) ?? null;
  }, [selectedKpiId, statusFilteredNodes]);

  const selectedPersistedKpi = React.useMemo(() => {
    if (editingKpiId === null) return null;
    return workspace?.allNodes?.find((node) => node.id === editingKpiId) ?? null;
  }, [editingKpiId, workspace?.allNodes]);

  const selectedPersistedParentLabel = React.useMemo(() => {
    if (!selectedPersistedKpi) return null;
    const parentId = kpiRelationships.parentByChild.get(selectedPersistedKpi.id);
    if (!parentId) return null;
    return kpiRelationships.labelsById.get(parentId) ?? "Untitled KPI";
  }, [kpiRelationships.labelsById, kpiRelationships.parentByChild, selectedPersistedKpi]);

  const selectedPersistedTarget = React.useMemo(() => {
    if (!selectedPersistedKpi) return undefined;
    const calculationMode = normalizeCalculationMode(
      selectedPersistedKpi.data.calculationMode,
      selectedPersistedKpi.data.isRollup
    );
    if (calculationMode === "calculated_values") {
      return reportNodes.find((node) => node.id === selectedPersistedKpi.id)?.target;
    }
    return getValueForContext(
      selectedPersistedKpi.data.targetHistory ?? {},
      reportReferenceDate,
      selectedPersistedKpi.data.frequency ?? "weekly",
      "global",
      defaultCampus,
      selectedPersistedKpi.data.valueType ?? "count",
      reportAggregationMode,
      selectedPersistedKpi.data.metricPersistence ?? "period_only"
    );
  }, [reportAggregationMode, reportNodes, reportReferenceDate, selectedPersistedKpi]);

  const selectedPersistedActual = React.useMemo(() => {
    if (!selectedPersistedKpi) return undefined;
    const calculationMode = normalizeCalculationMode(
      selectedPersistedKpi.data.calculationMode,
      selectedPersistedKpi.data.isRollup
    );
    if (calculationMode === "calculated_values" || calculationMode === "formula_percentage") {
      return reportNodes.find((node) => node.id === selectedPersistedKpi.id)?.actual;
    }
    return getValueForContext(
      selectedPersistedKpi.data.actualHistory ?? {},
      reportReferenceDate,
      selectedPersistedKpi.data.frequency ?? "weekly",
      "global",
      defaultCampus,
      selectedPersistedKpi.data.valueType ?? "count",
      reportAggregationMode,
      selectedPersistedKpi.data.metricPersistence ?? "period_only"
    );
  }, [reportAggregationMode, reportNodes, reportReferenceDate, selectedPersistedKpi]);

  const pollEditorKpi = React.useMemo(() => {
    if (pollEditorKpiId === null) return null;
    return workspace?.allNodes?.find((node) => node.id === pollEditorKpiId) ?? null;
  }, [pollEditorKpiId, workspace?.allNodes]);

  const pollEditorResults = React.useMemo(() => {
    const pollConfig = pollEditorKpi?.data.pollConfig;
    if (!pollConfig) return null;

    const responses = [...pollConfig.responses].sort((left, right) => {
      const leftTime = left.submittedAt ? new Date(left.submittedAt).getTime() : 0;
      const rightTime = right.submittedAt ? new Date(right.submittedAt).getTime() : 0;
      return rightTime - leftTime;
    });

    const numericValues = responses
      .map((response) =>
        typeof response.value === "number" ? response.value : Number(response.value)
      )
      .filter((value) => !Number.isNaN(value));
    const average =
      numericValues.length > 0
        ? numericValues.reduce((sum, value) => sum + value, 0) / numericValues.length
        : null;
    const commentTexts = responses
      .map((response) => response.comment ?? "")
      .filter((comment) => comment.trim().length > 0);
    const commentCount = commentTexts.length;
    const commentSummary = summarizePollComments(commentTexts);

    return {
      responseCount: responses.length,
      commentCount,
      completedInviteCount: (pollConfig.invites ?? []).filter((invite) => Boolean(invite.completedAt))
        .length,
      pendingInviteCount: (pollConfig.invites ?? []).filter((invite) => !invite.completedAt).length,
      respondentLabels: Array.from(
        new Set(
          responses
            .map((response) => response.respondentLabel?.trim())
            .filter((label): label is string => Boolean(label))
        )
      ),
      headline: average === null ? "No average yet" : `Average ${average.toFixed(1)}`,
      detail:
        responses.length > 0
          ? `${numericValues.length} ratings submitted${commentCount > 0 ? `, ${commentCount} comments` : ""}`
          : "No responses yet",
      commentSummaryShort: commentSummary?.shortSummary ?? null,
      commentSummaryLong: commentSummary?.longSummary ?? null,
      responses,
    };
  }, [pollEditorKpi]);

  React.useEffect(() => {
    setIsPollSummaryExpanded(false);
    setPollCommentsCopyMessage(null);
  }, [pollEditorKpiId, pollEditorResults?.responseCount]);

  const pollEditorRecipients = React.useMemo(() => {
    const pollConfig = pollEditorKpi?.data.pollConfig;
    if (!pollConfig) return [];

    const selectedGroups =
      pollConfig.targetGroups.length > 0
        ? pollConfig.targetGroups
        : pollConfig.targetGroup
          ? [pollConfig.targetGroup]
          : [];

    if (selectedGroups.length === 0) return [];

    const seenEmails = new Set<string>();
    return (workspace?.pollRecipients ?? []).filter((recipient) => {
      const normalizedEmail = recipient.email.trim().toLowerCase();
      const inGroup = recipient.groups.some((group) => selectedGroups.includes(group));
      if (!inGroup || normalizedEmail.length === 0 || seenEmails.has(normalizedEmail)) {
        return false;
      }
      seenEmails.add(normalizedEmail);
      return true;
    });
  }, [pollEditorKpi, workspace?.pollRecipients]);

  const pollEditorLinkedKpiOptions = React.useMemo(() => {
    if (!pollEditorKpi) return [];

    return (workspace?.allNodes ?? [])
      .filter((node) => node.id !== pollEditorKpi.id)
      .map((node) => ({
        id: node.id,
        label: node.data.label || "Untitled KPI",
      }))
      .sort((left, right) => left.label.localeCompare(right.label));
  }, [pollEditorKpi, workspace?.allNodes]);
  const pollCampaignSetup = React.useMemo(() => {
    if (!pollEditorKpi) return null;
    return getPollCampaignSetup(
      workspace?.allNodes ?? [],
      pollEditorKpi.id,
      pollEditorKpi.data.pollConfig,
      reportReferenceDate,
      defaultCampus
    );
  }, [pollEditorKpi, reportReferenceDate, workspace?.allNodes]);
  const pollEditorSelectedGroups = React.useMemo(() => {
    const pollConfig = pollEditorKpi?.data.pollConfig;
    if (!pollConfig) return [];

    return pollConfig.targetGroups.length > 0
      ? pollConfig.targetGroups
      : pollConfig.targetGroup
        ? [pollConfig.targetGroup]
        : [];
  }, [pollEditorKpi]);
  const pollPublishContext = React.useMemo(() => {
    if (!pollEditorKpi) return null;
    return getPollPublishContext(pollEditorKpi.data.scope, defaultCampus);
  }, [pollEditorKpi]);
  const isPollViewMode = pollEditorMode === "view";
  const isPollEditMode = pollEditorMode === "edit";

  const copyPollComments = React.useCallback(async () => {
    if (!pollEditorKpi || !pollEditorResults) return;

    const commentResponses = pollEditorResults.responses.filter(
      (response) => (response.comment ?? "").trim().length > 0
    );

    if (commentResponses.length === 0) {
      setPollCommentsCopyMessage("No comments to copy yet.");
      return;
    }

    const compiled = [
      `KPI: ${pollEditorKpi.data.label}`,
      `Poll: ${pollEditorKpi.data.pollConfig?.title ?? "Untitled Poll"}`,
      pollEditorKpi.data.pollConfig?.questionPrompt
        ? `Question: ${pollEditorKpi.data.pollConfig.questionPrompt}`
        : null,
      "",
      "Comments",
      ...commentResponses.map((response, index) => {
        const rating =
          typeof response.value === "number" || !Number.isNaN(Number(response.value))
            ? `Rating: ${Number(response.value)}/10`
            : null;
        const respondent = response.respondentLabel?.trim()
          ? response.respondentLabel.trim()
          : response.respondentGroup?.trim()
            ? response.respondentGroup.trim()
            : "Anonymous";

        return [
          `${index + 1}. ${respondent}`,
          rating,
          `Comment: ${(response.comment ?? "").trim()}`,
        ]
          .filter(Boolean)
          .join("\n");
      }),
    ]
      .filter(Boolean)
      .join("\n\n");

    try {
      await navigator.clipboard.writeText(compiled);
      setPollCommentsCopyMessage("Comments copied.");
    } catch (error) {
      console.error("Failed to copy poll comments", error);
      setPollCommentsCopyMessage("Could not copy comments.");
    }
  }, [pollEditorKpi, pollEditorResults]);

  const getPollInviteUrl = React.useCallback(
    (inviteToken: string) => {
      if (!selectedEnterpriseId) return "";
      return `${window.location.origin}/polls/respond?enterpriseId=${encodeURIComponent(
        String(selectedEnterpriseId)
      )}&inviteToken=${encodeURIComponent(inviteToken)}`;
    },
    [selectedEnterpriseId]
  );

  const emailPollInvitation = React.useCallback(
    (invite: PollInvite) => {
      if (!pollEditorKpi?.data.pollConfig) return;

      const selectedGroups =
        pollEditorKpi.data.pollConfig.targetGroups.length > 0
          ? pollEditorKpi.data.pollConfig.targetGroups
          : pollEditorKpi.data.pollConfig.targetGroup
            ? [pollEditorKpi.data.pollConfig.targetGroup]
            : [];
      const inviteUrl = getPollInviteUrl(invite.token);
      const subject = encodeURIComponent(
        `${pollEditorKpi.data.pollConfig.title} - Poll Invitation`
      );
      const body = encodeURIComponent(
        [
          `Hi ${invite.recipientName || "there"},`,
          "",
          "You are invited to complete a poll.",
          "",
          `KPI: ${pollEditorKpi.data.label || "Untitled KPI"}`,
          `Poll: ${pollEditorKpi.data.pollConfig.title}`,
          selectedGroups.length > 0 ? `Audience groups: ${selectedGroups.join(", ")}` : "",
          "",
          pollEditorKpi.data.pollConfig.description || "",
          "",
          `Question: ${pollEditorKpi.data.pollConfig.questionPrompt}`,
          "",
          "Poll link:",
          inviteUrl,
          "",
          "If the link does not auto-open, copy and paste it into your browser.",
        ]
          .filter(Boolean)
          .join("\n")
      );

      window.location.href = `mailto:${encodeURIComponent(
        invite.recipientEmail
      )}?subject=${subject}&body=${body}`;
    },
    [getPollInviteUrl, pollEditorKpi]
  );

  const openPollEmailComposer = React.useCallback(() => {
    if (!pollEditorKpi?.data.pollConfig) return;
    const pollTitle = pollEditorKpi.data.pollConfig.title || "KPI Poll";
    const question = pollEditorKpi.data.pollConfig.questionPrompt || "Please complete this poll.";
    setPollEmailSubject(`${pollTitle} | ${pollEditorKpi.data.label}`);
    setPollEmailGreeting("Hi {first_name},");
    setPollEmailBody(
      `Please take a moment to respond to this poll for ${pollEditorKpi.data.label}.\n\n${question}`
    );
    setPollEmailClosing("Thank you,\nLenses CPM");
    setPollEmailStatusMessage(null);
    setIsPollEmailComposerOpen(true);
  }, [pollEditorKpi]);

  const sendPollEmailCampaign = React.useCallback(async () => {
    if (!pollEditorKpi?.data.pollConfig) return;

    const recipients = (pollEditorKpi.data.pollConfig.invites ?? [])
      .filter((invite) => invite.recipientEmail?.trim())
      .map((invite) => {
        const name = invite.recipientName?.trim() || "";
        return {
          name,
          first_name: name.split(/\s+/).filter(Boolean)[0] ?? "",
          email: invite.recipientEmail.trim(),
          invite_url: getPollInviteUrl(invite.token),
        };
      });

    if (recipients.length === 0) {
      setPollEmailStatusMessage("No tracked email recipients are available for this poll.");
      return;
    }

    setIsSendingPollEmails(true);
    setPollEmailStatusMessage(null);

    try {
      const response = await api.post("/polls/send-email-campaign", {
        enterprise_id: selectedEnterpriseId,
        subject: pollEmailSubject,
        greeting: pollEmailGreeting,
        body: pollEmailBody,
        closing: pollEmailClosing,
        kpi_label: pollEditorKpi.data.label || "Untitled KPI",
        poll_title: pollEditorKpi.data.pollConfig.title || "KPI Poll",
        recipients,
      });
      const result = response.data as { sent_count: number; failed_count: number };
      setPollEmailStatusMessage(
        result.failed_count > 0
          ? `Sent ${result.sent_count} emails. ${result.failed_count} failed.`
          : `Sent ${result.sent_count} poll emails.`
      );
    } catch (error: unknown) {
      console.error("Failed to send poll email campaign", error);
      setPollEmailStatusMessage("Could not send poll emails.");
    } finally {
      setIsSendingPollEmails(false);
    }
  }, [
    getPollInviteUrl,
    pollEditorKpi,
    pollEmailBody,
    pollEmailClosing,
    pollEmailGreeting,
    pollEmailSubject,
    selectedEnterpriseId,
  ]);

  const openKpiEditor = React.useCallback((kpiId: string) => {
    setSelectedKpiId(kpiId);
    setEditingKpiId(kpiId);
    setIsKpiInfoOpen(true);
    setIsKpiWorkOpen(false);
    setWorkEditorFocus(null);
  }, []);

  const openWorkItemEditor = React.useCallback(
    (item: ReportWorkItem) => {
      setSelectedKpiId(item.kpiId);
      setEditingKpiId(item.kpiId);
      setIsKpiInfoOpen(true);
      setIsKpiWorkOpen(true);
      setWorkEditorFocusNonce((current) => current + 1);
      if (item.kind === "initiative" && item.initiativeId) {
        setWorkEditorFocus({ kind: "initiative", initiativeId: item.initiativeId });
        return;
      }
      if (item.kind === "kpi-task" && item.taskId) {
        setWorkEditorFocus({ kind: "kpi-task", taskId: item.taskId });
        return;
      }
      if (item.kind === "initiative-task" && item.initiativeId && item.taskId) {
        setWorkEditorFocus({
          kind: "initiative-task",
          initiativeId: item.initiativeId,
          taskId: item.taskId,
        });
        return;
      }
      setWorkEditorFocus(null);
    },
    []
  );

  const updateRequestTaskIds = React.useMemo(
    () =>
      Array.from(
        new Set(
          filteredWorkItems
            .filter(
              (item) =>
                item.kind !== "initiative" &&
                item.status !== "done" &&
                Boolean(item.taskId) &&
                Boolean(item.owner)
            )
            .map((item) => item.taskId as string)
        )
      ),
    [filteredWorkItems]
  );
  const updateRequestOwnerCount = React.useMemo(
    () =>
      new Set(
        filteredWorkItems
          .filter(
            (item) =>
              item.kind !== "initiative" &&
              item.status !== "done" &&
              Boolean(item.taskId) &&
              Boolean(item.owner)
          )
          .map((item) => item.owner)
      ).size,
    [filteredWorkItems]
  );
  const updateRequestSummary = React.useMemo(() => {
    const initiativeCount = filteredWorkItems.filter((item) => item.kind === "initiative").length;
    const taskItems = filteredWorkItems.filter((item) => item.kind !== "initiative" && Boolean(item.taskId));
    const doneTaskCount = taskItems.filter((item) => item.status === "done").length;
    const unassignedOpenTaskCount = taskItems.filter((item) => item.status !== "done" && !item.owner).length;
    return {
      initiativeCount,
      taskCount: taskItems.length,
      doneTaskCount,
      unassignedOpenTaskCount,
    };
  }, [filteredWorkItems]);

  const initiativeUpdateOptions = React.useMemo(
    () =>
      filteredWorkItems
        .filter((item) => item.kind === "initiative" && item.initiativeId)
        .map((item) => {
          const key = `${item.kpiId}:${item.initiativeId}`;
          const taskIds = Array.from(
            new Set(
              filteredWorkItems
                .filter(
                  (task) =>
                    task.kind === "initiative-task" &&
                    task.kpiId === item.kpiId &&
                    task.initiativeId === item.initiativeId &&
                    task.status !== "done" &&
                    Boolean(task.taskId) &&
                    Boolean(task.owner)
                )
                .map((task) => task.taskId as string)
            )
          );
          const ownerCount = new Set(
            filteredWorkItems
              .filter(
                (task) =>
                  task.kind === "initiative-task" &&
                  task.kpiId === item.kpiId &&
                  task.initiativeId === item.initiativeId &&
                  task.status !== "done" &&
                  Boolean(task.taskId) &&
                  Boolean(task.owner)
              )
              .map((task) => task.owner)
          ).size;
          return {
            key,
            label: `${item.title} (${item.kpiLineageLabel})`,
            taskIds,
            ownerCount,
          };
        }),
    [filteredWorkItems]
  );
  const selectedUpdateInitiative = React.useMemo(
    () =>
      initiativeUpdateOptions.find((option) => option.key === selectedUpdateInitiativeKey) ??
      null,
    [initiativeUpdateOptions, selectedUpdateInitiativeKey]
  );
  const selectedUpdateTaskIds = selectedUpdateInitiative?.taskIds ?? updateRequestTaskIds;
  const selectedUpdateOwnerCount = selectedUpdateInitiative?.ownerCount ?? updateRequestOwnerCount;

  const sendTaskUpdateRequests = React.useCallback(async (taskIds = updateRequestTaskIds) => {
    if (selectedEnterpriseId === null) {
      setTaskUpdateEmailStatusMessage("Select an enterprise before sending update requests.");
      return;
    }

    if (taskIds.length === 0) {
      setTaskUpdateEmailStatusMessage("No assigned open task owners match the current initiative filters.");
      return;
    }

    setIsSendingTaskUpdateEmails(true);
    setTaskUpdateEmailStatusMessage(null);
    try {
      const response = await api.post(
        `/enterprises/${selectedEnterpriseId}/tasks/send-update-requests`,
        {
          task_ids: taskIds,
          dry_run: false,
        }
      );
      const result = response.data as TaskEmailResult;
      const skippedText = result.skipped_count > 0 ? ` ${result.skipped_count} skipped because no matching email was found.` : "";
      const failedText = result.failed_count > 0 ? ` ${result.failed_count} failed.` : "";
      const errorText = result.errors?.length ? ` ${result.errors.slice(0, 2).join(" ")}` : "";
      setTaskUpdateEmailStatusMessage(
        `Sent ${result.sent_count} update request email${result.sent_count === 1 ? "" : "s"}.${skippedText}${failedText}${errorText}`
      );
    } catch (error) {
      console.error("Failed to send task update requests", error);
      setTaskUpdateEmailStatusMessage(
        getApiErrorDetail(error, "Could not send task update requests.")
      );
    } finally {
      setIsSendingTaskUpdateEmails(false);
    }
  }, [selectedEnterpriseId, updateRequestTaskIds]);

  React.useEffect(() => {
    if (!editingKpiId || workEditorFocus) return;

    const timeoutId = window.setTimeout(() => {
      ownerInputRef.current?.focus();
    }, 150);

    return () => window.clearTimeout(timeoutId);
  }, [editingKpiId, workEditorFocus]);

  React.useEffect(() => {
    if (!editingKpiId || !workEditorFocus || !isKpiWorkOpen) return;

    const timeoutId = window.setTimeout(() => {
      const node = workEditorItemRefs.current[getWorkEditorFocusKey(workEditorFocus)];
      node?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, 260);

    return () => window.clearTimeout(timeoutId);
  }, [editingKpiId, isKpiWorkOpen, workEditorFocus, workEditorFocusNonce]);

  React.useEffect(() => {
    if (editingKpiId !== null) return;
    setWorkEditorFocus(null);
  }, [editingKpiId]);

  const reportChromeButton: React.CSSProperties = {
    boxSizing: "border-box",
    height: 34,
    border: "1px solid rgba(148,163,184,0.26)",
    borderRadius: 8,
    padding: "0 12px",
    color: "#cbd5e1",
    background: "rgba(15,23,42,0.72)",
    fontSize: 13,
    fontWeight: 800,
    textDecoration: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    whiteSpace: "nowrap",
    cursor: "pointer",
  };
  const activeReportChromeButton: React.CSSProperties = {
    ...reportChromeButton,
    borderColor: "rgba(34,197,94,0.55)",
    color: "#f8fafc",
    background: "rgba(34,197,94,0.18)",
    boxShadow: "0 0 22px rgba(34,197,94,0.18), inset 0 1px 0 rgba(255,255,255,0.05)",
  };
  const reportAccountPill = signedInUser ? (
    <div
      aria-label="Signed in account"
      style={{
        minHeight: 34,
        display: "flex",
        alignItems: "center",
        gap: 8,
        maxWidth: 300,
        minWidth: 0,
        border: "1px solid rgba(148,163,184,0.26)",
        borderRadius: 10,
        padding: "5px 6px 5px 10px",
        background: "rgba(8,13,20,0.82)",
        color: "#f8fafc",
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 12,
            fontWeight: 900,
            lineHeight: 1.1,
          }}
        >
          {signedInUser.name || "Signed in"}
        </div>
        <div
          style={{
            maxWidth: 150,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontSize: 10,
            color: "#9ca3af",
            lineHeight: 1.1,
          }}
        >
          {signedInUser.email || signedInUser.role || ""}
        </div>
      </div>
      {onSignOut ? (
        <button
          type="button"
          onClick={onSignOut}
          style={{
            border: "1px solid rgba(148,163,184,0.38)",
            borderRadius: 8,
            background: "rgba(15,23,42,0.82)",
            color: "#e5e7eb",
            padding: "6px 8px",
            fontSize: 12,
            fontWeight: 900,
            whiteSpace: "nowrap",
            cursor: "pointer",
          }}
        >
          Sign out
        </button>
      ) : null}
    </div>
  ) : null;

  return (
    <div
      className="map-designed-page summary-view"
      style={{
        minHeight: "100vh",
        background:
          "radial-gradient(circle at top, rgba(255,255,255,0.06) 0, transparent 1px), #05070b",
        backgroundSize: "22px 22px",
        color: "#f8fafc",
        padding: "16px 24px 56px",
      }}
    >
      <div style={{ maxWidth: 1560, margin: "0 auto", display: "grid", gap: 24 }}>
        <div
          style={{
            display: "grid",
            gap: 14,
            alignItems: "flex-start",
          }}
        >
          <div
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              display: "grid",
              gap: 12,
              background:
                "linear-gradient(180deg, rgba(15,23,42,0.96) 0%, rgba(8,13,20,0.94) 100%)",
              border: "1px solid rgba(148,163,184,0.24)",
              borderRadius: 24,
              padding: 16,
              boxShadow:
                "0 18px 44px rgba(0,0,0,0.36), 0 0 46px rgba(34,197,94,0.08), inset 0 1px 0 rgba(255,255,255,0.06)",
              backdropFilter: "blur(10px)",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "2px 4px 2px 0",
                flex: "0 0 auto",
                justifyContent: "space-between",
                flexWrap: "wrap",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <img
                  src={brandLogoUrl}
                  alt="Lenses logo"
                  style={{
                    width: 60,
                    height: 60,
                    objectFit: "contain",
                    clipPath: "inset(3px 0 0 0)",
                  }}
                />
                <div style={{ minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      color: "#22c55e",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      lineHeight: 1.1,
                    }}
                  >
                    Lenses
                  </div>
                  <div
                    style={{
                      fontSize: 16,
                      fontWeight: 800,
                      color: "#f8fafc",
                      lineHeight: 1.15,
                      whiteSpace: "nowrap",
                    }}
                  >
                    Summary View
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button
                  type="button"
                  onClick={() => setIsSettingsLibrariesOpen((current) => !current)}
                  style={{
                    ...reportChromeButton,
                    borderColor: isSettingsLibrariesOpen ? "rgba(96,165,250,0.52)" : "rgba(148,163,184,0.26)",
                    color: isSettingsLibrariesOpen ? "#bfdbfe" : "#cbd5e1",
                    background: isSettingsLibrariesOpen ? "rgba(37,99,235,0.18)" : "rgba(15,23,42,0.72)",
                  }}
                >
                  Settings
                </button>
                {reportAccountPill}
                <div style={{ display: "grid", gap: 4 }}>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 800,
                      color: "#9ca3af",
                      textTransform: "uppercase",
                      textAlign: "center",
                    }}
                  >
                    Views
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 3,
                      padding: 3,
                      borderRadius: 10,
                      border: "1px solid rgba(148,163,184,0.26)",
                      background: "rgba(8,13,20,0.82)",
                    }}
                    aria-label="Workspace views"
                  >
                    <Link to="/" style={{ ...reportChromeButton, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}>
                      Map
                    </Link>
                    <span style={activeReportChromeButton}>Summary</span>
                    <Link
                      to={selectedEnterpriseId ? `/dashboard?enterpriseId=${selectedEnterpriseId}` : "/dashboard"}
                      style={{ ...reportChromeButton, borderColor: "transparent", background: "transparent", color: "#e5e7eb" }}
                    >
                      Dashboard
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div
              style={{
                display: "flex",
                gap: 12,
                flexWrap: "wrap",
                alignItems: "flex-start",
                justifyContent: "flex-start",
              }}
            >
              <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Enterprise
                </div>
                <select
                  value={selectedEnterpriseId ?? ""}
                  onChange={(event) => {
                    const nextEnterpriseId = Number(event.target.value);
                    setSelectedEnterpriseId(nextEnterpriseId);
                    setSearchParams({ enterpriseId: String(nextEnterpriseId) });
                  }}
                  style={{
                    border: "1px solid rgba(148,163,184,0.28)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 14,
                    background: "rgba(8,13,20,0.92)",
                    color: "#f8fafc",
                    minWidth: 180,
                    maxWidth: 220,
                  }}
                >
                  {enterprises.map((enterprise) => (
                    <option key={enterprise.id} value={enterprise.id}>
                      {enterprise.name}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Lens
                </div>
                <select
                  value={selectedLens}
                  onChange={(event) => setSelectedLens(event.target.value)}
                  style={{
                    border: "1px solid rgba(148,163,184,0.28)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 14,
                    background: "rgba(8,13,20,0.92)",
                    color: "#f8fafc",
                    minWidth: 160,
                    maxWidth: 220,
                  }}
                >
                  <option value="all">All lenses</option>
                  {workspaceLenses.map((lens) => (
                    <option key={lens} value={lens}>
                      {workspaceLensConfigs[lens]?.name ?? lens}
                    </option>
                  ))}
                </select>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 4,
                  justifyItems: "center",
                }}
              >
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Date
                </div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "40px 1fr 40px",
                    gap: 8,
                    alignItems: "center",
                    minWidth: 210,
                  }}
                >
                  <button
                    type="button"
                    onClick={() =>
                      setReportReferenceDate((current) => shiftReferenceDate(current, "previous"))
                    }
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid rgba(148,163,184,0.28)",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 14,
                      background: "rgba(8,13,20,0.92)",
                      color: "#f8fafc",
                      cursor: "pointer",
                    }}
                  >
                    {"<"}
                  </button>
                  <div
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid rgba(148,163,184,0.28)",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 13,
                      textAlign: "center",
                      fontWeight: 700,
                      color: "#f8fafc",
                      background: "rgba(15,23,42,0.86)",
                      lineHeight: 1.2,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {`Wk ${getReportingWeekParts(reportReferenceDate, weekStartsOn).week} | ${formatReferenceDate(
                      reportReferenceDate
                    )}`}
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setReportReferenceDate((current) => shiftReferenceDate(current, "next"))
                    }
                    style={{
                      width: "100%",
                      boxSizing: "border-box",
                      border: "1px solid rgba(148,163,184,0.28)",
                      borderRadius: 8,
                      padding: "8px 10px",
                      fontSize: 14,
                      background: "rgba(8,13,20,0.92)",
                      color: "#f8fafc",
                      cursor: "pointer",
                    }}
                  >
                    {">"}
                  </button>
                </div>
              </div>

              <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Owner
                </div>
                <select
                  value={selectedOwner}
                  onChange={(event) => setSelectedOwner(event.target.value)}
                  style={{
                    border: "1px solid rgba(148,163,184,0.28)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 14,
                    background: "rgba(8,13,20,0.92)",
                    color: "#f8fafc",
                    minWidth: 160,
                    maxWidth: 220,
                  }}
                >
                  <option value="all">All owners</option>
                  {ownerOptions.map((owner) => (
                    <option key={owner} value={owner}>
                      {owner}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Aggregation
                </div>
                <select
                  value={reportAggregationMode}
                  onChange={(event) =>
                    setReportAggregationMode(event.target.value as AggregationMode)
                  }
                  style={{
                    border: "1px solid rgba(148,163,184,0.28)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 13,
                    background: "rgba(8,13,20,0.92)",
                    color: "#f8fafc",
                    minWidth: 130,
                  }}
                >
                  {aggregationModeOptions.map((option) => (
                    <option key={option} value={option}>
                      {formatAggregationLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
              <div style={{ display: "grid", gap: 4, justifyItems: "center" }}>
                <div
                  style={{
                    fontSize: 11,
                    fontWeight: 800,
                    color: "#9ca3af",
                    textTransform: "uppercase",
                    letterSpacing: "0.04em",
                    textAlign: "center",
                  }}
                >
                  Comparison
                </div>
                <select
                  value={reportComparisonMode}
                  onChange={(event) =>
                    setReportComparisonMode(event.target.value as ComparisonMode)
                  }
                  style={{
                    border: "1px solid rgba(148,163,184,0.28)",
                    borderRadius: 8,
                    padding: "8px 10px",
                    fontSize: 13,
                    background: "rgba(8,13,20,0.92)",
                    color: "#f8fafc",
                    minWidth: 150,
                  }}
                >
                  {comparisonModeOptions.map((option) => (
                    <option key={option} value={option}>
                      {formatComparisonLabel(option)}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

        </div>

        {isSettingsLibrariesOpen && (
          <div
            onClick={() => setIsSettingsLibrariesOpen(false)}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(17, 24, 39, 0.52)",
              display: "grid",
              placeItems: "center",
              padding: 24,
              zIndex: 1000,
            }}
          >
            <div
              className="settings-library-surface"
              onClick={(event) => event.stopPropagation()}
              style={{
                width: "min(920px, 100%)",
                maxHeight: "min(82vh, 780px)",
                overflow: "auto",
                background: "#fff",
                borderRadius: 18,
                border: "1px solid #d1d5db",
                boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
                padding: 22,
                display: "grid",
                gap: 16,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: 16,
                  position: "sticky",
                  top: -22,
                  margin: "-22px -22px 0",
                  padding: "22px 22px 16px",
                  background: "rgba(255,255,255,0.96)",
                  borderBottom: "1px solid #e5e7eb",
                  zIndex: 2,
                  backdropFilter: "blur(6px)",
                }}
              >
                <div>
                  <div style={{ fontSize: 20, fontWeight: 800, color: "#111827" }}>
                    Settings & Libraries
                  </div>
                  <div style={{ marginTop: 4, fontSize: 13, color: "#4b5563" }}>
                    Access workspace libraries and summary filters for this enterprise.
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setIsSettingsLibrariesOpen(false)}
                  style={{
                    border: "1px solid #d1d5db",
                    borderRadius: 10,
                    padding: "8px 12px",
                    background: "#fff",
                    color: "#111827",
                    fontWeight: 700,
                    cursor: "pointer",
                  }}
                >
                  Close
                </button>
              </div>

              <div style={{ fontSize: 13, color: "#4b5563", lineHeight: 1.5 }}>
                Open only the areas you want to work in.
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <button
                  type="button"
                  onClick={() => setIsSettingsLensesCollapsed((previous) => !previous)}
                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 14px", fontSize: 14, fontWeight: 800, background: "#f9fafb", color: "#111827", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span>Lenses</span>
                  <span>{isSettingsLensesCollapsed ? "Show" : "Hide"}</span>
                </button>
                {isSettingsLensesCollapsed ? null : (
                  <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
                    <Link to={selectedEnterpriseId ? `/?enterpriseId=${selectedEnterpriseId}` : "/"} style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: "10px 12px", background: "#111827", color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none", textAlign: "center" }}>
                      Manage Lenses
                    </Link>
                    {workspaceLenses.map((lens) => (
                      <div key={lens} style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: selectedLens === lens ? "#f3f4f6" : "#fff" }}>
                        <div style={{ fontSize: 15, fontWeight: 700, color: "#111827" }}>{workspaceLensConfigs[lens]?.name ?? lens}</div>
                        <div style={{ marginTop: 4, fontSize: 12, color: "#6b7280" }}>
                          {(workspace?.allNodes ?? []).filter((node) => node.data.lenses?.includes(lens)).length} KPI
                          {(workspace?.allNodes ?? []).filter((node) => node.data.lenses?.includes(lens)).length === 1 ? "" : "s"} in this lens
                        </div>
                        <button type="button" onClick={() => { setSelectedLens(lens); setIsSettingsLibrariesOpen(false); }} style={{ marginTop: 8, border: "1px solid #d1d5db", borderRadius: 10, padding: "7px 9px", background: selectedLens === lens ? "#111827" : "#fff", color: selectedLens === lens ? "#fff" : "#111827", fontSize: 12, fontWeight: 700, cursor: "pointer" }}>
                          {selectedLens === lens ? "Current Lens" : "View Lens"}
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setIsSettingsKpisCollapsed((previous) => !previous)}
                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 14px", fontSize: 14, fontWeight: 800, background: "#f9fafb", color: "#111827", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span>KPIs</span>
                  <span>{isSettingsKpisCollapsed ? "Show" : "Hide"}</span>
                </button>
                {isSettingsKpisCollapsed ? null : (
                  <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
                    <Link to={selectedEnterpriseId ? `/?enterpriseId=${selectedEnterpriseId}` : "/"} style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: "10px 12px", background: "#111827", color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none", textAlign: "center" }}>
                      Manage KPI Library
                    </Link>
                    <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff", display: "grid", gap: 6 }}>
                      <div style={{ fontSize: 13, color: "#111827" }}>Visible in this summary: {filteredNodes.length}</div>
                      <div style={{ fontSize: 13, color: "#111827" }}>Selected lens filter: {selectedLens === "all" ? "All lenses" : selectedLens}</div>
                      <div style={{ fontSize: 13, color: "#111827" }}>Selected owner filter: {selectedOwner === "all" ? "All owners" : selectedOwner}</div>
                    </div>
                    {selectedKpiId ? (
                      <button type="button" onClick={() => { openKpiEditor(selectedKpiId); setIsSettingsLibrariesOpen(false); }} style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: "10px 12px", background: "#111827", color: "#fff", fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
                        Edit Selected KPI
                      </button>
                    ) : null}
                  </div>
                )}

                <button
                  type="button"
                  onClick={() => setIsSettingsReferenceListsCollapsed((previous) => !previous)}
                  style={{ width: "100%", border: "1px solid #d1d5db", borderRadius: 12, padding: "12px 14px", fontSize: 14, fontWeight: 800, background: "#f9fafb", color: "#111827", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between" }}
                >
                  <span>Reference Lists</span>
                  <span>{isSettingsReferenceListsCollapsed ? "Show" : "Hide"}</span>
                </button>
                {isSettingsReferenceListsCollapsed ? null : (
                  <div style={{ marginLeft: 16, display: "grid", gap: 10 }}>
                    <Link to={selectedEnterpriseId ? `/?enterpriseId=${selectedEnterpriseId}` : "/"} style={{ border: "1px solid #d1d5db", borderRadius: 12, padding: "10px 12px", background: "#111827", color: "#fff", fontSize: 13, fontWeight: 700, textDecoration: "none", textAlign: "center" }}>
                      Manage Reference Lists
                    </Link>
                    <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Owners</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{ownerOptions.length > 0 ? ownerOptions.join(", ") : "No owners listed."}</div>
                    </div>
                    <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Poll Audience Groups</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{audienceGroupOptions.length > 0 ? audienceGroupOptions.join(", ") : "No audience groups listed."}</div>
                    </div>
                    <div style={{ border: "1px solid #d1d5db", borderRadius: 14, padding: 12, background: "#fff" }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#111827" }}>Poll Recipients</div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>{(workspace?.pollRecipients ?? []).length} recipients in this enterprise</div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {error ? (
          <div
            style={{
              border: "1px solid #fecaca",
              background: "#fef2f2",
              color: "#991b1b",
              borderRadius: 18,
              padding: 18,
              fontWeight: 700,
            }}
          >
            {error}
          </div>
        ) : null}

        {isLoading ? (
          <div
            style={{
              borderRadius: 18,
              padding: 32,
              background: "#fff",
              border: "1px solid #e5e7eb",
              fontSize: 16,
              fontWeight: 700,
            }}
          >
            Building report...
          </div>
        ) : null}

        {!isLoading && !error && filteredNodes.length === 0 ? (
          <div
            style={{
              borderRadius: 18,
              padding: 32,
              background: "#fff",
              border: "1px solid #e5e7eb",
              fontSize: 16,
              color: "#4b5563",
            }}
          >
            No KPI data is available in this workspace for the selected lens.
          </div>
        ) : null}

        {!isLoading && !error && filteredNodes.length > 0 ? (
          <>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                gap: 14,
              }}
            >
              {(
                [
                  ["all", "All KPIs", filteredNodes.length, "#111827"],
                  ["green", "Green", statusCounts.green + statusCounts.green_strong, "#166534"],
                  ["yellow", "Yellow", statusCounts.yellow, "#a16207"],
                  ["red", "Red", statusCounts.red, "#b91c1c"],
                  ["neutral", "Neutral", statusCounts.neutral, "#6b7280"],
                ] as const
              ).map(([filterValue, label, value, color]) => (
                <button
                  type="button"
                  onClick={() => setSelectedStatusFilter(filterValue)}
                  key={label}
                  style={{
                    borderRadius: 20,
                    padding: 18,
                    background:
                      selectedStatusFilter === filterValue
                        ? `linear-gradient(180deg, ${hexToRgba(color, 0.28)}, rgba(8,13,20,0.94))`
                        : "linear-gradient(180deg, rgba(17,24,39,0.96), rgba(8,13,20,0.94))",
                    border:
                      selectedStatusFilter === filterValue
                        ? `2px solid ${color}`
                        : "1px solid rgba(148,163,184,0.22)",
                    textAlign: "left",
                    cursor: "pointer",
                    boxShadow: selectedStatusFilter === filterValue
                      ? `0 0 0 4px ${hexToRgba(color, 0.16)}, 0 14px 26px rgba(0,0,0,0.24)`
                      : "0 10px 24px rgba(0,0,0,0.2)",
                  }}
                >
                  <div
                    style={{
                      fontSize: 12,
                      fontWeight: 800,
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                      color: "#94a3b8",
                      marginBottom: 10,
                    }}
                  >
                    {label}
                  </div>
                  <div style={{ fontSize: 34, fontWeight: 800, color: color === "#111827" ? "#f8fafc" : color }}>{value}</div>
                </button>
              ))}
            </div>

            <div
              style={{
                display: "inline-flex",
                width: "fit-content",
                border: "1px solid rgba(148,163,184,0.26)",
                borderRadius: 999,
                padding: 4,
                background: "rgba(8,13,20,0.82)",
                gap: 4,
              }}
            >
              {(
                [
                  ["kpis", "View by KPIs"],
                  ["initiatives", "View by Initiatives"],
                ] as const
              ).map(([mode, label]) => (
                <button
                  key={mode}
                  type="button"
                  onClick={() => setSummaryDisplayMode(mode)}
                  style={{
                    border: "none",
                    borderRadius: 999,
                    padding: "8px 14px",
                    background: summaryDisplayMode === mode ? "rgba(34,197,94,0.18)" : "transparent",
                    color: summaryDisplayMode === mode ? "#f8fafc" : "#cbd5e1",
                    fontSize: 13,
                    fontWeight: 800,
                    cursor: "pointer",
                  }}
                >
                  {label}
                </button>
              ))}
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: summaryDisplayMode === "kpis" ? "minmax(320px, 0.75fr) minmax(520px, 1.25fr)" : "1fr",
                gap: 18,
              }}
            >
              {summaryDisplayMode === "kpis" ? (
              <div
                style={{
                  background: "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(8,13,20,0.94))",
                  border: "1px solid rgba(148,163,184,0.24)",
                  borderRadius: 18,
                  padding: 20,
                  boxShadow: "0 18px 36px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05)",
                }}
              >
                  <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 14, color: "#f8fafc" }}>
                  KPI Summary
                  </div>
                  <div style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 14 }}>
                    Showing {formatEnumLabel(selectedStatusFilter)} KPIs
                    {selectedLens === "all" ? "" : " in the selected lens"}
                    {selectedOwner === "all" ? "" : ` for ${selectedOwner}`}.
                  </div>
                <div style={{ display: "grid", gap: 10 }}>
                  {treeFilteredNodes.length === 0 ? (
                    <div style={{ color: "#94a3b8" }}>
                      No {formatEnumLabel(selectedStatusFilter)} KPIs found in this view.
                    </div>
                  ) : (
                    treeFilteredNodes.map(({ depth, node }) => {
                      const isSelected = selectedKpiId === node.id;
                      const hasChildren =
                        (kpiRelationships.childrenByParent.get(node.id)?.length ?? 0) > 0;
                      const hasParent = kpiRelationships.parentByChild.has(node.id);
                      const lineageLabels = kpiRelationships.lineageLabelsById.get(node.id) ?? [node.label];
                      const ancestorLabels = lineageLabels.slice(0, -1);
                      const lineageLabel = kpiRelationships.lineageLabelById.get(node.id) ?? node.label;
                      const lensLabel = node.lenses.length > 0
                        ? node.lenses
                            .map((lens) => workspaceLensConfigs[lens]?.name ?? lens)
                            .join(", ")
                        : "No lenses";
                      const parentLabel = hasParent
                        ? kpiRelationships.labelsById.get(
                            kpiRelationships.parentByChild.get(node.id) ?? ""
                          ) ?? "Untitled KPI"
                        : null;
                      const nodeColor = kpiDisplayColor(node);
                      const initiativeCount = node.initiatives.length;
                      const taskCount =
                        node.tasks.length +
                        node.initiatives.reduce((total, initiative) => total + initiative.tasks.length, 0);
                      return (
                      <div
                        key={node.id}
                        onClick={() => setSelectedKpiId(node.id)}
                        onDoubleClick={() => openKpiEditor(node.id)}
                        style={{
                          border: isSelected
                            ? `3px solid ${nodeColor}`
                            : `2px solid ${hexToRgba(nodeColor, 0.82)}`,
                          borderRadius: 14,
                          padding: isSelected ? 16 : "12px 14px",
                          display: "grid",
                          gap: isSelected ? 10 : 6,
                          cursor: "pointer",
                          marginLeft: depth * 28,
                          background: isSelected
                            ? `linear-gradient(180deg, ${hexToRgba(nodeColor, 0.3)}, rgba(8,13,20,0.96))`
                            : depth > 0
                              ? "rgba(15,23,42,0.78)"
                              : "linear-gradient(180deg, rgba(17,24,39,0.96), rgba(8,13,20,0.92))",
                          boxShadow:
                            isSelected
                              ? `0 0 0 4px ${hexToRgba(nodeColor, 0.18)}, 0 14px 24px rgba(0,0,0,0.24), 0 0 32px ${hexToRgba(nodeColor, 0.14)}`
                              : "0 10px 22px rgba(0,0,0,0.18)",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 12,
                            alignItems: "flex-start",
                            flexWrap: "wrap",
                          }}
                        >
                          <div>
                            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                              <div style={{ fontSize: 17, fontWeight: 800, color: "#f8fafc" }}>
                                {depth > 0 ? "\u21b3 " : ""}
                                {node.label}
                              </div>
                              {node.calculationMode !== "direct" ? (
                                <span
                                  style={{
                                    fontSize: 11,
                                    fontWeight: 800,
                                    color: "#94a3b8",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                  }}
                                >
                                  {node.calculationMode === "calculated_values" ? "Calculated" : "Influenced"}
                                </span>
                              ) : null}
                            </div>
                            {ancestorLabels.length > 0 ? (
                              <div style={{ marginTop: 5, fontSize: 12, color: "#cbd5e1", lineHeight: 1.45 }}>
                                <span style={{ fontWeight: 800, color: "#94a3b8" }}>Lineage:</span>{" "}
                                {lineageLabel}
                              </div>
                            ) : null}
                            <div style={{ marginTop: 6, fontSize: 13, color: "#dbeafe" }}>
                              Actual: <strong>{formatMetricValue(node.actual, node.valueType)}</strong> |{" "}
                              {formatComparisonMetricLabel(reportComparisonMode)}:{" "}
                              <strong>{formatMetricValue(node.target, node.valueType)}</strong>
                            </div>
                            <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>
                              {initiativeCount} {initiativeCount === 1 ? "initiative" : "initiatives"} •{" "}
                              {taskCount} {taskCount === 1 ? "task" : "tasks"}
                            </div>
                            {isSelected ? (
                              <div style={{ marginTop: 8 }}>
                              <select
                                value={ownerOptions.includes(node.owner) ? node.owner : ""}
                                onClick={(event) => event.stopPropagation()}
                                onChange={(event) => {
                                  event.stopPropagation();
                                  onKpiFieldChange(node.id, "owner", event.target.value);
                                }}
                                style={{
                                  border: "1px solid rgba(148,163,184,0.28)",
                                  borderRadius: 10,
                                  padding: "8px 10px",
                                  fontSize: 13,
                                  background: "rgba(8,13,20,0.92)",
                                  color: "#f8fafc",
                                  minWidth: 220,
                                }}
                              >
                                <option value="">Unassigned</option>
                                {ownerOptions.map((owner) => (
                                  <option key={owner} value={owner}>
                                    {owner}
                                  </option>
                                ))}
                              </select>
                              </div>
                            ) : (
                              <div style={{ marginTop: 6, fontSize: 12, color: "#94a3b8" }}>
                                Owner: {node.owner || "Unassigned"}
                              </div>
                            )}
                          </div>
                        </div>
                        {isSelected ? (
                          <>
                            <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                              {lineageLabel}
                            </div>
                            <div style={{ fontSize: 13, color: "#cbd5e1" }}>
                              {node.lenses
                                .map((lens) => workspaceLensConfigs[lens]?.name ?? lens)
                                .join(", ")}
                            </div>
                            {node.statusNote ? (
                              <div style={{ fontSize: 13, color: "#fed7aa" }}>{node.statusNote}</div>
                            ) : null}
                          </>
                        ) : (
                          <>
                            <div style={{ fontSize: 12, color: "#94a3b8" }}>
                              {hasChildren
                                ? "Parent KPI"
                                : hasParent
                                  ? `Child KPI · Parent: ${parentLabel}`
                                  : "Standalone KPI"}
                            </div>
                            <div style={{ fontSize: 12, color: "#94a3b8" }}>
                              Lenses: {lensLabel}
                            </div>
                          </>
                        )}
                      </div>
                      );
                    })
                  )}
                </div>
              </div>
              ) : (
                <div
                  style={{
                    background: "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(8,13,20,0.94))",
                    border: "1px solid rgba(148,163,184,0.24)",
                    borderRadius: 18,
                    padding: 20,
                    boxShadow: "0 18px 36px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05)",
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 8, color: "#f8fafc" }}>
                    Initiative Summary
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      gap: 12,
                      alignItems: "flex-start",
                      marginBottom: 14,
                      flexWrap: "wrap",
                    }}
                  >
                    <div style={{ fontSize: 13, color: "#cbd5e1", lineHeight: 1.5 }}>
                      Showing {filteredWorkItems.length} of {sortedWorkItems.length} initiatives and tasks from matching KPIs and their descendants.
                      <br />
                      Update emails only go to assigned, open tasks. Initiatives, completed tasks, and unassigned tasks are not emailed.
                    </div>
                    <div style={{ display: "grid", gap: 6, justifyItems: "end", maxWidth: 620 }}>
                      <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center", flexWrap: "wrap" }}>
                        <select
                          aria-label="Initiative update scope"
                          value={selectedUpdateInitiativeKey}
                          onChange={(event) => setSelectedUpdateInitiativeKey(event.target.value)}
                          style={{
                            width: 300,
                            maxWidth: "100%",
                            border: "1px solid rgba(148,163,184,0.28)",
                            borderRadius: 10,
                            padding: "8px 10px",
                            background: "rgba(8,13,20,0.92)",
                            color: "#f8fafc",
                            fontSize: 13,
                            fontWeight: 700,
                          }}
                          title="Choose one initiative, or leave this on all matching work."
                        >
                          <option value="">All matching initiatives and tasks</option>
                          {initiativeUpdateOptions.map((option) => (
                            <option key={option.key} value={option.key}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => sendTaskUpdateRequests(selectedUpdateTaskIds)}
                          disabled={isSendingTaskUpdateEmails || selectedUpdateTaskIds.length === 0}
                          style={{
                            border: "1px solid rgba(34,197,94,0.45)",
                            borderRadius: 10,
                            padding: "8px 12px",
                            background:
                              isSendingTaskUpdateEmails || selectedUpdateTaskIds.length === 0
                                ? "rgba(15,23,42,0.46)"
                                : "rgba(17,44,30,0.72)",
                            color:
                              isSendingTaskUpdateEmails || selectedUpdateTaskIds.length === 0
                                ? "rgba(148,163,184,0.66)"
                                : "#73f28e",
                            cursor:
                              isSendingTaskUpdateEmails || selectedUpdateTaskIds.length === 0
                                ? "not-allowed"
                                : "pointer",
                            fontSize: 13,
                            fontWeight: 850,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {isSendingTaskUpdateEmails
                            ? "Sending..."
                            : selectedUpdateTaskIds.length === 0
                            ? "No assigned task owners"
                            : `Email ${selectedUpdateOwnerCount} owner${selectedUpdateOwnerCount === 1 ? "" : "s"} for ${selectedUpdateTaskIds.length} task update${selectedUpdateTaskIds.length === 1 ? "" : "s"}`}
                        </button>
                      </div>
                      {taskUpdateEmailStatusMessage ? (
                        <div style={{ maxWidth: 360, fontSize: 12, color: "#cbd5e1", textAlign: "right" }}>
                          {taskUpdateEmailStatusMessage}
                        </div>
                      ) : (
                        <div style={{ maxWidth: 420, fontSize: 12, color: "#94a3b8", textAlign: "right" }}>
                          {selectedUpdateInitiative
                            ? `${selectedUpdateTaskIds.length} eligible task update${selectedUpdateTaskIds.length === 1 ? "" : "s"} in the selected initiative`
                            : `${updateRequestTaskIds.length} eligible of ${updateRequestSummary.taskCount} task${updateRequestSummary.taskCount === 1 ? "" : "s"} shown`}
                          {!selectedUpdateInitiative && updateRequestSummary.initiativeCount > 0 ? `; ${updateRequestSummary.initiativeCount} initiative${updateRequestSummary.initiativeCount === 1 ? "" : "s"} excluded` : ""}
                          {!selectedUpdateInitiative && updateRequestSummary.doneTaskCount > 0 ? `; ${updateRequestSummary.doneTaskCount} completed excluded` : ""}
                          {!selectedUpdateInitiative && updateRequestSummary.unassignedOpenTaskCount > 0 ? `; ${updateRequestSummary.unassignedOpenTaskCount} unassigned excluded` : ""}
                        </div>
                      )}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
                      gap: 10,
                      marginBottom: 16,
                      padding: 12,
                      borderRadius: 16,
                      background: "rgba(8,13,20,0.76)",
                      border: "1px solid rgba(148,163,184,0.18)",
                    }}
                  >
                    <label style={{ display: "grid", gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Sort
                      </span>
                      <select
                        value={workSortMode}
                        onChange={(event) => setWorkSortMode(event.target.value as WorkSortMode)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.28)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: 13,
                          background: "rgba(8,13,20,0.92)",
                          color: "#f8fafc",
                        }}
                      >
                        <option value="due-date">Due date</option>
                        <option value="priority">Priority</option>
                        <option value="assigned-to">Assigned to</option>
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Owner
                      </span>
                      <select
                        value={workOwnerFilter}
                        onChange={(event) => setWorkOwnerFilter(event.target.value)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.28)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: 13,
                          background: "rgba(8,13,20,0.92)",
                          color: "#f8fafc",
                        }}
                      >
                        <option value="all">All owners</option>
                        {workOwnerOptions.map((owner) => (
                          <option key={owner} value={owner}>
                            {owner}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Priority
                      </span>
                      <select
                        value={workPriorityFilter}
                        onChange={(event) => setWorkPriorityFilter(event.target.value as WorkPriorityFilter)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.28)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: 13,
                          background: "rgba(8,13,20,0.92)",
                          color: "#f8fafc",
                        }}
                      >
                        <option value="all">All priorities</option>
                        {workPriorityOptions.map((priority) => (
                          <option key={priority} value={priority}>
                            {formatWorkPriorityLabel(priority)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Status
                      </span>
                      <select
                        value={workStatusFilter}
                        onChange={(event) => setWorkStatusFilter(event.target.value as WorkStatusFilter)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.28)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: 13,
                          background: "rgba(8,13,20,0.92)",
                          color: "#f8fafc",
                        }}
                      >
                        <option value="all">All open statuses</option>
                        {workStatusOptions.filter((status) => status !== "done").map((status) => (
                          <option key={status} value={status}>
                            {formatWorkStatusLabel(status)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Due
                      </span>
                      <select
                        value={workDueFilter}
                        onChange={(event) => setWorkDueFilter(event.target.value as WorkDueFilter)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.28)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: 13,
                          background: "rgba(8,13,20,0.92)",
                          color: "#f8fafc",
                        }}
                      >
                        <option value="all">All due dates</option>
                        <option value="past-due">Past due</option>
                        <option value="this-week">This week</option>
                        <option value="next-week">Next week</option>
                        <option value="future">Future</option>
                        <option value="unassigned">No due date</option>
                      </select>
                    </label>
                    <label style={{ display: "grid", gap: 5 }}>
                      <span style={{ fontSize: 11, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.06em" }}>
                        Effort
                      </span>
                      <select
                        value={workEffortFilter}
                        onChange={(event) => setWorkEffortFilter(event.target.value as WorkEffortFilter)}
                        style={{
                          border: "1px solid rgba(148,163,184,0.28)",
                          borderRadius: 10,
                          padding: "8px 10px",
                          fontSize: 13,
                          background: "rgba(8,13,20,0.92)",
                          color: "#f8fafc",
                        }}
                      >
                        <option value="all">All effort</option>
                        {workEffortOptions.map((effort) => (
                          <option key={effort} value={effort}>
                            {formatWorkEffortLabel(effort)}
                          </option>
                        ))}
                      </select>
                    </label>
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {filteredWorkItems.length === 0 ? (
                      <div style={{ color: "#6b7280" }}>
                        No initiatives or tasks match these filters.
                      </div>
                    ) : (
                      filteredWorkItems.map((item, index) => {
                        const surface = workItemSurface(item.kind);
                        const previousItem = filteredWorkItems[index - 1];
                        const shouldShowKpiHeader = !previousItem || previousItem.kpiId !== item.kpiId;
                        const shouldShowPriority = Boolean(item.priority && item.priority !== "normal");
                        const shouldShowEffort = Boolean(item.effort && item.effort !== "medium");
                        return (
                          <React.Fragment
                            key={`${item.kind}-${item.kpiId}-${item.initiativeId ?? ""}-${item.taskId ?? ""}-${index}`}
                          >
                            {shouldShowKpiHeader ? (
                              <div
                                style={{
                                  marginTop: index === 0 ? 0 : 12,
                                  padding: "4px 2px",
                                  color: "#111827",
                                  fontSize: 13,
                                  fontWeight: 900,
                                  textTransform: "uppercase",
                                  letterSpacing: "0.06em",
                                }}
                              >
                                {item.kpiLineageLabel}
                              </div>
                            ) : null}
                            <div
                              role="button"
                              tabIndex={0}
                              onClick={() => openWorkItemEditor(item)}
                              onKeyDown={(event) => {
                                if (event.key === "Enter" || event.key === " ") {
                                  event.preventDefault();
                                  openWorkItemEditor(item);
                                }
                              }}
                              style={{
                                width: "100%",
                                border: `1px solid ${surface.border}`,
                                borderRadius: 14,
                                padding: "12px 14px",
                                background: surface.background,
                                cursor: "pointer",
                                textAlign: "left",
                                display: "grid",
                                gap: 7,
                                marginLeft: item.indentLevel * 20,
                              }}
                            >
                              <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
                                <div style={{ minWidth: 0 }}>
                                  <div style={{ fontSize: item.kind === "initiative" ? 16 : 14, fontWeight: 850, color: surface.title }}>
                                    {item.indentLevel > 0 ? `↳ ${item.title}` : item.title}
                                  </div>
                                  <div style={{ marginTop: 4, fontSize: 12, color: surface.meta }}>
                                    {item.owner || "Unassigned"} • {formatWorkDueDate(item.dueDate)} • {formatWorkStatusLabel(item.status)}
                                    {item.initiativeTitle && item.kind !== "initiative" ? ` • ${item.initiativeTitle}` : ""}
                                  </div>
                                </div>
                                <div style={{ display: "flex", gap: 6, flexWrap: "wrap", justifyContent: "flex-end", alignItems: "center" }}>
                                  <select
                                    aria-label={`Update status for ${item.title}`}
                                    value={item.status}
                                    onClick={(event) => event.stopPropagation()}
                                    onKeyDown={(event) => event.stopPropagation()}
                                    onChange={(event) => {
                                      event.stopPropagation();
                                      onWorkItemStatusChange(item, event.target.value as WorkStatus);
                                    }}
                                    style={{
                                      ...workStatusChipStyle(item.status),
                                      appearance: "none",
                                      cursor: "pointer",
                                      minWidth: 116,
                                      padding: "6px 10px",
                                    }}
                                  >
                                    {workStatusOptions.map((status) => (
                                      <option key={status} value={status}>
                                        {formatWorkStatusLabel(status)}
                                      </option>
                                    ))}
                                  </select>
                                  {shouldShowPriority ? (
                                    <span style={workPriorityChipStyle(item.priority)}>
                                      {formatWorkPriorityLabel(item.priority)}
                                    </span>
                                  ) : null}
                                  {shouldShowEffort ? (
                                    <span style={workEffortChipStyle(item.effort)}>
                                      {formatWorkEffortLabel(item.effort)}
                                    </span>
                                  ) : null}
                                </div>
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {formatWorkItemKindLabel(item.kind)}
                              </div>
                            </div>
                          </React.Fragment>
                        );
                      })
                    )}
                  </div>
                </div>
              )}

              {summaryDisplayMode === "kpis" ? (
              <div style={{ display: "grid", gap: 18 }}>
                <div
                  style={{
                    background: "linear-gradient(180deg, rgba(15,23,42,0.96), rgba(8,13,20,0.94))",
                    border: "1px solid rgba(148,163,184,0.24)",
                    borderRadius: 18,
                    padding: 20,
                    position: "sticky",
                    top: 16,
                    alignSelf: "start",
                    boxShadow: "0 18px 36px rgba(0,0,0,0.28), inset 0 1px 0 rgba(255,255,255,0.05)",
                  }}
                >
                  <div style={{ fontSize: 20, fontWeight: 800, marginBottom: 14, color: "#f8fafc" }}>
                    Initiatives and Tasks
                  </div>
                  <div style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 14 }}>
                    {selectedFilteredKpi
                      ? `Showing initiatives and tasks for ${
                          kpiRelationships.lineageLabelById.get(selectedFilteredKpi.id) ?? selectedFilteredKpi.label
                        }.`
                      : "Select a KPI to view its initiatives and tasks."}
                  </div>
                  <div style={{ display: "grid", gap: 10 }}>
                    {displayedWorkItems.length === 0 ? (
                      <div style={{ color: "#94a3b8" }}>
                        {selectedFilteredKpi
                          ? "No initiatives or tasks found for this KPI."
                          : "No KPI selected yet."}
                      </div>
                    ) : (
                      displayedWorkItems.slice(0, 10).map((item, index) => {
                        const surface = workItemSurface(item.kind);
                        const relatedKpiStatus = statusFilteredNodes.find(
                          (node) => node.id === item.kpiId
                        )?.status;
                        const itemLineageLabel = kpiRelationships.lineageLabelById.get(item.kpiId) ?? item.kpiLineageLabel;

                        return (
                          <div
                            key={`${item.kind}-${item.title}-${index}`}
                            onClick={() => openWorkItemEditor(item)}
                            onDoubleClick={() => openWorkItemEditor(item)}
                            style={{
                              borderRadius: 14,
                              padding: 12,
                              background: surface.background,
                              border: `1px solid ${surface.border}`,
                              marginLeft: item.indentLevel * 24,
                              cursor: "pointer",
                            }}
                          >
                            <div style={{ fontWeight: 800, color: surface.title }}>
                              {item.indentLevel > 0 ? `\u21b3 ${item.title}` : item.title}
                            </div>
                            <div style={{ fontSize: 13, color: surface.meta, marginTop: 4 }}>
                              {formatEnumLabel(item.kind)} | {formatEnumLabel(item.status)}
                            </div>
                            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6 }}>
                              Owner: {item.owner || "Unassigned"}
                            </div>
                            <div style={{ fontSize: 13, color: "#94a3b8", marginTop: 6 }}>
                              <span>KPI: </span>
                              <span
                                style={{
                                  color: relatedKpiStatus
                                    ? statusColor(relatedKpiStatus)
                                    : "#93c5fd",
                                  fontWeight: 700,
                                  textDecoration: "underline",
                                  textDecorationThickness: "2px",
                                  textUnderlineOffset: "2px",
                                }}
                              >
                                {itemLineageLabel}
                              </span>
                              {item.initiativeTitle ? ` | Initiative: ${item.initiativeTitle}` : ""}
                              {item.dueDate ? ` | Due: ${item.dueDate}` : ""}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>
              </div>
              ) : null}
            </div>

            {editingKpiId ? (
              <div
                className="kpi-editor-backdrop"
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(17, 24, 39, 0.52)",
                  display: "grid",
                  placeItems: "center",
                  padding: 24,
                  zIndex: 1000,
                }}
              >
                <div
                  className="kpi-editor-surface"
                  onClick={(event) => event.stopPropagation()}
                  style={{
                    width: "min(940px, calc(100vw - 48px))",
                    maxHeight: "min(82vh, 780px)",
                    minWidth: 620,
                    minHeight: 520,
                    overflow: "auto",
                    resize: "both",
                    background: "#fff",
                    borderRadius: 18,
                    border: "1px solid #d1d5db",
                    boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
                    padding: 22,
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 16,
                      marginBottom: 18,
                      position: "sticky",
                      top: -22,
                      margin: "-22px -22px 18px",
                      padding: "22px 22px 16px",
                      background: "rgba(255,255,255,0.96)",
                      borderBottom: "1px solid #e5e7eb",
                      zIndex: 2,
                      backdropFilter: "blur(6px)",
                    }}
                  >
                    <div style={{ flex: 1 }}>
                      <div style={{ marginBottom: 10 }}>
                        <label style={{ display: "grid", gap: 6 }}>
                          <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                            KPI Name
                          </span>
                          <input
                            value={selectedPersistedKpi?.data.label ?? ""}
                            onChange={(event) =>
                              selectedPersistedKpi
                                ? onKpiFieldChange(
                                    selectedPersistedKpi.id,
                                    "label",
                                    event.target.value
                                  )
                                : undefined
                            }
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "10px 12px",
                              fontSize: 14,
                              width: "100%",
                              boxSizing: "border-box",
                            }}
                          />
                        </label>
                      </div>
                      <div
                        style={{
                          fontSize: 13,
                          color: "#4b5563",
                          lineHeight: 1.5,
                        }}
                      >
                        {selectedPersistedKpi
                          ? `${toTitleCase(selectedPersistedKpi.data.frequency ?? "weekly")} | ${
                              (selectedPersistedKpi.data.performanceDirection ?? "higher_is_better") ===
                              "lower_is_better"
                                ? "Lower is better"
                                : "Higher is better"
                            } | ${
                              formatValueTypeLabel(
                                (selectedPersistedKpi.data.valueType ?? "count") as ValueType
                              )
                            } | ${
                              selectedPersistedKpi.data.eventDate
                                ? `Event: ${selectedPersistedKpi.data.eventDate}`
                                : "Ongoing"
                            } | ${
                              formatCalculationModeLabel(
                                normalizeCalculationMode(
                                  selectedPersistedKpi.data.calculationMode,
                                  selectedPersistedKpi.data.isRollup
                                )
                              )
                            }`
                          : "Edit KPI"}
                      </div>
                      {selectedPersistedParentLabel ? (
                        <div
                          style={{
                            fontSize: 13,
                            color: "#6b7280",
                            lineHeight: 1.5,
                            marginTop: 6,
                          }}
                        >
                          Parent KPI: <strong style={{ color: "#111827" }}>{selectedPersistedParentLabel}</strong>
                        </div>
                      ) : null}
                      <div
                        style={{
                          fontSize: 13,
                          fontWeight: 700,
                          color: isSaving
                            ? "#1d4ed8"
                            : saveMessage === "Changes saved."
                              ? "#166534"
                              : saveMessage
                                ? "#b91c1c"
                                : "#6b7280",
                          marginTop: 8,
                        }}
                      >
                        {isSaving
                          ? "Saving changes..."
                          : saveMessage
                            ? saveMessage
                            : "Changes save automatically."}
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setEditingKpiId(null);
                        setPollEditorKpiId(null);
                      }}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: 10,
                        background: "#fff",
                        color: "#111827",
                        padding: "8px 12px",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                      }}
                    >
                      Close
                    </button>
                  </div>

              {!selectedPersistedKpi ? (
                <div style={{ color: "#6b7280" }}>
                  No KPI selected. Use the Edit KPI button from a KPI card.
                </div>
              ) : (
                <>
                  <div
                    className="kpi-editor-section"
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      className="kpi-editor-toggle"
                      onClick={() => setIsKpiInfoOpen((current) => !current)}
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        padding: "14px 16px",
                        border: "none",
                        background: "#f9fafb",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>KPI Info</div>
                        <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                          Definition, measurement, ownership, and KPI settings
                        </div>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
                        {isKpiInfoOpen ? "Hide" : "Show"}
                      </div>
                    </button>

                    {isKpiInfoOpen ? (
                      <div
                        style={{
                          padding: 16,
                          display: "grid",
                          gap: 18,
                        }}
                      >
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))",
                            gap: 12,
                          }}
                        >
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Owner
                              </span>
                              <select
                                ref={ownerInputRef}
                                value={
                                  ownerOptions.includes(selectedPersistedKpi.data.owner ?? "")
                                    ? (selectedPersistedKpi.data.owner ?? "")
                                    : ""
                                }
                                onChange={(event) =>
                                  onKpiFieldChange(selectedPersistedKpi.id, "owner", event.target.value)
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                              >
                                <option value="">Unassigned</option>
                                {ownerOptions.map((owner) => (
                                  <option key={owner} value={owner}>
                                    {owner}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Reporting Period
                              </span>
                              <select
                                value={selectedPersistedKpi.data.frequency ?? "weekly"}
                                onChange={(event) =>
                                  onKpiDetailChange(
                                    selectedPersistedKpi.id,
                                    "frequency",
                                    event.target.value
                                  )
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                              >
                                {frequencyOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {formatEnumLabel(option)}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Parent KPI
                              </span>
                              <div
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  background: "#fff",
                                  color: "#111827",
                                  minHeight: 20,
                                }}
                              >
                                {selectedPersistedParentLabel ?? "No parent KPI"}
                              </div>
                            </label>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Performance Rule
                              </span>
                              <select
                                value={selectedPersistedKpi.data.performanceDirection ?? "higher_is_better"}
                                onChange={(event) =>
                                  onKpiConfigChange(
                                    selectedPersistedKpi.id,
                                    "performanceDirection",
                                    event.target.value
                                  )
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                              >
                                <option value="higher_is_better">Higher is better</option>
                                <option value="lower_is_better">Lower is better</option>
                              </select>
                            </label>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                KPI Calculation
                              </span>
                              <select
                                value={normalizeCalculationMode(
                                  selectedPersistedKpi.data.calculationMode,
                                  selectedPersistedKpi.data.isRollup
                                )}
                                onChange={(event) =>
                                  onKpiConfigChange(
                                    selectedPersistedKpi.id,
                                    "calculationMode",
                                    event.target.value as KpiCalculationMode
                                  )
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                              >
                                <option value="direct">Direct KPI</option>
                                <option value="calculated_values">Calculated from child values</option>
                                <option value="influenced_by_children">Influenced by child performance</option>
                                <option value="formula_percentage">Calculated percentage from KPIs</option>
                              </select>
                            </label>
                            <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginTop: 8 }}>
                              {normalizeCalculationMode(
                                selectedPersistedKpi.data.calculationMode,
                                selectedPersistedKpi.data.isRollup
                              ) === "calculated_values"
                                ? "Target and actual are calculated from child KPIs. Relationship percentages are not used."
                                : normalizeCalculationMode(
                                      selectedPersistedKpi.data.calculationMode,
                                      selectedPersistedKpi.data.isRollup
                                    ) === "formula_percentage"
                                  ? "Target is entered directly. Actual is a read-only percentage calculated from a numerator KPI divided by a denominator KPI."
                                : normalizeCalculationMode(
                                      selectedPersistedKpi.data.calculationMode,
                                      selectedPersistedKpi.data.isRollup
                                    ) === "influenced_by_children"
                                  ? "Performance is calculated from child KPI scores using relationship percentages."
                                  : "Target and actual are entered directly for this KPI."}
                            </div>
                            {normalizeCalculationMode(
                              selectedPersistedKpi.data.calculationMode,
                              selectedPersistedKpi.data.isRollup
                            ) === "formula_percentage" ? (
                              <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
                                <label style={{ display: "grid", gap: 6 }}>
                                  <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                    Numerator KPI
                                  </span>
                                  <select
                                    value={selectedPersistedKpi.data.formulaNumeratorKpiId ?? ""}
                                    onChange={(event) =>
                                      onKpiDetailChange(
                                        selectedPersistedKpi.id,
                                        "formulaNumeratorKpiId",
                                        event.target.value
                                      )
                                    }
                                    style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                                  >
                                    <option value="">Select numerator KPI</option>
                                    {(workspace?.allNodes ?? [])
                                      .filter((node) => node.id !== selectedPersistedKpi.id)
                                      .map((node) => (
                                        <option key={node.id} value={node.id}>
                                          {node.data.label}
                                        </option>
                                      ))}
                                  </select>
                                </label>
                                <label style={{ display: "grid", gap: 6 }}>
                                  <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                    Denominator KPI
                                  </span>
                                  <select
                                    value={selectedPersistedKpi.data.formulaDenominatorKpiId ?? ""}
                                    onChange={(event) =>
                                      onKpiDetailChange(
                                        selectedPersistedKpi.id,
                                        "formulaDenominatorKpiId",
                                        event.target.value
                                      )
                                    }
                                    style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                                  >
                                    <option value="">Select denominator KPI</option>
                                    {(workspace?.allNodes ?? [])
                                      .filter((node) => node.id !== selectedPersistedKpi.id)
                                      .map((node) => (
                                        <option key={node.id} value={node.id}>
                                          {node.data.label}
                                        </option>
                                      ))}
                                  </select>
                                </label>
                              </div>
                            ) : null}
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Event Date
                              </span>
                              <input
                                type="date"
                                value={selectedPersistedKpi.data.eventDate ?? ""}
                                onChange={(event) =>
                                  onKpiDetailChange(selectedPersistedKpi.id, "eventDate", event.target.value)
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14 }}
                              />
                            </label>
                            <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginTop: 8 }}>
                              Leave blank for an ongoing KPI. Before this date, the KPI stays neutral.
                            </div>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Data Source
                              </span>
                              <select
                                value={
                                  dataSourceOptions.includes(
                                    (selectedPersistedKpi.data.dataSource ?? "Manual") as (typeof dataSourceOptions)[number]
                                  )
                                    ? (selectedPersistedKpi.data.dataSource ?? "Manual")
                                    : "Manual"
                                }
                                onChange={(event) =>
                                  onKpiDetailChange(selectedPersistedKpi.id, "dataSource", event.target.value)
                                }
                                style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, background: "#fff" }}
                              >
                                {dataSourceOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {option}
                                  </option>
                                ))}
                              </select>
                            </label>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Value Type
                              </span>
                              <select
                                value={
                                  valueTypeOptions.includes(
                                    (
                                      normalizeCalculationMode(
                                        selectedPersistedKpi.data.calculationMode,
                                        selectedPersistedKpi.data.isRollup
                                      ) === "formula_percentage"
                                        ? "percentage"
                                        : selectedPersistedKpi.data.dataSource === "Poll"
                                        ? "rating_1_10"
                                        : (selectedPersistedKpi.data.valueType ?? "count")
                                    ) as ValueType
                                  )
                                    ? normalizeCalculationMode(
                                        selectedPersistedKpi.data.calculationMode,
                                        selectedPersistedKpi.data.isRollup
                                      ) === "formula_percentage"
                                      ? "percentage"
                                      : selectedPersistedKpi.data.dataSource === "Poll"
                                      ? "rating_1_10"
                                      : (selectedPersistedKpi.data.valueType ?? "count")
                                    : "count"
                                }
                                onChange={(event) =>
                                  onKpiDetailChange(selectedPersistedKpi.id, "valueType", event.target.value)
                                }
                                disabled={
                                  selectedPersistedKpi.data.dataSource === "Poll" ||
                                  normalizeCalculationMode(
                                    selectedPersistedKpi.data.calculationMode,
                                    selectedPersistedKpi.data.isRollup
                                  ) === "formula_percentage"
                                }
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  background:
                                    selectedPersistedKpi.data.dataSource === "Poll" ||
                                    normalizeCalculationMode(
                                      selectedPersistedKpi.data.calculationMode,
                                      selectedPersistedKpi.data.isRollup
                                    ) === "formula_percentage"
                                      ? "#f3f4f6"
                                      : "#fff",
                                  color:
                                    selectedPersistedKpi.data.dataSource === "Poll" ||
                                    normalizeCalculationMode(
                                      selectedPersistedKpi.data.calculationMode,
                                      selectedPersistedKpi.data.isRollup
                                    ) === "formula_percentage"
                                      ? "#6b7280"
                                      : "#111827",
                                  cursor:
                                    selectedPersistedKpi.data.dataSource === "Poll" ||
                                    normalizeCalculationMode(
                                      selectedPersistedKpi.data.calculationMode,
                                      selectedPersistedKpi.data.isRollup
                                    ) === "formula_percentage"
                                      ? "not-allowed"
                                      : "pointer",
                                }}
                              >
                                {valueTypeOptions.map((option) => (
                                  <option key={option} value={option}>
                                    {formatValueTypeLabel(option)}
                                  </option>
                                ))}
                              </select>
                              {selectedPersistedKpi.data.dataSource === "Poll" ? (
                                <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45 }}>
                                  Poll-based KPIs always use a 1-10 rating value.
                                </div>
                              ) : normalizeCalculationMode(
                                  selectedPersistedKpi.data.calculationMode,
                                  selectedPersistedKpi.data.isRollup
                                ) === "formula_percentage" ? (
                                <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45 }}>
                                  Formula percentage KPIs always use a percentage value type.
                                </div>
                              ) : null}
                            </label>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <label style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Target
                              </span>
                              <input
                                type="number"
                                disabled={
                                  normalizeCalculationMode(
                                    selectedPersistedKpi.data.calculationMode,
                                    selectedPersistedKpi.data.isRollup
                                  ) === "calculated_values"
                                }
                                value={selectedPersistedTarget ?? ""}
                                onChange={(event) =>
                                  onKpiMetricChange(selectedPersistedKpi.id, "target", event.target.value)
                                }
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  background:
                                    normalizeCalculationMode(
                                      selectedPersistedKpi.data.calculationMode,
                                      selectedPersistedKpi.data.isRollup
                                    ) === "calculated_values"
                                      ? "#f3f4f6"
                                      : "#fff",
                                }}
                              />
                            </label>
                            <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginTop: 8 }}>
                              {normalizeCalculationMode(
                                selectedPersistedKpi.data.calculationMode,
                                selectedPersistedKpi.data.isRollup
                              ) === "calculated_values"
                                ? "Calculated from child KPI targets."
                                : normalizeCalculationMode(
                                    selectedPersistedKpi.data.calculationMode,
                                    selectedPersistedKpi.data.isRollup
                                  ) === "formula_percentage"
                                  ? "Set the desired percentage target for this KPI."
                                : "Set the desired result for the current reporting period."}
                            </div>
                          </div>
                          <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 12, padding: 14 }}>
                            <div style={{ display: "grid", gap: 6 }}>
                              <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                                Actual
                              </span>
                              <div style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, color: "#111827", background: selectedPersistedKpi.data.dataSource === "Poll" ? "#f3f4f6" : "#fff" }}>
                                {formatMetricValue(
                                  selectedPersistedActual,
                                  getNormalizedValueType(
                                    selectedPersistedKpi.data.dataSource,
                                    selectedPersistedKpi.data.valueType
                                  )
                                )}
                              </div>
                            </div>
                            <div style={{ fontSize: 12, color: "#4b5563", lineHeight: 1.45, marginTop: 8 }}>
                              {normalizeCalculationMode(
                                selectedPersistedKpi.data.calculationMode,
                                selectedPersistedKpi.data.isRollup
                              ) === "calculated_values"
                                ? "Calculated from child KPI actuals."
                                : normalizeCalculationMode(
                                    selectedPersistedKpi.data.calculationMode,
                                    selectedPersistedKpi.data.isRollup
                                  ) === "formula_percentage"
                                  ? "Calculated from numerator and denominator KPI actuals."
                                : selectedPersistedKpi.data.dataSource === "Poll"
                                ? "Poll KPIs calculate actual performance from submitted responses."
                                : "Actual reflects the value stored for the current reporting period."}
                            </div>
                          </div>
                        </div>

                        <div style={{ display: "grid", gap: 16 }}>
                          <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                              Definition
                            </span>
                            <textarea
                              value={selectedPersistedKpi.data.definition ?? ""}
                              onChange={(event) =>
                                onKpiDetailChange(selectedPersistedKpi.id, "definition", event.target.value)
                              }
                              rows={3}
                              style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                            />
                          </label>
                          <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                              How It Is Measured
                            </span>
                            <textarea
                              value={selectedPersistedKpi.data.measurement ?? ""}
                              onChange={(event) =>
                                onKpiDetailChange(selectedPersistedKpi.id, "measurement", event.target.value)
                              }
                              rows={3}
                              style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                            />
                          </label>
                          <label style={{ display: "grid", gap: 6 }}>
                            <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                              Update Notes
                            </span>
                            <textarea
                              value={selectedPersistedKpi.data.updateNotes ?? ""}
                              onChange={(event) =>
                                onKpiDetailChange(selectedPersistedKpi.id, "updateNotes", event.target.value)
                              }
                              rows={3}
                              style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                            />
                          </label>
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 16,
                      padding: 16,
                      display: "grid",
                      gap: 12,
                      background: "#fafafa",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>KPI Polling</div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                          Create a poll for this KPI so you can collect responses later.
                        </div>
                      </div>
                      {!selectedPersistedKpi.data.pollConfig ? (
                        <button
                          type="button"
                          onClick={() => {
                            onCreatePoll(selectedPersistedKpi.id);
                            setPollEditorMode("edit");
                            setPollEditorKpiId(selectedPersistedKpi.id);
                          }}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 12px",
                            background: "#fff",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Create Poll
                        </button>
                      ) : (
                        <div style={{ display: "grid", gap: 8 }}>
                          <button
                            type="button"
                            onClick={() => {
                              setPollEditorMode("view");
                              setPollEditorKpiId(selectedPersistedKpi.id);
                            }}
                            style={{
                              border: "1px solid #bfdbfe",
                              borderRadius: 10,
                              padding: "8px 12px",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                          >
                            View Polling Data
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setPollEditorMode("edit");
                              setPollEditorKpiId(selectedPersistedKpi.id);
                            }}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 12px",
                              background: "#fff",
                              color: "#111827",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                          >
                            Edit Poll
                          </button>
                        </div>
                      )}
                    </div>

                    {selectedPersistedKpi.data.pollConfig ? (
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                          borderTop: "1px solid #e5e7eb",
                          paddingTop: 12,
                        }}
                      >
                        <div style={{ display: "grid", gap: 4 }}>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>
                            {selectedPersistedKpi.data.pollConfig.title}
                          </div>
                          <div style={{ fontSize: 13, color: "#6b7280" }}>
                            {selectedPersistedKpi.data.pollConfig.isAnonymous
                              ? "Anonymous"
                              : "Identified"}{" "}
                            rating poll for{" "}
                            {(selectedPersistedKpi.data.pollConfig.targetGroups?.length ?? 0) > 0
                              ? selectedPersistedKpi.data.pollConfig.targetGroups.join(", ")
                              : selectedPersistedKpi.data.pollConfig.targetGroup || "selected groups"}
                          </div>
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            fontWeight: 800,
                            color:
                              selectedPersistedKpi.data.pollConfig.status === "published"
                                ? "#166534"
                                : "#92400e",
                            textTransform: "uppercase",
                            letterSpacing: "0.08em",
                          }}
                        >
                          {selectedPersistedKpi.data.pollConfig.status}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  <div
                    className="kpi-work-section"
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 14,
                      background: "#fff",
                      overflow: "hidden",
                    }}
                  >
                    <button
                      type="button"
                      className="kpi-work-toggle"
                      onClick={() => setIsKpiWorkOpen((current) => !current)}
                      style={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        padding: "14px 16px",
                        border: "none",
                        background: "#f9fafb",
                        color: "#111827",
                        cursor: "pointer",
                      }}
                    >
                      <div style={{ textAlign: "left" }}>
                        <div style={{ fontSize: 16, fontWeight: 800 }}>
                          KPI Initiatives and Tasks
                        </div>
                        <div style={{ fontSize: 12, color: "#4b5563", marginTop: 2 }}>
                          Manage direct KPI tasks and initiative-based work
                        </div>
                      </div>
                      <div style={{ fontSize: 12, fontWeight: 800, color: "#2563eb" }}>
                        {isKpiWorkOpen ? "Hide" : "Show"}
                      </div>
                    </button>

                    {isKpiWorkOpen ? (
                      <div
                        style={{
                          padding: 16,
                          display: "grid",
                          gap: 18,
                        }}
                      >
                        {taskUpdateEmailStatusMessage ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                              gap: 12,
                              border: "1px solid #bfdbfe",
                              borderRadius: 12,
                              padding: "10px 12px",
                              background: "#eff6ff",
                              color: "#1e40af",
                              fontSize: 13,
                              fontWeight: 700,
                            }}
                          >
                            <span>{taskUpdateEmailStatusMessage}</span>
                            <button
                              type="button"
                              onClick={() => setTaskUpdateEmailStatusMessage(null)}
                              style={{
                                width: 24,
                                height: 24,
                                border: "1px solid transparent",
                                borderRadius: 999,
                                padding: 0,
                                background: "transparent",
                                color: "#1d4ed8",
                                fontWeight: 900,
                                cursor: "pointer",
                              }}
                              aria-label="Dismiss update request message"
                            >
                              ×
                            </button>
                          </div>
                        ) : null}
                    <div className="kpi-work-initiative-list" style={{ display: "grid", gap: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontSize: 16, fontWeight: 800 }}>Initiatives</div>
                        <button
                          type="button"
                          className="kpi-work-action-button"
                          onClick={() => onAddInitiative(selectedPersistedKpi.id)}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            background: "#fff",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Add Initiative
                        </button>
                      </div>
                      {(selectedPersistedKpi.data.initiatives ?? []).length === 0 ? (
                        <div style={{ color: "#6b7280" }}>No initiatives yet.</div>
                      ) : (
                        (selectedPersistedKpi.data.initiatives ?? []).map((initiative) => (
                          <div
                            key={initiative.id}
                            className="kpi-work-initiative-card"
                            ref={(node) => {
                              workEditorItemRefs.current[`initiative:${initiative.id}`] = node;
                            }}
                            style={{
                              border:
                                workEditorFocus?.kind === "initiative" &&
                                workEditorFocus.initiativeId === initiative.id
                                  ? "2px solid #2563eb"
                                  : "1px solid #e5e7eb",
                              borderRadius: 14,
                              padding: 18,
                              display: "grid",
                              gap: 14,
                              background:
                                workEditorFocus?.kind === "initiative" &&
                                workEditorFocus.initiativeId === initiative.id
                                  ? "#eff6ff"
                                  : "#fff",
                              boxShadow: "0 10px 24px rgba(15, 23, 42, 0.06)",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "flex-end" }}>
                              <button
                                type="button"
                                className="kpi-work-update-button"
                                onClick={() =>
                                  sendTaskUpdateRequests(
                                    initiative.tasks
                                      .filter(canRequestTaskUpdate)
                                      .map((task) => task.id)
                                  )
                                }
                                disabled={
                                  isSendingTaskUpdateEmails ||
                                  initiative.tasks.filter(canRequestTaskUpdate).length === 0
                                }
                                style={{
                                  border: "1px solid #bfdbfe",
                                  borderRadius: 10,
                                  padding: "6px 10px",
                                  background:
                                    isSendingTaskUpdateEmails ||
                                    initiative.tasks.filter(canRequestTaskUpdate).length === 0
                                      ? "#f3f4f6"
                                      : "#eff6ff",
                                  color:
                                    isSendingTaskUpdateEmails ||
                                    initiative.tasks.filter(canRequestTaskUpdate).length === 0
                                      ? "#9ca3af"
                                      : "#1d4ed8",
                                  fontWeight: 800,
                                  cursor:
                                    isSendingTaskUpdateEmails ||
                                    initiative.tasks.filter(canRequestTaskUpdate).length === 0
                                      ? "not-allowed"
                                      : "pointer",
                                }}
                                title={
                                  initiative.tasks.filter(canRequestTaskUpdate).length === 0
                                    ? "Assign an open task before requesting an update."
                                    : "Request updates for assigned open tasks in this initiative."
                                }
                              >
                                Request update
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  onDeleteInitiative(selectedPersistedKpi.id, initiative.id)
                                }
                                style={{
                                  width: 28,
                                  height: 28,
                                  border: "1px solid transparent",
                                  borderRadius: 999,
                                  padding: 0,
                                  background: "transparent",
                                  color: "#9ca3af",
                                  fontWeight: 700,
                                  fontSize: 18,
                                  lineHeight: 1,
                                  cursor: "pointer",
                                  marginLeft: 8,
                                }}
                                title="Delete initiative"
                              >
                                ×
                              </button>
                            </div>
                            <input
                              value={initiative.title}
                              onChange={(event) =>
                                onInitiativeChange(
                                  selectedPersistedKpi.id,
                                  initiative.id,
                                  "title",
                                  event.target.value
                                )
                              }
                              style={{
                                border: "1px solid transparent",
                                borderRadius: 8,
                                padding: "4px 0",
                                fontSize: 18,
                                fontWeight: 800,
                                background: "transparent",
                                color: "#111827",
                              }}
                            />
                            <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(4, minmax(96px, auto))", gap: 8, alignItems: "center" }}>
                              <select
                                value={
                                  ownerOptions.includes(initiative.owner) ? initiative.owner : ""
                                }
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "owner",
                                    event.target.value
                                  )
                                }
                                style={{
                                  border: "none",
                                  borderBottom: "1px solid #e5e7eb",
                                  padding: "4px 0",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  background: "transparent",
                                  color: "#4b5563",
                                }}
                              >
                                <option value="">Unassigned</option>
                                {ownerOptions.map((owner) => (
                                  <option key={owner} value={owner}>
                                    {owner}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="date"
                                value={initiative.dueDate}
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "dueDate",
                                    event.target.value
                                  )
                                }
                                style={{
                                  border: "none",
                                  borderBottom: "1px solid #e5e7eb",
                                  padding: "4px 0",
                                  fontSize: 12,
                                  fontWeight: 700,
                                  background: "transparent",
                                  color: "#6b7280",
                                }}
                                title={formatWorkDueDate(initiative.dueDate)}
                              />
                              <select
                                value={initiative.status}
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "status",
                                    event.target.value
                                  )
                                }
                                style={{
                                  ...workStatusChipStyle(initiative.status),
                                  appearance: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {workStatusOptions.map((status) => (
                                  <option key={status} value={status}>
                                    {formatWorkStatusLabel(status)}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={initiative.priority ?? "normal"}
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "priority",
                                    event.target.value
                                  )
                                }
                                style={{
                                  ...workPriorityChipStyle(initiative.priority),
                                  appearance: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {workPriorityOptions.map((priority) => (
                                  <option key={priority} value={priority}>
                                    {formatWorkPriorityLabel(priority)}
                                  </option>
                                ))}
                              </select>
                              <select
                                value={initiative.effort ?? "medium"}
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "effort",
                                    event.target.value
                                  )
                                }
                                style={{
                                  ...workEffortChipStyle(initiative.effort),
                                  appearance: "none",
                                  cursor: "pointer",
                                }}
                              >
                                {workEffortOptions.map((effort) => (
                                  <option key={effort} value={effort}>
                                    {formatWorkEffortLabel(effort)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <div style={{ display: "grid", gap: 6 }}>
                              <div style={{ fontSize: 11, fontWeight: 800, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em" }}>
                                Description
                              </div>
                              <textarea
                                value={initiative.summary}
                                placeholder="Describe what this initiative is meant to accomplish"
                                onChange={(event) =>
                                  onInitiativeChange(
                                    selectedPersistedKpi.id,
                                    initiative.id,
                                    "summary",
                                    event.target.value
                                  )
                                }
                                rows={3}
                                style={{
                                  border: "1px solid #e5e7eb",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  resize: "vertical",
                                  background: "#f9fafb",
                                  color: "#374151",
                                  lineHeight: 1.45,
                                }}
                              />
                            </div>
                            <div style={{ display: "grid", gap: 8 }}>
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <div style={{ fontSize: 13, fontWeight: 800, color: "#6b7280" }}>
                                  Initiative Tasks
                                </div>
                                <button
                                  type="button"
                                  className="kpi-work-action-button"
                                  onClick={() =>
                                    onAddInitiativeTask(selectedPersistedKpi.id, initiative.id)
                                  }
                                  style={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "6px 8px",
                                    background: "#fff",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  Add Task
                                </button>
                              </div>
                              {initiative.tasks.map((task) => (
                                <div
                                  key={task.id}
                                  className="kpi-work-task-row kpi-work-nested-task-row"
                                  ref={(node) => {
                                    workEditorItemRefs.current[
                                      `initiative-task:${initiative.id}:${task.id}`
                                    ] = node;
                                  }}
                                  style={{
                                    border:
                                      workEditorFocus?.kind === "initiative-task" &&
                                      workEditorFocus.initiativeId === initiative.id &&
                                      workEditorFocus.taskId === task.id
                                        ? "2px solid #2563eb"
                                        : "1px solid #f3f4f6",
                                    borderRadius: 12,
                                    padding: 10,
                                    display: "grid",
                                    gap: 8,
                                    background:
                                      workEditorFocus?.kind === "initiative-task" &&
                                      workEditorFocus.initiativeId === initiative.id &&
                                      workEditorFocus.taskId === task.id
                                        ? "#dbeafe"
                                        : "#fafafa",
                                  }}
                                >
                                  <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                                    <button
                                      type="button"
                                      className="kpi-work-update-button"
                                      onClick={() => sendTaskUpdateRequests([task.id])}
                                      disabled={isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)}
                                      style={{
                                        border: "1px solid #bfdbfe",
                                        borderRadius: 10,
                                        padding: "6px 10px",
                                        background:
                                          isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)
                                            ? "#f3f4f6"
                                            : "#eff6ff",
                                        color:
                                          isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)
                                            ? "#9ca3af"
                                            : "#1d4ed8",
                                        fontWeight: 800,
                                        cursor:
                                          isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)
                                            ? "not-allowed"
                                            : "pointer",
                                      }}
                                      title={
                                        canRequestTaskUpdate(task)
                                          ? "Request an update for this task."
                                          : "Assign this open task before requesting an update."
                                      }
                                    >
                                      Request update
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() =>
                                        onDeleteInitiativeTask(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id
                                        )
                                      }
                                      style={{
                                        width: 26,
                                        height: 26,
                                        border: "1px solid transparent",
                                        borderRadius: 999,
                                        padding: 0,
                                        background: "transparent",
                                        color: "#9ca3af",
                                        fontWeight: 700,
                                        fontSize: 16,
                                        lineHeight: 1,
                                        cursor: "pointer",
                                      }}
                                      title="Delete task"
                                    >
                                      ×
                                    </button>
                                  </div>
                                  <input
                                    value={task.title}
                                    onChange={(event) =>
                                      onInitiativeTaskChange(
                                        selectedPersistedKpi.id,
                                        initiative.id,
                                        task.id,
                                        "title",
                                        event.target.value
                                      )
                                    }
                                    style={{
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                    }}
                                  />
                                  <div style={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) repeat(4, minmax(92px, auto))", gap: 8 }}>
                                    <select
                                      value={ownerOptions.includes(task.owner) ? task.owner : ""}
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "owner",
                                          event.target.value
                                        )
                                      }
                                      style={{
                                        border: "1px solid #d1d5db",
                                        borderRadius: 10,
                                        padding: "10px 12px",
                                        fontSize: 14,
                                        background: "#fff",
                                      }}
                                    >
                                      <option value="">Unassigned</option>
                                      {ownerOptions.map((owner) => (
                                        <option key={owner} value={owner}>
                                          {owner}
                                        </option>
                                      ))}
                                    </select>
                                    <input
                                      type="date"
                                      value={task.dueDate}
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "dueDate",
                                          event.target.value
                                        )
                                      }
                                      style={{
                                        border: "1px solid #d1d5db",
                                        borderRadius: 10,
                                        padding: "10px 12px",
                                        fontSize: 14,
                                      }}
                                    />
                                    <select
                                      value={task.status}
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "status",
                                          event.target.value
                                        )
                                      }
                                      style={{
                                        border: "1px solid #d1d5db",
                                        borderRadius: 10,
                                        padding: "10px 12px",
                                        fontSize: 14,
                                        background: "#fff",
                                      }}
                                    >
                                      {workStatusOptions.map((status) => (
                                        <option key={status} value={status}>
                                          {formatWorkStatusLabel(status)}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      value={task.priority ?? "normal"}
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "priority",
                                          event.target.value
                                        )
                                      }
                                      style={{
                                        ...workPriorityChipStyle(task.priority),
                                        appearance: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {workPriorityOptions.map((priority) => (
                                        <option key={priority} value={priority}>
                                          {formatWorkPriorityLabel(priority)}
                                        </option>
                                      ))}
                                    </select>
                                    <select
                                      value={task.effort ?? "medium"}
                                      onChange={(event) =>
                                        onInitiativeTaskChange(
                                          selectedPersistedKpi.id,
                                          initiative.id,
                                          task.id,
                                          "effort",
                                          event.target.value
                                        )
                                      }
                                      style={{
                                        ...workEffortChipStyle(task.effort),
                                        appearance: "none",
                                        cursor: "pointer",
                                      }}
                                    >
                                      {workEffortOptions.map((effort) => (
                                        <option key={effort} value={effort}>
                                          {formatWorkEffortLabel(effort)}
                                        </option>
                                      ))}
                                    </select>
                                  </div>
                                  <textarea
                                    value={task.notes}
                                    placeholder="Notes"
                                    onChange={(event) =>
                                      onInitiativeTaskChange(
                                        selectedPersistedKpi.id,
                                        initiative.id,
                                        task.id,
                                        "notes",
                                        event.target.value
                                      )
                                    }
                                    rows={2}
                                    style={{
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "10px 12px",
                                      fontSize: 14,
                                      resize: "vertical",
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="kpi-work-direct-card" style={{ display: "grid", gap: 12 }}>
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                        }}
                      >
                        <div style={{ fontSize: 16, fontWeight: 800 }}>KPI Tasks</div>
                        <button
                          type="button"
                          className="kpi-work-action-button"
                          onClick={() => onAddTask(selectedPersistedKpi.id)}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            background: "#fff",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          Add Task
                        </button>
                      </div>
                      {(selectedPersistedKpi.data.tasks ?? []).length === 0 ? (
                        <div style={{ color: "#6b7280" }}>No direct KPI tasks yet.</div>
                      ) : (
                        (selectedPersistedKpi.data.tasks ?? []).map((task) => (
                          <div
                            key={task.id}
                            className="kpi-work-task-row"
                            ref={(node) => {
                              workEditorItemRefs.current[`kpi-task:${task.id}`] = node;
                            }}
                            style={{
                              border:
                                workEditorFocus?.kind === "kpi-task" &&
                                workEditorFocus.taskId === task.id
                                  ? "2px solid #2563eb"
                                  : "1px solid #e5e7eb",
                              borderRadius: 14,
                              padding: 12,
                              display: "grid",
                              gap: 8,
                              background:
                                workEditorFocus?.kind === "kpi-task" &&
                                workEditorFocus.taskId === task.id
                                  ? "#eff6ff"
                                  : "#fff",
                            }}
                          >
                            <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                              <button
                                type="button"
                                className="kpi-work-update-button"
                                onClick={() => sendTaskUpdateRequests([task.id])}
                                disabled={isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)}
                                style={{
                                  border: "1px solid #bfdbfe",
                                  borderRadius: 10,
                                  padding: "6px 10px",
                                  background:
                                    isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)
                                      ? "#f3f4f6"
                                      : "#eff6ff",
                                  color:
                                    isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)
                                      ? "#9ca3af"
                                      : "#1d4ed8",
                                  fontWeight: 800,
                                  cursor:
                                    isSendingTaskUpdateEmails || !canRequestTaskUpdate(task)
                                      ? "not-allowed"
                                      : "pointer",
                                }}
                                title={
                                  canRequestTaskUpdate(task)
                                    ? "Request an update for this task."
                                    : "Assign this open task before requesting an update."
                                }
                              >
                                Request update
                              </button>
                              <button
                                type="button"
                                onClick={() => onDeleteTask(selectedPersistedKpi.id, task.id)}
                                style={{
                                  border: "1px solid #fecaca",
                                  borderRadius: 10,
                                  padding: "6px 10px",
                                  background: "#fff5f5",
                                  color: "#b91c1c",
                                  fontWeight: 700,
                                  cursor: "pointer",
                                }}
                              >
                                Delete Task
                              </button>
                            </div>
                            <input
                              value={task.title}
                              onChange={(event) =>
                                onTaskChange(
                                  selectedPersistedKpi.id,
                                  task.id,
                                  "title",
                                  event.target.value
                                )
                              }
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "10px 12px",
                                fontSize: 14,
                              }}
                            />
                            <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 160px", gap: 8 }}>
                              <select
                                value={ownerOptions.includes(task.owner) ? task.owner : ""}
                                onChange={(event) =>
                                  onTaskChange(
                                    selectedPersistedKpi.id,
                                    task.id,
                                    "owner",
                                    event.target.value
                                  )
                                }
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  background: "#fff",
                                }}
                              >
                                <option value="">Unassigned</option>
                                {ownerOptions.map((owner) => (
                                  <option key={owner} value={owner}>
                                    {owner}
                                  </option>
                                ))}
                              </select>
                              <input
                                type="date"
                                value={task.dueDate}
                                onChange={(event) =>
                                  onTaskChange(
                                    selectedPersistedKpi.id,
                                    task.id,
                                    "dueDate",
                                    event.target.value
                                  )
                                }
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                }}
                              />
                              <select
                                value={task.status}
                                onChange={(event) =>
                                  onTaskChange(
                                    selectedPersistedKpi.id,
                                    task.id,
                                    "status",
                                    event.target.value
                                  )
                                }
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "10px 12px",
                                  fontSize: 14,
                                  background: "#fff",
                                }}
                              >
                                {workStatusOptions.map((status) => (
                                  <option key={status} value={status}>
                                    {formatEnumLabel(status)}
                                  </option>
                                ))}
                              </select>
                            </div>
                            <textarea
                              value={task.notes}
                              placeholder="Notes"
                              onChange={(event) =>
                                onTaskChange(
                                  selectedPersistedKpi.id,
                                  task.id,
                                  "notes",
                                  event.target.value
                                )
                              }
                              rows={3}
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "10px 12px",
                                fontSize: 14,
                                resize: "vertical",
                              }}
                            />
                          </div>
                        ))
                      )}
                    </div>
                      </div>
                    ) : null}
                  </div>
                </>
              )}
                </div>
              </div>
            ) : null}
            {pollEditorKpi?.data.pollConfig ? (
              <div
                onClick={() => setPollEditorKpiId(null)}
                style={{
                  position: "fixed",
                  inset: 0,
                  background: "rgba(17, 24, 39, 0.48)",
                  display: "grid",
                  placeItems: "center",
                  padding: 24,
                  zIndex: 1100,
                }}
              >
                <div
                  onClick={(event) => event.stopPropagation()}
                  style={{
                    width: "min(760px, 100%)",
                    maxHeight: "min(84vh, 880px)",
                    overflow: "auto",
                    background: "#fff",
                    border: "2px solid #1d4ed8",
                    borderRadius: 20,
                    padding: 22,
                    display: "grid",
                    gap: 16,
                    boxShadow: "0 30px 70px rgba(0,0,0,0.24)",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: 12,
                      flexWrap: "wrap",
                    }}
                  >
                    <div>
                      <div style={{ fontSize: 20, fontWeight: 800 }}>
                        {isPollViewMode ? "Polling Data" : "Edit Poll"}
                      </div>
                      <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                        {isPollViewMode
                          ? "Review responses, comments, and respondent coverage for this poll."
                          : "Configure a fixed 1-10 rating poll with optional comments."}
                      </div>
                      <div style={{ fontSize: 13, color: "#1f2937", marginTop: 8 }}>
                        KPI: {pollEditorKpi.data.label || "Untitled KPI"}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                      {pollEditorKpi.data.pollConfig ? (
                        <button
                          type="button"
                          onClick={() => setPollEditorMode(isPollViewMode ? "edit" : "view")}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 12,
                            padding: "10px 14px",
                            background: "#fff",
                            color: "#111827",
                            fontWeight: 700,
                            cursor: "pointer",
                          }}
                        >
                          {isPollViewMode ? "Edit Poll" : "View Polling Data"}
                        </button>
                      ) : null}
                      <button
                        type="button"
                        onClick={() => setPollEditorKpiId(null)}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 12,
                          padding: "10px 14px",
                          background: "#fff",
                          color: "#111827",
                          fontWeight: 700,
                          cursor: "pointer",
                        }}
                      >
                        Close
                      </button>
                    </div>
                  </div>

                  {isPollEditMode && (
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 12,
                    }}
                  >
                    <label style={{ display: "grid", gap: 6 }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Poll Title
                      </span>
                      <input
                        value={pollEditorKpi.data.pollConfig!.title}
                        onChange={(event) =>
                          onPollConfigChange(pollEditorKpi.id, "title", event.target.value)
                        }
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "10px 12px",
                          fontSize: 14,
                        }}
                      />
                    </label>
                    <div
                      style={{
                        display: "grid",
                        gap: 6,
                        alignContent: "start",
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        padding: "10px 12px",
                        background: "#f9fafb",
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Publish Status
                      </span>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                        {formatEnumLabel(pollEditorKpi.data.pollConfig!.status)}
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        {pollEditorKpi.data.pollConfig!.publishedAt
                          ? `Published ${new Date(
                              pollEditorKpi.data.pollConfig!.publishedAt
                            ).toLocaleString("en-US")}`
                          : "This poll is still in draft mode."}
                      </div>
                    </div>
                    <label style={{ display: "grid", gap: 6, gridColumn: "1 / -1" }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Poll Description
                      </span>
                      <textarea
                        value={pollEditorKpi.data.pollConfig!.description}
                        onChange={(event) =>
                          onPollConfigChange(pollEditorKpi.id, "description", event.target.value)
                        }
                        rows={3}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "10px 12px",
                          fontSize: 14,
                          resize: "vertical",
                        }}
                      />
                    </label>
                    <label style={{ display: "grid", gap: 6, gridColumn: "1 / -1" }}>
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Question Prompt
                      </span>
                      <textarea
                        value={pollEditorKpi.data.pollConfig!.questionPrompt}
                        onChange={(event) =>
                          onPollConfigChange(
                            pollEditorKpi.id,
                            "questionPrompt",
                            event.target.value
                          )
                        }
                        rows={3}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: 10,
                          padding: "10px 12px",
                          fontSize: 14,
                          resize: "vertical",
                        }}
                      />
                    </label>
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: "14px 16px",
                        background: "#f9fafb",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                          Included KPIs
                        </div>
                        <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                          Send one poll that asks about multiple KPIs in the same response flow.
                        </div>
                      </div>
                      <div
                        style={{
                          border: "1px solid #dbe2ea",
                          borderRadius: 12,
                          padding: "10px 12px",
                          background: "#fff",
                          fontSize: 13,
                          color: "#111827",
                        }}
                      >
                        This KPI is always included: {pollEditorKpi.data.label || "Untitled KPI"}
                      </div>
                      {pollEditorLinkedKpiOptions.length === 0 ? (
                        <div style={{ fontSize: 13, color: "#6b7280" }}>
                          No other KPIs are available to add yet.
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 10,
                          }}
                        >
                          {pollEditorLinkedKpiOptions.map((linkedKpi) => {
                            const isSelected = (pollEditorKpi.data.pollConfig?.linkedKpiIds ?? []).includes(
                              linkedKpi.id
                            );

                            return (
                              <label
                                key={linkedKpi.id}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                                  borderRadius: 12,
                                  padding: "12px 14px",
                                  background: isSelected ? "#eff6ff" : "#fff",
                                  color: "#111827",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={isSelected}
                                  onChange={() => onTogglePollLinkedKpi(pollEditorKpi.id, linkedKpi.id)}
                                />
                                {linkedKpi.label}
                              </label>
                            );
                          })}
                        </div>
                      )}
                      {pollCampaignSetup ? (
                        <div style={{ display: "grid", gap: 10 }}>
                          {pollCampaignSetup.autoConvertKpis.length > 0 ? (
                            <div
                              style={{
                                borderRadius: 12,
                                padding: "12px 14px",
                                background: "#eff6ff",
                                border: "1px solid #bfdbfe",
                                display: "grid",
                                gap: 4,
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#1d4ed8" }}>
                                These KPIs will switch to Poll when you publish.
                              </div>
                              <div style={{ fontSize: 12, lineHeight: 1.5, color: "#1e3a8a" }}>
                                {pollCampaignSetup.autoConvertKpis
                                  .map((node) => node.data.label || "Untitled KPI")
                                  .join(", ")}{" "}
                                will be normalized to Data Source: Poll and Value Type: Rating (1-10).
                              </div>
                            </div>
                          ) : null}
                          {pollCampaignSetup.missingTargetKpis.length > 0 ? (
                            <div
                              style={{
                                borderRadius: 12,
                                padding: "12px 14px",
                                background: "#fff7ed",
                                border: "1px solid #fdba74",
                                display: "grid",
                                gap: 4,
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#c2410c" }}>
                                Some KPIs do not have a target for this polling context.
                              </div>
                              <div style={{ fontSize: 12, lineHeight: 1.5, color: "#9a3412" }}>
                                Add targets before publishing if you want results to evaluate cleanly:{" "}
                                {pollCampaignSetup.missingTargetKpis
                                  .map((node) => node.data.label || "Untitled KPI")
                                  .join(", ")}
                                .
                              </div>
                            </div>
                          ) : null}
                          {pollCampaignSetup.mismatchedFrequencies ? (
                            <div
                              style={{
                                borderRadius: 12,
                                padding: "12px 14px",
                                background: "#fefce8",
                                border: "1px solid #fde68a",
                                display: "grid",
                                gap: 4,
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#a16207" }}>
                                Included KPIs use different reporting periods.
                              </div>
                              <div style={{ fontSize: 12, lineHeight: 1.5, color: "#854d0e" }}>
                                {Object.entries(pollCampaignSetup.frequencyGroups)
                                  .map(([frequency, labels]) => `${formatEnumLabel(frequency)}: ${labels.join(", ")}`)
                                  .join(" | ")}
                                . The poll can still run, but answers will write back into different reporting buckets.
                              </div>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gap: 6,
                        alignContent: "start",
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        padding: "10px 12px",
                        background: "#f9fafb",
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Response Format
                      </span>
                      <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                        1-10 rating
                      </div>
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        Respondents can also leave comments with evidence or examples behind their rating.
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gap: 8,
                        alignContent: "start",
                      }}
                    >
                      <span style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                        Audience Groups
                      </span>
                      <div style={{ fontSize: 13, color: "#6b7280" }}>
                        Choose one or more groups to publish this poll to.
                      </div>
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                        gap: 10,
                        gridColumn: "1 / -1",
                      }}
                    >
                      {audienceGroupOptions.map((group) => {
                        const selectedGroups =
                          pollEditorKpi.data.pollConfig!.targetGroups.length > 0
                            ? pollEditorKpi.data.pollConfig!.targetGroups
                            : pollEditorKpi.data.pollConfig!.targetGroup
                              ? [pollEditorKpi.data.pollConfig!.targetGroup]
                              : [];
                        const isSelected = selectedGroups.includes(group);

                        return (
                          <label
                            key={group}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                              border: isSelected ? "2px solid #2563eb" : "1px solid #d1d5db",
                              borderRadius: 12,
                              padding: "12px 14px",
                              background: isSelected ? "#eff6ff" : "#fff",
                              color: "#111827",
                              cursor: "pointer",
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => onTogglePollAudienceGroup(pollEditorKpi.id, group)}
                            />
                            {group}
                          </label>
                        );
                      })}
                    </div>
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        border: "1px solid #e5e7eb",
                        borderRadius: 14,
                        padding: "14px 16px",
                        background: "#f9fafb",
                        display: "grid",
                        gap: 10,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>Recipients</div>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                            People in the selected groups who will receive tracked invitation links.
                          </div>
                        </div>
                        <div style={{ fontSize: 13, color: "#374151" }}>
                          {(pollEditorKpi.data.pollConfig?.invites ?? []).length} tracked invite
                          {(pollEditorKpi.data.pollConfig?.invites ?? []).length === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: "#374151" }}>
                        {pollEditorRecipients.length} matched recipient
                        {pollEditorRecipients.length === 1 ? "" : "s"}
                      </div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        <button
                          type="button"
                          onClick={openPollEmailComposer}
                          disabled={(pollEditorKpi.data.pollConfig?.invites ?? []).filter((invite) => invite.recipientEmail?.trim()).length === 0}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 10,
                            padding: "8px 10px",
                            background: "#fff",
                            color:
                              (pollEditorKpi.data.pollConfig?.invites ?? []).filter((invite) => invite.recipientEmail?.trim()).length === 0
                                ? "#9ca3af"
                                : "#111827",
                            fontWeight: 700,
                            cursor:
                              (pollEditorKpi.data.pollConfig?.invites ?? []).filter((invite) => invite.recipientEmail?.trim()).length === 0
                                ? "not-allowed"
                                : "pointer",
                          }}
                        >
                          Send Poll Emails
                        </button>
                      </div>
                      {isPollEmailComposerOpen ? (
                        <div
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: 12,
                            padding: "12px 14px",
                            background: "#fff",
                            display: "grid",
                            gap: 10,
                          }}
                        >
                          <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
                            Poll Email Campaign
                          </div>
                          <div style={{ fontSize: 12, color: "#6b7280" }}>
                            Use placeholders like {"{first_name}"}, {"{name}"}, and {"{invite_url}"}.
                          </div>
                          <input
                            value={pollEmailSubject}
                            onChange={(event) => setPollEmailSubject(event.target.value)}
                            placeholder="Subject"
                            style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14 }}
                          />
                          <textarea
                            value={pollEmailGreeting}
                            onChange={(event) => setPollEmailGreeting(event.target.value)}
                            rows={2}
                            placeholder="Greeting"
                            style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                          />
                          <textarea
                            value={pollEmailBody}
                            onChange={(event) => setPollEmailBody(event.target.value)}
                            rows={5}
                            placeholder="Body"
                            style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                          />
                          <textarea
                            value={pollEmailClosing}
                            onChange={(event) => setPollEmailClosing(event.target.value)}
                            rows={3}
                            placeholder="Closing"
                            style={{ border: "1px solid #d1d5db", borderRadius: 10, padding: "10px 12px", fontSize: 14, resize: "vertical" }}
                          />
                          {pollEmailStatusMessage ? (
                            <div style={{ fontSize: 12, color: "#374151" }}>{pollEmailStatusMessage}</div>
                          ) : null}
                          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            <button
                              type="button"
                              onClick={() => setIsPollEmailComposerOpen(false)}
                              style={{
                                border: "1px solid #d1d5db",
                                borderRadius: 10,
                                padding: "8px 10px",
                                background: "#fff",
                                color: "#111827",
                                fontWeight: 700,
                                cursor: "pointer",
                              }}
                            >
                              Close Composer
                            </button>
                            <button
                              type="button"
                              onClick={() => void sendPollEmailCampaign()}
                              disabled={isSendingPollEmails || pollEmailSubject.trim().length === 0}
                              style={{
                                border: "none",
                                borderRadius: 10,
                                padding: "8px 10px",
                                background: isSendingPollEmails ? "#9ca3af" : "#1d4ed8",
                                color: "#fff",
                                fontWeight: 800,
                                cursor: isSendingPollEmails ? "wait" : "pointer",
                              }}
                            >
                              {isSendingPollEmails ? "Sending..." : "Send to All Recipients"}
                            </button>
                          </div>
                        </div>
                      ) : null}
                      {(pollEditorKpi.data.pollConfig?.invites ?? []).length === 0 ? (
                        <div style={{ fontSize: 13, color: "#6b7280" }}>
                          No recipients are assigned to the selected groups yet. Add people in the
                          `Poll Recipients` section of the editor setup area.
                        </div>
                      ) : (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                            gap: 10,
                          }}
                        >
                          {(pollEditorKpi.data.pollConfig?.invites ?? []).slice(0, 12).map((invite) => (
                            <div
                              key={invite.id}
                              style={{
                                border: invite.completedAt ? "2px solid #16a34a" : "1px solid #d1d5db",
                                borderRadius: 12,
                                padding: "10px 12px",
                                background: invite.completedAt ? "#f0fdf4" : "#fff",
                                display: "grid",
                                gap: 8,
                                boxShadow: invite.completedAt
                                  ? "0 0 0 2px rgba(22, 163, 74, 0.08)"
                                  : "none",
                              }}
                            >
                              <div style={{ fontSize: 13, fontWeight: 800, color: "#111827" }}>
                                {invite.recipientName || "Unnamed recipient"}
                              </div>
                              <div style={{ fontSize: 12, color: "#4b5563" }}>
                                {invite.recipientEmail}
                              </div>
                              <div
                                style={{
                                  border: "1px solid #d1d5db",
                                  borderRadius: 10,
                                  padding: "8px 10px",
                                  background: "#f9fafb",
                                  fontSize: 12,
                                  color: "#111827",
                                  wordBreak: "break-all",
                                }}
                              >
                                {getPollInviteUrl(invite.token)}
                              </div>
                              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                                {invite.recipientEmail ? (
                                  <button
                                    type="button"
                                    onClick={() => emailPollInvitation(invite)}
                                    style={{
                                      border: "none",
                                      borderRadius: 10,
                                      padding: "8px 10px",
                                      background: "#111827",
                                      color: "#fff",
                                      fontWeight: 800,
                                      cursor: "pointer",
                                    }}
                                  >
                                    Email Invite
                                  </button>
                                ) : null}
                                <button
                                  type="button"
                                  onClick={() =>
                                    void navigator.clipboard.writeText(getPollInviteUrl(invite.token))
                                  }
                                  style={{
                                    border: "1px solid #d1d5db",
                                    borderRadius: 10,
                                    padding: "8px 10px",
                                    background: "#fff",
                                    color: "#111827",
                                    fontWeight: 700,
                                    cursor: "pointer",
                                  }}
                                >
                                  Copy Link
                                </button>
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                {invite.completedAt
                                  ? `Responded ${new Date(invite.completedAt).toLocaleString("en-US")}`
                                  : invite.recipientEmail
                                    ? "No response yet"
                                    : "Share this link directly with your audience"}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        gridColumn: "1 / -1",
                        fontSize: 14,
                        color: "#111827",
                        border: "1px solid #e5e7eb",
                        borderRadius: 12,
                        padding: "12px 14px",
                        background: "#f9fafb",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={pollEditorKpi.data.pollConfig!.isAnonymous}
                        onChange={(event) =>
                          onPollConfigChange(
                            pollEditorKpi.id,
                            "isAnonymous",
                            event.target.checked
                          )
                        }
                      />
                      Anonymous responses
                    </label>
                    <div
                      style={{
                        gridColumn: "1 / -1",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 12,
                        flexWrap: "wrap",
                        borderTop: "1px solid #e5e7eb",
                        paddingTop: 12,
                      }}
                    >
                      <div style={{ fontSize: 13, color: "#6b7280" }}>
                        {pollEditorSelectedGroups.length} audience groups selected
                      </div>
                      {pollPublishContext ? (
                        <div
                          style={{
                            width: "100%",
                            borderRadius: 12,
                            padding: "12px 14px",
                            background:
                              pollPublishContext.tone === "warning" ? "#fef2f2" : "#eff6ff",
                            border:
                              pollPublishContext.tone === "warning"
                                ? "1px solid #fecaca"
                                : "1px solid #bfdbfe",
                            display: "grid",
                            gap: 4,
                          }}
                        >
                          <div
                            style={{
                              fontSize: 13,
                              fontWeight: 800,
                              color:
                                pollPublishContext.tone === "warning" ? "#b91c1c" : "#1d4ed8",
                            }}
                          >
                            {pollPublishContext.summary}
                          </div>
                          <div
                            style={{
                              fontSize: 12,
                              lineHeight: 1.5,
                              color:
                                pollPublishContext.tone === "warning" ? "#7f1d1d" : "#1e3a8a",
                            }}
                          >
                            {pollPublishContext.detail}
                          </div>
                        </div>
                      ) : null}
                      <button
                        type="button"
                        disabled={
                          pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                        }
                        onClick={() => onPublishPoll(pollEditorKpi.id)}
                        style={{
                          border: "none",
                          borderRadius: 12,
                          padding: "10px 14px",
                          background:
                            pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                              ? "#9ca3af"
                              : "#1d4ed8",
                          color: "#fff",
                          fontWeight: 800,
                          cursor:
                            pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                              ? "not-allowed"
                              : "pointer",
                        }}
                      >
	                        Publish Poll
	                      </button>
                        {pollEditorKpi.data.pollConfig?.status === "published" ||
                        (pollEditorKpi.data.pollConfig?.responses.length ?? 0) > 0 ||
                        (pollEditorKpi.data.pollConfig?.invites.length ?? 0) > 0 ? (
                          <button
                            type="button"
                            disabled={
                              pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                            }
                            onClick={() => onRerunPoll(pollEditorKpi.id)}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 12,
                              padding: "10px 14px",
                              background: "#fff",
                              color:
                                pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                                  ? "#9ca3af"
                                  : "#111827",
                              fontWeight: 800,
                              cursor:
                                pollEditorSelectedGroups.length === 0 || !pollPublishContext?.canPublish
                                  ? "not-allowed"
                                  : "pointer",
                            }}
                          >
                            Re-run Poll
                          </button>
                        ) : null}
	                    </div>
	                  </div>
	                  )}
	                    <div
                      style={{
                        gridColumn: "1 / -1",
                        borderTop: "1px solid #e5e7eb",
                        paddingTop: 16,
                        display: "grid",
                        gap: 14,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          gap: 12,
                          flexWrap: "wrap",
                        }}
                      >
                        <div>
                          <div style={{ fontSize: 16, fontWeight: 800 }}>Poll Results</div>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 4 }}>
                            Review how many responses have come in and what people are saying.
                          </div>
                        </div>
                        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
                          <div
                            style={{
                              border: "1px solid #dbeafe",
                              borderRadius: 999,
                              padding: "6px 10px",
                              background: "#eff6ff",
                              color: "#1d4ed8",
                              fontSize: 12,
                              fontWeight: 800,
                            }}
                          >
                            {pollEditorResults?.responseCount ?? 0} responses received
                          </div>
                          <button
                            type="button"
                            onClick={() => void loadWorkspace({ preserveSelection: true })}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 10px",
                              background: "#fff",
                              color: "#111827",
                              fontSize: 12,
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                          >
                            Refresh Results
                          </button>
                          <button
                            type="button"
                            onClick={() => void copyPollComments()}
                            style={{
                              border: "1px solid #d1d5db",
                              borderRadius: 10,
                              padding: "8px 10px",
                              background: "#fff",
                              color: "#111827",
                              fontSize: 12,
                              fontWeight: 800,
                              cursor: "pointer",
                            }}
                          >
                            Copy Comments
                          </button>
                        </div>
                      </div>

                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                          gap: 12,
                        }}
                      >
                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 14,
                            padding: "14px 16px",
                            background: "#fff",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                            Total Responses
                          </div>
                          <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8 }}>
                            {pollEditorResults?.responseCount ?? 0}
                          </div>
                        </div>
                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 14,
                            padding: "14px 16px",
                            background: "#fff",
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                            Results Summary
                          </div>
                          <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8 }}>
                            {pollEditorResults?.headline ?? "No data yet"}
                          </div>
                          <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                            {pollEditorResults?.detail ?? "Publish the poll and collect responses."}
                          </div>
                          {pollCommentsCopyMessage ? (
                            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 8 }}>
                              {pollCommentsCopyMessage}
                            </div>
                          ) : null}
                          {pollEditorResults?.commentSummaryShort ? (
                            <div
                              style={{
                                marginTop: 10,
                                paddingTop: 10,
                                borderTop: "1px solid #e5e7eb",
                                fontSize: 13,
                                color: "#374151",
                                lineHeight: 1.5,
                                whiteSpace: "pre-line",
                              }}
                            >
                              <span style={{ fontWeight: 800, color: "#111827" }}>AI Summary:</span>{" "}
                              {isPollSummaryExpanded && pollEditorResults.commentSummaryLong
                                ? pollEditorResults.commentSummaryLong
                                : pollEditorResults.commentSummaryShort}
                              {pollEditorResults.commentSummaryLong &&
                              pollEditorResults.commentSummaryLong !== pollEditorResults.commentSummaryShort ? (
                                <div style={{ marginTop: 8 }}>
                                  <button
                                    type="button"
                                    onClick={() => setIsPollSummaryExpanded((current) => !current)}
                                    style={{
                                      border: "1px solid #d1d5db",
                                      borderRadius: 10,
                                      padding: "6px 10px",
                                      background: "#fff",
                                      color: "#111827",
                                      fontSize: 12,
                                      fontWeight: 700,
                                      cursor: "pointer",
                                    }}
                                  >
                                    {isPollSummaryExpanded ? "Show Less" : "Show Full Summary"}
                                  </button>
                                </div>
                              ) : null}
                            </div>
                          ) : null}
                        </div>
                      </div>

                      {isPollViewMode ? (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                            gap: 12,
                          }}
                        >
                          <div
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: 14,
                              padding: "14px 16px",
                              background: "#fff",
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                              Comments Received
                            </div>
                            <div style={{ fontSize: 28, fontWeight: 800, marginTop: 8 }}>
                              {pollEditorResults?.commentCount ?? 0}
                            </div>
                            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                              Responses with written feedback
                            </div>
                          </div>
                          <div
                            style={{
                              border: "1px solid #e5e7eb",
                              borderRadius: 14,
                              padding: "14px 16px",
                              background: "#fff",
                            }}
                          >
                            <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                              Response Coverage
                            </div>
                            <div style={{ fontSize: 20, fontWeight: 800, marginTop: 8 }}>
                              {pollEditorResults?.completedInviteCount ?? 0} responded
                            </div>
                            <div style={{ fontSize: 13, color: "#6b7280", marginTop: 6 }}>
                              {pollEditorResults?.pendingInviteCount ?? 0} still pending
                            </div>
                          </div>
                        </div>
                      ) : null}

                      {isPollViewMode ? (
                        <div
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 14,
                            padding: "14px 16px",
                            background: "#fff",
                            display: "grid",
                            gap: 8,
                          }}
                        >
                          <div style={{ fontSize: 12, fontWeight: 800, color: "#6b7280" }}>
                            Question
                          </div>
                          <div style={{ fontSize: 15, fontWeight: 700, color: "#111827", lineHeight: 1.5 }}>
                            {pollEditorKpi.data.pollConfig!.questionPrompt}
                          </div>
                        </div>
                      ) : null}

                      {(pollEditorResults?.responses.length ?? 0) === 0 ? (
                        <div
                          style={{
                            border: "1px dashed #d1d5db",
                            borderRadius: 14,
                            padding: "18px 16px",
                            background: "#f9fafb",
                            color: "#6b7280",
                            fontSize: 14,
                          }}
                        >
                          No poll responses have been received yet. Once people submit answers, their
                          results will appear here.
                        </div>
                      ) : (
                        <div style={{ display: "grid", gap: 10 }}>
                          <div style={{ fontSize: 14, fontWeight: 800 }}>Recent Responses</div>
                          {pollEditorResults?.responses.slice(0, 6).map((response) => (
                            <div
                              key={response.id}
                              style={{
                                border: "1px solid #e5e7eb",
                                borderRadius: 14,
                                padding: "12px 14px",
                                background: "#fff",
                                display: "grid",
                                gap: 6,
                              }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                  gap: 12,
                                  flexWrap: "wrap",
                                }}
                              >
                                <div style={{ fontSize: 14, fontWeight: 700, color: "#111827" }}>
                                  {pollEditorKpi.data.pollConfig!.isAnonymous
                                    ? "Anonymous response"
                                    : response.respondentLabel || "Unnamed respondent"}
                                </div>
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                  {response.submittedAt
                                    ? new Date(response.submittedAt).toLocaleString("en-US")
                                    : "No timestamp"}
                                </div>
                              </div>
                              <div style={{ fontSize: 14, color: "#1f2937" }}>
                                {formatPollResponseValue(response.value)}
                              </div>
                              {(response.comment ?? "").trim().length > 0 ? (
                                <div
                                  style={{
                                    borderTop: "1px solid #e5e7eb",
                                    paddingTop: 8,
                                    fontSize: 13,
                                    color: "#374151",
                                    lineHeight: 1.5,
                                    whiteSpace: "pre-wrap",
                                  }}
                                >
                                  Comment: {response.comment}
                                </div>
                              ) : null}
                              {response.respondentGroup ? (
                                <div style={{ fontSize: 12, color: "#6b7280" }}>
                                  Group: {response.respondentGroup}
                                </div>
                              ) : null}
                            </div>
                          ))}
                        </div>
	                      )}
	                    </div>
	                  </div>
	              </div>
	            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
